// Briefing index + viewer rendering. Pure DOM; no framework.
//
// The viewer mirrors the synthesizer payload shape (threads + unthreaded +
// needs_review). Each renderable line gets a `<form>` with a `data-target-id`
// composite path key — see target_id.ts for the contract. Form submission
// is wired up in forms.ts.

import {
  apiPathPrefix, deleteJson, getJson, inPreviewMode, postEmpty, postJson,
} from "./api";
import {
  acceptMergeSuggestion,
  acceptProposedAnswer,
  confirmProposedAssociations,
  dismissProposedAssociation,
  dismissProposedAnswer,
  mergeProposalDismissed,
  proposedAnswerDismissed,
  purgeMenuSectionPrefs,
  renderFormControls,
  resolveSubProjectBucket,
  retractFeedbackEvent,
  type ActionOption,
  type ClosedActionOption,
  type ItemOption,
  type ParticipantOption,
  type SubcategoryOption,
  type ThreadOption,
} from "./forms";
// #60 File-under-Closed: the closed-Item picker unions the briefing blob
// (≤2-week retention) with the all-ages Closed-Items snapshot. Fixture mode
// uses the stubbed snapshot (mirrors app.ts's fetchClosed binding; fixture's
// imports of this module are type-only, so no runtime cycle).
import { fetchClosedItems, type ClosedItemsSnapshot } from "./closed_items";
import { fetchFixtureClosedItems, inFixtureMode } from "./fixture";
// S3 accept-assist: one-click merge is undo-backed — POST immediately, Undo retracts.
import { showUndoToast } from "./undo_toast";

// Memoized per session — the viewer re-renders on every KIL toggle /
// feedback-state change, and re-firing GET /closed-items each time is
// wasted load (the snapshot moves at most hourly). A failed fetch clears
// the memo so the next render retries.
let closedItemsSnapshotMemo: Promise<ClosedItemsSnapshot> | null = null;
function closedItemsSnapshotOnce(): Promise<ClosedItemsSnapshot> {
  if (!closedItemsSnapshotMemo) {
    closedItemsSnapshotMemo = (
      inFixtureMode() ? fetchFixtureClosedItems() : fetchClosedItems()
    ).catch((err) => {
      closedItemsSnapshotMemo = null;
      throw err;
    });
  }
  return closedItemsSnapshotMemo;
}

// Category-level one_liner / description rendering. The client spec
// (tmp/scale-brief-structure.pdf) defines a Category as just a
// tag/number + Open/Closed status — no description requested. The
// previous render leaned on `thread.one_liner` as an auto-summary of
// items below, which drifted from the spec. Synth rule 12 was also
// updated to stop emitting one_liner; this renderer flag is the
// belt-and-suspenders gate for historical payloads that still carry
// one_liner data. Flip to true if the client later asks for
// Category descriptions to come back.
const CATEGORY_DESCRIPTION_VISIBLE = false;
// Ray 2026-09-29 declutter ("remove the formerly-N annotation"): hide the
// "(formerly N.M, recategorized DATE)" move breadcrumb in the briefing body.
// The DATA-agreement guarantee (briefing + snapshots read the same
// display_number_breadcrumbs) is untouched — this gates only the render. A flag,
// not a delete, so the category-numbering redesign can restore it deliberately.
const SHOW_MOVE_BREADCRUMB = false;
import {
  actionItemKey,
  needsReviewItemKey,
  subcategoryKey,
  threadItemKey,
  threadKey,
  unfiledKey,
  informationalKey,
  unthreadedItemKey,
  unthreadedWorkItemKey,
  unthreadedWorkItemSectionItemKey,
  workItemKey,
  workItemSectionItemKey,
  type Section,
} from "./target_id";

export interface BriefingListItem {
  run_id: string;
  week_start: string;
  week_end: string;
  stored_at: string;
}

export interface BriefingPayload {
  project: {
    slug: string;
    name?: string;
    display_name?: string;
    // Slice 8 Item #5 follow-up: roster seeds for collectParticipantOptions.
    // team_members is the configured project roster (project YAML).
    // participants is the observed-in-traffic roster from state.db. Both
    // are injected by the agent harness; either can be omitted when
    // empty. The SPA merges these with names parsed out of the briefing
    // body to produce the responsible_party_assign datalist.
    team_members?: Array<{ name: string; email?: string | null; role?: string | null }>;
    participants?: Array<{ name: string; email?: string | null }>;
  };
  week: { start: string; end: string };
  // Slice 7: present (=2) on payloads emitted by the v2 synthesizer.
  // Absent on legacy payloads. The viewer uses this to route between
  // renderers. v4 (Category → Item → Action redesign) bumps this to 4.
  payload_schema_version?: number;
  // Slice 7 feedback item #6: when the agent attached the rendered
  // .docx, this is its base64-encoded body. The SPA never decodes it
  // client-side — the download button hits the Worker's
  // GET /briefings/:runId/download route which streams decoded bytes.
  docx_b64?: string;
  executive_summary?: string;
  // P4c: sub-project registry projection (see SubProjectView). Absent on a
  // flat project → the band never activates and the render is unchanged.
  sub_projects?: SubProjectView[];
  // R2: per-sub-project executive summaries, keyed by sub-project slug →
  // summary string (agent-generated, node-only; passthrough from the blob).
  // Absent on a flat project. Merged onto each SubProjectView at render time.
  sub_project_summaries?: Record<string, string>;
  threads?: ThreadView[];
  unthreaded?:
    | (Partial<Record<Section, AnyItem[]>> & { work_items?: WorkItemView[] })
    | undefined;
  // v4 (payload_schema_version === 4): top-level orphan bucket for
  // items the synthesizer couldn't confidently place under any
  // Category → Item. Absent on v3 payloads.
  unfiled?: UnfiledItemView[];
  // A² (2026-06-03): Needs-Your-Input FYI bucket. Low-confidence /
  // pure-FYI content the synth surfaced here instead of fabricating a
  // thin Item + Informational Action in the Category tree. Absent on
  // pre-A² payloads.
  informational?: InformationalItemView[];
  // A³/A² (2026-06-03): entries the recipient dismissed-as-acknowledged —
  // rendered as a collapsed "Acknowledged this period (N)" line.
  informational_acknowledged?: InformationalItemView[];
  candidate_threads?: CandidateThreadView[];
  needs_review?: Array<{ item_id: string; summary: string; why_flagged?: string }>;
  nothing_new?: boolean;
  // Past corrections raised against this run_id, threaded through by
  // agent/portal.py::_briefing_envelope. Used to render "corrected"
  // badges on matching rows. Items with no `target.id` are present in
  // the array but not rendered (their effect is already visible through
  // the synthesizer's output).
  corrections?: Correction[];
}

export interface Correction {
  kind: string;
  recorded_at?: string;
  recorded_by?: string;
  applies_to_run_id?: string | null;
  target?: { type?: string; id?: string };
  // ClassificationCorrection / DateCorrection / ThreadTagCorrection
  item_id?: string;
  // DateCorrection
  field?: string;
  new_value?: string;
  // OperatorNote
  kind_note?: string;
  text?: string;
  // ThreadRenameCorrection / ThreadMergeCorrection / ThreadTagCorrection
  from_slug?: string;
  to_slug?: string;
  to_name?: string;
  source_slugs?: string[];
  target_slug?: string;
  from_thread?: string;
  to_thread?: string;
}

// #38: an unnumbered, recipient-created grouping inside a Category (the overlay
// tier). Stamped into the blob from state every cron; never synth-emitted.
// Sorted (created_at ASC, seq tiebreak) by the renderer; a closed group renders
// as a header-only tombstone for 14 days, then the stamp stops emitting it.
export interface SubCategoryView {
  slug: string;
  name: string;
  status?: "open" | "closed" | string;
  created_at?: string | null;
  closed_at?: string | null;
  closed_reason?: string | null;
  // Hidden backend ordinal (MAX+1 per Category) — render tiebreak only, never
  // shown (the "no Dewey decimal" guarantee).
  seq?: number | null;
}

// #37 phase tier. Closed 4-value enum (mirrors the Pydantic Literal in
// agent/payload_v4.py and the Zod enum in worker/src/schemas.ts) — the
// code-level enum structurally enforces "no new phases mid-project".
// Underscore-canonical storage; PHASE_LABELS maps to the display banners.
export type PhaseValue =
  | "pre_development"
  | "pre_construction"
  | "construction"
  | "general";

// Fixed render order, top → bottom. General is last so the Needs-Your-Input
// tail (which lives in the General band) sits where today's global tail does.
export const PHASE_ORDER: readonly PhaseValue[] = [
  "pre_development",
  "pre_construction",
  "construction",
  "general",
];

export const PHASE_LABELS: Record<PhaseValue, string> = {
  pre_development: "Pre-Development",
  pre_construction: "Pre-Construction",
  construction: "Construction",
  general: "General",
};

// A thread's phase coerced to a render band: null/absent/unknown => General.
export function phaseBand(phase?: string | null): PhaseValue {
  return phase && (PHASE_ORDER as readonly string[]).includes(phase)
    ? (phase as PhaseValue)
    : "general";
}

// P4c (R §P4a): the sub-project tier ABOVE the Category. Carried as a flat
// top-level `payload.sub_projects[]` (state-owned registry, stamped by the
// harness ONLY for a project whose registry has rows — a flat project has no
// key); a Category points in via `ThreadView.sub_project_slug`. `seq` is the
// hidden registry ordinal (1 → letter A, 2 → B, …) that the P4b numbering
// regime derives the `<letter><index>` Category number from.
export interface SubProjectView {
  slug: string;
  name: string;
  seq?: number | null;
  status?: string;
  associations?: string[];
  // R2: the per-sub-project executive summary (merged in from the top-level
  // `payload.sub_project_summaries` map at render time; absent on a flat project
  // or a scope with no summary this week). Rendered under the band header.
  summary?: string | null;
}

export interface ThreadView {
  slug: string;
  name: string;
  status?: string;
  inferred?: boolean;
  // D4 (2026-10-16): true for a preset (template-seeded) Category; the pencil
  // gates the restructure affordances off it. Absent/false on recipient-added.
  preset?: boolean | null;
  // P4c: which sub-project this Category belongs to (null/absent = not in a
  // sub-project; in a sub-projected project such a Category — recipient-added,
  // the P4b known limit — renders last under a "Not in a sub-project" band).
  sub_project_slug?: string | null;
  // #38: sub-category grouping overlay — flat sibling metadata. Items keep
  // `work_items[]` as their only child array; the renderer partitions that
  // flat list by `work_items[].subcategory_slug` at render time.
  subcategories?: SubCategoryView[];
  // #37: phase placement overlay — a state-owned attribute (config-seeded,
  // recipient-migratable via category_move_to_phase), stamped from state by
  // the harness. null/absent => unphased/legacy, rendered in the General band.
  phase?: PhaseValue | null;
  one_liner?: string;
  first_seen?: string;
  // v4: client-facing path number (e.g. "1") rendered by the v4 SPA
  // path. Absent on v3 payloads; v4 renderer falls back gracefully.
  display_number?: string;
  // v4: closure timestamp; populated when status === "closed".
  closed_at?: string;
  // v4: italic inline annotation when this Category was recently
  // moved (Decision 3.6). Renders for 2 weeks.
  breadcrumb?: BreadcrumbView | null;
  // Legacy: categories sit directly on the thread.
  completed?: AnyItem[];
  milestones?: AnyItem[];
  in_progress?: AnyItem[];
  decisions?: AnyItem[];
  blockers?: AnyItem[];
  action_items?: AnyItem[];
  open_questions?: AnyItem[];
  leadership_attention?: AnyItem[];
  // Slice 7: categories nest inside work_items.
  work_items?: WorkItemView[];
  archived_work_items?: ArchivedWorkItemView[];
}

export interface WorkItemView {
  slug: string;
  name: string;
  summary?: string;
  status?: "open" | "closed" | string;
  first_seen?: string;
  involved?: string[];
  key_dates?: string[];
  // Slice 8 Item #5 (v3): per-work-item responsible_party. Lifecycle
  // is register-once on the agent side — operator_override always wins,
  // then prior registration, then computed, then default_pm.
  responsible_party?: ResponsiblePartyView | null;
  // Slice 8 Item #3 (v3): compactness hint. true → routine sections
  // collapse into `pending`; always-full surfaces still render.
  compact?: boolean;
  pending?: string[];
  // v4 (payload_schema_version === 4) additions per Section 8.3 of the
  // Category → Item → Action redesign.
  // - `serial` / `display_number`: stable backend ID (I-NNN) + path
  //   number ("1.1"). Both absent on v3 payloads.
  serial?: number;
  display_number?: string;
  // - `narrative`: 2-3 line description rendered under the Item title
  //   (Decision 1.2). v3 used `summary` for this role; v4 splits them
  //   — `summary` is the short status line, `narrative` is the body.
  narrative?: string | null;
  // - `start_date`: alias for `first_seen` in v4 vocabulary. Renderer
  //   should accept either.
  start_date?: string;
  // #38: grouping pointer into the parent Category's `subcategories[]`
  // (null/absent = ungrouped, today's layout). Stamped from state post-upsert;
  // the deterministic-only class display_number/serial occupy. Never synth-
  // emitted. display_number is untouched by a grouping move.
  subcategory_slug?: string | null;
  // - `closed_at`: populated when status === "closed".
  closed_at?: string;
  // A①: closure reason — "merged into <target>" routes the Item to the
  // "Recently Merged" section. Set optimistically on merge submit.
  closed_reason?: string | null;
  // - `kil_marked`: persistent Project Status Update mark (Section 5). Marks
  //   are sticky across weeks until unmarked or aged out.
  kil_marked?: boolean;
  // P1r (R reports): report-inclusion set {558, summary} — which client reports
  // this Item is placed into. Stamped onto the briefing blob from state (P0);
  // NON-psu (PSU membership stays `kil_marked`). Absent/[] until placed.
  report_tags?: string[];
  // P3/P4d (R association axis): the within-project association tag-set (slugs
  // from `config.associations[].slug`). Stamped from state (P0); absent/[] until
  // placed. P4d renders the Association lens/filter over it; an Item's effective
  // set is the UNION of its own tags and all its Actions' (action-origin cascade,
  // computed at render — see itemAssociationUnion).
  association_tags?: string[];
  // SPA report-parity (T1 follow-up, 2026-10-28): the accepted 558 "Point"
  // (responsible firm/s, e.g. "Scale / KAMM"), projected from the state `point`
  // column onto the payload by agent `stamp_point`. Absent/null until the
  // recipient sets it (item_set_point). Read by the Key Issue Log report page to
  // render the Point column the generated .xlsx reads from state.
  point?: string | null;
  // - `breadcrumb`: italic inline annotation when this Item was
  //   recently moved between Categories (Decision 3.6).
  breadcrumb?: BreadcrumbView | null;
  // - `matter_note`: #66 consult annotation. `absorbed` = a re-minted
  //   recurrence of this Item's matter was folded into it this run (a
  //   breadcrumb). `ambiguous` = this Item may belong to a known matter —
  //   surfaced inline with a "place it" nudge to the existing Merge
  //   affordance (`targets` = the candidate member slugs). Render-only,
  //   re-derived every run by the consult (never persisted).
  matter_note?: MatterNoteView | null;
  // - `merge_candidate`: #66 D1-D3 dedup-consult overlap proposal (surface-
  //   only, re-derived every run). Renders an inline ◇ note + feeds the ✎
  //   menu's orange merge banner when a counterpart Item resolves.
  merge_candidate?: MergeCandidateView | null;
  completed?: AnyItem[];
  milestones?: AnyItem[];
  in_progress?: AnyItem[];
  decisions?: AnyItem[];
  blockers?: AnyItem[];
  action_items?: AnyItem[];
  open_questions?: AnyItem[];
  leadership_attention?: AnyItem[];
  // BACKLOG #28: per-Work-Item closed AIs. Rendered as a compact
  // "Closed action items (N)" sub-section under the WI card.
  archived_action_items?: ArchivedActionItemView[];
  // A² (2026-06-03): true when this Item first appears this period
  // (set deterministically by the agent's mark_new pass). Drives the
  // NEW pill + "New only" filter.
  is_new?: boolean;
  // A1 (2026-07-22): true when the narrative-merger judged this carryover
  // Item a MERGE this period (real new info). Drives the "Updated" pill +
  // "new/updated" filter. Set by merge_narratives; rides the blob like is_new.
  is_updated?: boolean;
}

export interface ResponsiblePartyView {
  name: string;
  email?: string | null;
  source?: "operator_override" | "synthesized" | "default_pm" | string;
  locked_at?: string;
}

export interface CandidateThreadView {
  slug: string;
  name: string;
  one_liner?: string;
  first_seen?: string;
  // NEW-this-period badge (mark_new): true when the suggested Category was
  // first proposed this run (first_seen in the week window).
  is_new?: boolean;
  // Slice 8 Item #1 Layer B: structural (promote/merge decision) vs
  // relevance_uncertain (confirm-belonging decision). Absent on v3
  // payloads emitted before the agent-side bucketing landed; defaults
  // to "structural" in the renderer.
  bucket?: "structural" | "relevance_uncertain";
}

export interface ArchivedWorkItemView {
  slug: string;
  name: string;
  closed_at?: string;
  closed_reason?: string;
}

// BACKLOG #28: closed AIs render in a per-work-item "Closed action
// items" sub-section. The serial is preserved through close so the
// reader sees what came off the plate this week.
export interface ArchivedActionItemView {
  ai_slug: string;
  serial: number;
  text: string;
  closed_at?: string;
  closed_reason?: string;
}

export interface RecommendedNextStep {
  text: string;
  // Machine-readable item id (hash). Preserved for future SPA features
  // that navigate back to the source item.
  source_ref?: string;
  observed_at?: string;
  // Human-readable label resolved by the agent harness from the source
  // item ("Ray Lastra email, 5/4"). When present, the renderer uses
  // this in place of the hash. Absent on briefings rendered before
  // the source-label feature landed; the renderer falls back to
  // source_ref then.
  attribution?: string;
}

export interface AnyItem {
  what?: string;
  // v4 payload key for Action title. Pydantic ActionItem uses
  // `text: str = Field(..., alias="what")` — serializes as `text`,
  // accepts `what` as input alias. SPA must read `text` first to
  // pick up the actual payload field; `what` retained as fallback
  // for any pre-v4 briefings that still hold the old key.
  text?: string;
  question?: string;
  // Slice 7 L3 feedback: every category entry can carry a 1-3 sentence
  // verbose description below the short `what`/`question` lead.
  // Synthesizer is prompted to split substance into a scannable bold
  // title (`what`) plus this verbose body. Optional; missing fall back
  // to title-only rendering.
  description?: string | null;
  owner?: string | null;
  assignee?: string | null;
  status?: string;
  decided_by?: string | null;
  decided_at?: string | null;
  due?: string | null;
  raised_by?: string | null;
  why?: string;
  // Slice 8 Item #6 (v3): non-null on impact-bearing action_items (the
  // formerly-blocker class). Pairs with `stale` to gate the Risk badge
  // — impact != null AND stale == false renders [Risk] + always-full.
  impact?: string | null;
  // Slice 7: object with text/source/date. Legacy briefings may still
  // carry a plain string; both shapes accepted, normalized at render.
  recommended_next_step?: RecommendedNextStep | string | null;
  significance?: string;
  notes?: string;
  attribution?: string;
  recommend_action?: string;
  // Slice 8 Item #6 (v3): unified carryover flag, replaces pre-v3
  // `persistent` for blocker-shaped items.
  stale?: boolean;
  first_seen?: string;
  // BACKLOG #28 (v3+): per-Work-Item stable AI handle. `ai_slug` is
  // the cross-week identity; `serial` is the renderer-friendly
  // integer (displayed as #NNN). Both are written by the pipeline's
  // action_items upsert; legacy or un-numbered AIs have neither and
  // render without the prefix.
  ai_slug?: string;
  serial?: number;
  // #66 D1-D3 (2026-07-23): dedup-consult overlap proposal on an Action —
  // inline ◇ advisory only (consolidation runs through the existing ✎ menu
  // "Merge into…" / "Move to another Item" affordances; no one-click in v1).
  merge_candidate?: MergeCandidateView | null;
  // v4 (payload_schema_version === 4) additions on action_item rows.
  // Other section kinds (completed, decisions, etc.) don't populate
  // these; they're declared here because AnyItem is the shared shape
  // across all section arrays.
  display_number?: string;        // path number, e.g. "1.1.2"
  narrative?: string | null;       // 1-2 line description (Decision 7.3)
  priority_status?: PriorityStatus;    // 4-state enum (Decision 1.3)
  action_type?: ActionType;            // "regular" | "invoices_aggregate"
  kil_marked?: boolean;                // Project Status Update mark
  // Slice 2 (2026-09-09): Action-tier report inclusion. Key Issue Log membership
  // is report_tags ∋ "558" (now action-keyed, consumed by the Slice-1 renderer);
  // PSU membership stays `kil_marked`.
  report_tags?: string[];
  reason?: string | null;              // Critical / Delay / On Hold metadata
  off_hold_condition?: string | null;  // On Hold metadata (Decision 4.2)
  // v4 puts a `responsible_party` directly on action rows so the
  // Action meta line can render "Responsible: NAME" (Decision 2.7
  // + 4.4). v3 used the work-item-level responsible_party only.
  responsible_party?: ResponsiblePartyView | null;
  open_questions_v4?: OpenQuestionView[];
  // ^ named `_v4` to avoid collision with the v2/v3 `open_questions`
  // category section under work_items, which is `AnyItem[]` (the
  // string-question shape). v4 puts open questions on Actions; the
  // SPA pivots to this field when payload_schema_version === 4.
  invoices?: InvoiceView[];            // billing aggregate (Section 11)
  breadcrumb?: BreadcrumbView | null;
  // Option Y: a live "propose closing" candidate on an open Action/Item.
  // Presence drives the *_dismiss_closure_candidate ("Keep open") affordance
  // (read via cast at the menu call sites — declared here so fixtures and
  // callers share one shape).
  closure_candidate?: { reason?: string } | null;
  closed_at?: string;
  // A1: closure reason — "merged into <target>" routes the Action to the
  // "Recently Merged" section. Set optimistically on merge submit.
  closed_reason?: string | null;
  // A2 (2026-06-03): true when this Action first appears this period
  // (agent mark_new pass). Drives the NEW pill + "New only" filter.
  is_new?: boolean;
  // A1 (2026-07-22): true when the narrative-merger judged this carryover
  // Action a MERGE this period (real new info). Drives the "Updated" pill +
  // "new/updated" filter. Set by merge_narratives; rides the blob like is_new.
  is_updated?: boolean;
  // P3/P4d (R association axis): the association tag-set on an Action (the
  // origin tier). Stamped from state (P0); absent/[] until placed. An Item's
  // effective association set unions these up (see itemAssociationUnion).
  association_tags?: string[];
  // P4d-2: a confident-only, TRANSIENT inferred-association proposal (the ghost
  // `?` chip). Stamped post-synth by the association-proposer when the Action has
  // empty association_tags on an axis project; null otherwise. Drives the ghost
  // badge + the Confirm/Dismiss menu items. Payload-only (never persisted).
  association_candidate?: {
    associations: string[];
    source_ref: string;
    proposed_at: string;
    source_subject?: string | null;
    source_author?: string | null;
    source_timestamp?: string | null;
  } | null;
}

// --- v4 (Category → Item → Action redesign) ---------------------------------

// 4-state priority on Actions, orthogonal to Open/Closed lifecycle
// (Decision 1.3). Drives the action-row left-edge color + status pill.
export type PriorityStatus = "Critical" | "Delay Potential" | "Active" | "On Hold" | "Informational";

// Action variant discriminator (Decision 11.1). `invoices_aggregate`
// is the per-Item "Invoices & Pay Applications" rollup with a nested
// `invoices[]` array; `regular` is everything else.
export type ActionType = "regular" | "invoices_aggregate";

export interface BreadcrumbView {
  /** #90 step 10 — "recategorized" (a real move) vs "renumbered" (an
   * ancestor's number changed; this entity did not change parent). Optional:
   * blobs written before step 10 carry no `kind` and fall back. */
  kind?: string;
  // Client-visible path notation the entity carried *before* the
  // move (e.g. "1.4"). Renders italic inline in the entity title.
  former_path: string;
  // ISO8601 calendar date of the move. Drives the 2-week
  // retention window (Decision 3.6) — after that, breadcrumb drops.
  moved_at: string;
}

/** The "(formerly N.M, recategorized DATE)" note, as ONE renderer.
 *
 * Extracted 2026-08-11. The 2026-08-10 ruling is that the briefing and the
 * state-derived snapshots must not say different things about a renumbered
 * entity. The agent already guarantees the DATA agrees by reading both from
 * `display_number_breadcrumbs`; this is the render half of the same guarantee
 * — Closed Items and the KIL/PSU view call this exact function rather than
 * re-implementing the wording, so the two surfaces cannot drift in phrasing
 * the way they previously drifted in content.
 *
 * `kind` falls back to "recategorized" for blobs written before step 10.
 */
export function renderBreadcrumb(bc: BreadcrumbView): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = "item-breadcrumb";
  el.textContent =
    `(formerly ${bc.former_path}, ${bc.kind ?? "recategorized"} ${bc.moved_at})`;
  return el;
}

// #66 PR3: the matter-ledger consult's inline annotation on an Item. The
// consult is SURFACE-ONLY, so the only kind is `ambiguous` — this Item may
// belong to a known matter/member; rendered inline with a nudge to place it
// via the existing Merge-Into-Existing affordance. `targets` are the candidate
// member work_item slugs (resolved to item numbers + names at render time).
export interface MatterNoteView {
  kind: "ambiguous";
  text: string;
  matter_ids?: string[];
  targets?: string[];
}

/** #66 D1-D3 (2026-07-23): structured locator for one counterpart node in a
 * merge_candidate — aligned index-for-index with `targets` labels. An Action
 * target's mergeable Item is its PARENT (`item`); an Item target is itself.
 * Slugs are the wire identity — never key an accept on display numbers. */
export interface MergeTargetView {
  thread: string;
  item: string;
  action?: string | null;
  label?: string;
}

/** #66 D1-D3: the dedup consult's SURFACE-ONLY cross-source overlap proposal
 * (the synth's own "overlaps with…" tell, re-derived every run). `targets` are
 * readable labels; `target_locators` (2026-07-23+) the machine twin — absent
 * on older blobs, so labels-only is the primary degrade path until the first
 * post-promote cron. */
export interface MergeCandidateView {
  kind: "overlap";
  text: string;
  targets?: string[];
  target_locators?: MergeTargetView[];
  evidence?: string[];
  // #66 D4 re-land (2026-09-29): which pass stamped this. `cross_reference_tell`
  // (default, absent on older blobs) = the D1-D3 dedup_consult prose tell, shown
  // INLINE. `judge` = the dedup_judge LLM flagging a NEW Item/Action as a
  // suspected duplicate of a CARRYOVER one — an Item so flagged is RELOCATED to
  // the Needs-Your-Input section (Ray's declutter framing) via isJudgeDup below.
  source?: "cross_reference_tell" | "judge";
  confidence?: "high" | "low" | null;
}

/** #66 D4: a v4 Item the dedup JUDGE flagged as a suspected duplicate of a
 * carryover Item. It stays a real tree work_item but is pulled OUT of the
 * category body and rendered under "Needs Your Input" instead (the same
 * in-tree-but-relocated discipline as the closed-item group and the #441
 * empty-category hide). Action-tier judge hints (source==="judge" on an
 * Action) are NOT relocated — the Item is legitimate, so its flagged Action
 * gets an inline one-click merge instead. */
export function isJudgeDup(
  wi: { status?: string; merge_candidate?: { source?: string | null } | null },
): boolean {
  return wi.merge_candidate?.source === "judge" && wi.status !== "closed";
}

/** #66 D4 (code review 2026-09-29): whether an Action renders its inline
 * "◇ …consolidate" overlap note. The D1-D3 dedup_consult tell (source
 * `cross_reference_tell`, the default) still renders inline, as it has for weeks.
 * A JUDGE-sourced action dup is SUPPRESSED from the body: its false positives are
 * high-confidence and would clutter exactly the surface Ray "stepped back" over,
 * and the action tier has no Needs-Your-Input relocation yet (a fast-follow). The
 * judge stamp still sits on the node for that fast-follow to surface. */
export function showsInlineActionOverlap(
  action: { status?: string; merge_candidate?: { source?: string | null } | null },
): boolean {
  return !!action.merge_candidate
    && action.merge_candidate.source !== "judge"
    && (action.status ?? "open") === "open";
}

export interface OpenQuestionView {
  text: string;
  // #52b: stable identity (state-owned; survives a synth reword). Absent on
  // pre-#52b blobs — the picker falls back to keying by text.
  qid?: string | null;
  raised_by?: string | null;
  raised_at?: string | null;
  // Populated when an operator marks the question answered. The
  // Action keeps its priority status throughout — Open Question is
  // a *field*, not a 5th priority (Decision 1.4).
  answer?: string | null;
  answered_at?: string | null;
  // N2/#117 — a confident, source-grounded proposed answer stamped by the
  // answer-proposer pass (present only while unanswered). The recipient
  // one-click-accepts it, which submits action_open_question_resolve{qid, answer}.
  resolution_candidate?: {
    answer: string;
    source_ref?: string | null;
    proposed_at?: string | null;
    source_subject?: string | null;
    source_author?: string | null;
    source_timestamp?: string | null;
  } | null;
}

export interface InvoiceView {
  number: string;
  received_date?: string | null;
  amount?: number | null;
  status?: "Received" | "Under Review" | "Approved" | "Paid" | "Disputed";
  due_date?: string | null;
  blocking_note?: string | null;
  source_ref?: string | null;
}

// v4 placement-orphan items the synthesizer couldn't tentatively route
// to a Category → Item (Decision 1.5). Renders in a top-level panel
// above the Category tree. Operator triages each via the pencil
// control (place in existing Item / create new Item / not relevant /
// flag next week / comment).
export interface UnfiledItemView {
  id: string;
  text: string;
  narrative?: string | null;
  source_ref: string;
  source_author?: string | null;
  source_timestamp?: string | null;
  attribution?: string | null;
  // Optional placement hint — populated when the synthesizer
  // detected that the item topic-matches a closed Item (Decision 4.8).
  hint?: string | null;
  // #61 (2026-06-09): "you're missing information" framing — what's absent
  // to place this as an Item/Action, plus the synth's best tier guess.
  // Absent on historical blobs (additive payload fields).
  missing?: string[] | null;
  suggested_tier?: "item" | "action" | null;
  // S3 accept-assist (2026-10-29): a machine-actionable merge target the agent
  // resolved from the hint, so the SPA can offer a one-click "Merge into «X»".
  // Only "merge_item" ships in v1 (place_item reserved). Absent on historical blobs.
  suggested_action?: "place_item" | "merge_item" | null;
  suggested_target_thread_slug?: string | null;
  suggested_target_work_item_slug?: string | null;
  // #60 PR4-v2 (#1.4): set on rows folded in from a candidate thread — the SPA
  // threads it through Place/Merge so the absorber retires the inferred thread.
  candidate_slug?: string | null;
  // A² (2026-06-03): true when this orphan first appears this period.
  is_new?: boolean;
}

// A² (2026-06-03): one entry in the Needs-Your-Input Informational
// bucket. Mirrors agent/payload_v4.py::InformationalItem.
export interface InformationalItemView {
  id: string;
  text: string;
  narrative?: string | null;
  source_ref: string;
  source_author?: string | null;
  source_timestamp?: string | null;
  attribution?: string | null;
  // Origin context when relocated out of the tree by the guard — shows
  // the recipient where it surfaced. Null when synth emitted it straight
  // to the bucket.
  origin_thread_slug?: string | null;
  origin_thread_name?: string | null;
  origin_work_item_slug?: string | null;
  origin_work_item_name?: string | null;
  is_new?: boolean;
  // A³/A² (2026-06-03): set on entries in informational_acknowledged[].
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  // CADENCE-REPORT-SUPPRESS (2026-10-29): set when the AGENT set the entry
  // aside as a recurring weekly ritual (vs a recipient ack); acknowledged_by
  // is suppressed by the actor sanitizer so the attribution is here.
  auto_acknowledged?: boolean;
  acknowledged_reason?: string | null;
}

export interface BriefingRecord {
  project_slug: string;
  run_id: string;
  week_start: string;
  week_end: string;
  stored_at: string;
  payload: BriefingPayload;
}

const SECTION_ORDER: Section[] = [
  "completed",
  "milestones",
  "in_progress",
  "decisions",
  "blockers",
  "action_items",
  "open_questions",
  "leadership_attention",
];

const SECTION_LABEL: Record<Section, string> = {
  completed: "Completed",
  milestones: "Milestones",
  in_progress: "In progress",
  decisions: "Decisions",
  blockers: "Blockers",
  action_items: "Action items",
  open_questions: "Open questions",
  leadership_attention: "Requires your attention",
};

export async function fetchBriefingIndex(): Promise<BriefingListItem[]> {
  const res = await getJson<{ briefings: BriefingListItem[] }>("/briefings");
  return res.briefings;
}

export async function fetchBriefing(runId: string): Promise<BriefingRecord> {
  return getJson<BriefingRecord>(`/briefings/${encodeURIComponent(runId)}`);
}

export interface SubmittedFeedbackEvent {
  event_id: string;
  run_id: string;
  project_slug: string;
  kind: string;
  target?: { type: string; id: string };
  payload: Record<string, unknown>;
  submitted_at: string;
  submitted_by: string;
  via: string;
  // B7: false once poll-portal has drained the event into corrections.yaml —
  // then it's no longer retractable (reverse with the inverse correction).
  // Absent on legacy responses → treated as retractable (Worker is the
  // authoritative gate and 409s an absorbed event).
  pending?: boolean;
}

/**
 * R2: does a submitted feedback event belong to a given summary-edit scope?
 * `scope === null` = the project-level Executive Summary (no sub_project_slug);
 * `scope === "<slug>"` = that sub-project band's summary. Both the project-level
 * and the per-band edit controls share the `project:<slug>` targetId, so each
 * filters its submitted chips through this — a North edit shows ONLY under North,
 * never under the project summary or another band. Exported so the scope
 * separation is unit-tested rather than trusted (adversarial-trace probe).
 */
export function isSummaryEditForScope(
  e: SubmittedFeedbackEvent, scope: string | null,
): boolean {
  if (e.kind !== "set_executive_summary") return false;
  const evScope = (e.payload?.sub_project_slug as string | undefined) ?? null;
  return evScope === scope;
}

export async function fetchMyFeedback(
  runId: string,
): Promise<SubmittedFeedbackEvent[]> {
  const res = await getJson<{ events: SubmittedFeedbackEvent[] }>(
    `/feedback/${encodeURIComponent(runId)}`,
  );
  return res.events;
}

// B7: retract a pending feedback event. Resolves on 200; throws ApiError
// (409 already-absorbed / 403 / 404) otherwise so the caller can message.
export async function retractFeedback(
  runId: string, eventId: string,
): Promise<void> {
  await deleteJson<{ retracted: string }>(
    `/feedback/${encodeURIComponent(runId)}/${encodeURIComponent(eventId)}`,
  );
}

export async function sendReadReceipt(runId: string): Promise<void> {
  // Best-effort. Failure is silent — receipts are heartbeats, not blocking.
  await postEmpty(`/receipt/${encodeURIComponent(runId)}`, {}).catch(
    () => undefined,
  );
}

// --- Rendering ---------------------------------------------------------------

export function formatStoredAt(iso: string): string {
  // Disambiguate same-week reruns in the index hover. Format as
  // "YYYY-MM-DD HH:MM" in the viewer's local zone — precise enough that
  // multiple reruns of the same week_end show distinct timestamps,
  // readable enough that operators can correlate with their own clock.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Cadence run-role for the briefings index label (#485 Mon+Thu cadence).
 * Two runs land per week — a Thursday mid-week in-progress pull and the
 * following Monday close — and both carry the SAME `week_end` (that Sunday),
 * so the bare "Week ending <date>" label collides in the list. The close runs
 * the day AFTER the week ends, so a stored date strictly after `week_end` is
 * the Final (close); on/before is the Mid-week pull. Pre-twice-weekly
 * (Monday-only) runs also store after their Sunday → Final, which is correct.
 * Compares UTC calendar dates (the ISO date prefix), never `Date()`, so a
 * 04:15-UTC Monday close is not pulled back to Sunday in a negative-offset
 * viewer zone. Returns null when either date is unparseable (label falls back
 * to no suffix — the pre-change behavior).
 */
export function briefingRunRole(
  weekEnd: string,
  storedAt: string,
): "final" | "mid_week" | null {
  const DATE = /^\d{4}-\d{2}-\d{2}$/;
  const storedDay = (storedAt ?? "").slice(0, 10);
  if (!DATE.test(weekEnd) || !DATE.test(storedDay)) return null;
  return storedDay > weekEnd ? "final" : "mid_week";
}

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Compact week_end for the index row: "2026-11-01" → "Nov 1" (no year — the
 * list is recent + reverse-chron, and the full date + year stays in the hover
 * title). Parses the ISO parts directly, never `Date()`, so there is no tz
 * off-by-one. Returns the input verbatim when it is not a YYYY-MM-DD date.
 */
export function formatWeekEndShort(weekEnd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(weekEnd ?? "");
  if (!m) return weekEnd;
  const mon = MONTH_ABBR[Number(m[2]) - 1];
  if (!mon) return weekEnd;
  return `${mon} ${Number(m[3])}`;
}


// Decision 12.11 (2026-05-28, client feedback #8): TOC anchor id format.
// Used by both the thread-header DOM id and the TOC link href.
export function categoryAnchorId(slug: string): string {
  return `category-${slug}`;
}

// #37: anchor for a phase band header, so renderTocV4 can jump to a band.
// Distinct namespace from `category-<slug>` (a band is not a Category).
// P4c: `scope` (a sub-project slug) keeps the id unique when the 4 bands
// repeat once per sub-project; a flat project passes none → unchanged id.
export function phaseAnchorId(phase: PhaseValue, scope?: string): string {
  return scope ? `sub-project-${scope}-phase-${phase}` : `phase-${phase}`;
}

// Decision 12.15 (2026-05-28): Needs Your Input TOC anchor. Distinct
// from `category-<slug>` because the Needs Your Input section is a
// peer of the Category tree, not a Category itself.
export const NEEDS_INPUT_ANCHOR_ID = "needs-your-input";

// Decision 12.11 (2026-05-28, client feedback #8): table-of-contents
// listing every visible Category in the current briefing, rendered
// below the briefing list in the sidebar. Click → anchor-scroll to
// the Category header. The active Category is NOT highlighted (no
// scroll-spy in v1; can layer in later if recipients ask).
//
// Decision 12.15 (2026-05-28): Needs Your Input section is included
// when present, prepended above the Category list. Distinct anchor
// id (NEEDS_INPUT_ANCHOR_ID) so it doesn't collide with a Category
// slug that happens to be "needs-your-input".
export interface TocCategory {
  slug: string;
  display_number?: string | null;
  name: string;
  // #37: the Category's phase band, for grouping the TOC under phase headers.
  phase?: PhaseValue | null;
  // P4c: the Category's sub-project, for grouping the TOC under sub-project
  // headers (only when `options.subProjects` is non-empty).
  sub_project_slug?: string | null;
}

export function renderTocV4(
  el: HTMLElement,
  categories: TocCategory[],
  options: { hasNeedsInput?: boolean; subProjects?: SubProjectView[] | null } = {},
): void {
  if (!categories.length && !options.hasNeedsInput) return;

  const heading = document.createElement("h2");
  heading.textContent = "Categories";
  el.appendChild(heading);

  const list = document.createElement("ul");
  list.className = "toc-list";

  // Per-entry anchor-scroll handler factored out so the Needs Your
  // Input row can share it.
  const buildRow = (
    href: string, anchorId: string, label: string, titleAttr: string,
  ): HTMLElement => {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.href = href;
    a.textContent = label;
    a.title = titleAttr;
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const target = document.getElementById(anchorId);
      if (target) {
        // P4c-2: a collapsed bubble-up Category expands on a TOC jump.
        const collapsedCat = target.closest<HTMLElement>(".thread.thread-v4.bubble-up.collapsed");
        if (collapsedCat) setCategoryCollapsed(collapsedCat, false);
        target.scrollIntoView({ behavior: "smooth", block: "start" });
        history.replaceState(null, "", href);
      }
    });
    li.appendChild(a);
    return li;
  };

  const buildCategoryRow = (c: TocCategory): HTMLElement => {
    const num = c.display_number ? `${c.display_number}. ` : "";
    return buildRow(
      `#${categoryAnchorId(c.slug)}`,
      categoryAnchorId(c.slug),
      `${num}${c.name}`,
      c.name,
    );
  };

  // #37: group the TOC under phase section headers when phasing is ACTIVE
  // (≥1 category in a non-general band). When everything is General
  // (unphased/legacy project) the TOC stays flat — byte-identical to the
  // pre-#37 list, no lone "General" header. Empty bands are omitted from the
  // TOC even under always-show: the TOC is a jump-list to content, not the
  // Brief structure itself. Within a band the numeric order matches the body.
  // P4c: the banded section is a helper so a sub-projected project can run it
  // once per sub-project (phase anchors scoped by `scope` so ids stay unique).
  const appendBandedRows = (cats: TocCategory[], scope?: string): void => {
    const byBand = new Map<PhaseValue, TocCategory[]>();
    for (const c of cats) {
      const band = phaseBand(c.phase);
      const arr = byBand.get(band);
      if (arr) arr.push(c);
      else byBand.set(band, [c]);
    }
    const phasingActive = PHASE_ORDER.some(
      (p) => p !== "general" && (byBand.get(p)?.length ?? 0) > 0,
    );

    if (phasingActive) {
      for (const phase of PHASE_ORDER) {
        const inBand = (byBand.get(phase) ?? []).slice().sort(
          (a, b) => compareDisplayNumber(a.display_number, b.display_number),
        );
        if (!inBand.length) continue;
        const sectionLi = document.createElement("li");
        sectionLi.className = "toc-phase-header";
        const sectionLink = document.createElement("a");
        sectionLink.href = `#${phaseAnchorId(phase, scope)}`;
        sectionLink.textContent = PHASE_LABELS[phase];
        sectionLink.addEventListener("click", (e) => {
          e.preventDefault();
          const target = document.getElementById(phaseAnchorId(phase, scope));
          if (target) {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            history.replaceState(null, "", `#${phaseAnchorId(phase, scope)}`);
          }
        });
        sectionLi.appendChild(sectionLink);
        list.appendChild(sectionLi);
        for (const c of inBand) list.appendChild(buildCategoryRow(c));
      }
    } else {
      for (const c of cats) list.appendChild(buildCategoryRow(c));
    }
  };

  const subProjects = sortedSubProjects(options.subProjects);
  if (subProjects.length) {
    // P4c: Sub-project header → (phase headers) → Category rows, in registry
    // seq order; a sub-project with no listed Category is omitted (the TOC is a
    // jump-list); null-sub_project Categories trail under their own header.
    const appendSubProjectHeader = (sp: SubProjectView): void => {
      const letter = subProjectLetter(sp.seq);
      const li = buildRow(
        `#${subProjectAnchorId(sp.slug)}`, subProjectAnchorId(sp.slug),
        letter ? `${letter} · ${sp.name}` : sp.name, sp.name,
      );
      li.className = "toc-sub-project-header";
      list.appendChild(li);
    };
    for (const sp of subProjects) {
      const inSp = categories.filter((c) => (c.sub_project_slug ?? null) === sp.slug);
      if (!inSp.length) continue;
      appendSubProjectHeader(sp);
      appendBandedRows(inSp, sp.slug);
    }
    const rest = categories.filter((c) => isUnassignedThread(c, subProjects));
    if (rest.length) {
      appendSubProjectHeader(UNASSIGNED_SUB_PROJECT);
      appendBandedRows(rest, UNASSIGNED_SUB_PROJECT.slug);
    }
  } else {
    appendBandedRows(categories);
  }

  // Needs Your Input goes LAST in the TOC — mirrors the body order
  // where the section is at the bottom of the briefing. Operator
  // direction (2026-05-28 follow-up). Dashed divider ABOVE
  // separates it from the Category list.
  if (options.hasNeedsInput) {
    const li = buildRow(
      `#${NEEDS_INPUT_ANCHOR_ID}`,
      NEEDS_INPUT_ANCHOR_ID,
      "Needs Your Input",
      "Things the agent couldn't place or decide — you decide what each is",
    );
    li.classList.add("toc-needs-input");
    list.appendChild(li);
  }
  el.appendChild(list);
}

// Decision 12.10 (2026-05-28, client feedback #7): briefing list shows
// at most BRIEFING_LIST_PAGE_SIZE entries by default. If there are more,
// a "Show N older briefings ↓" button reveals the rest. The active
// briefing is ALWAYS visible — if it lives outside the first page, the
// expand state defaults to expanded so the recipient sees their
// current location without an extra click.
export const BRIEFING_LIST_PAGE_SIZE = 8;

export function renderBriefingIndex(
  el: HTMLElement,
  briefings: BriefingListItem[],
  onPick: (runId: string) => void,
  activeRunId?: string,
): void {
  el.innerHTML = "";
  const heading = document.createElement("h2");
  heading.textContent = "Briefings";
  el.appendChild(heading);

  if (briefings.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "No briefings yet.";
    el.appendChild(empty);
    return;
  }
  const list = document.createElement("ul");
  list.className = "briefing-list";

  const appendRow = (b: BriefingListItem): void => {
    const li = document.createElement("li");
    if (activeRunId === b.run_id) li.classList.add("active");
    const a = document.createElement("a");
    a.href = `${apiPathPrefix()}/runs/${encodeURIComponent(b.run_id)}`;
    // #485: short date (no year) + a plain inline role suffix, all on one line —
    // "Week ending Nov 1 — Final" (Monday close) vs "— Mid" (Thursday
    // in-progress). The full date + year stay in the hover title.
    const role = briefingRunRole(b.week_end, b.stored_at);
    const suffix = role === "final" ? " — Final" : role === "mid_week" ? " — Mid" : "";
    a.textContent = `Week ending ${formatWeekEndShort(b.week_end)}${suffix}`;
    a.title = `${b.week_start} – ${b.week_end} · generated ${formatStoredAt(b.stored_at)}`;
    a.addEventListener("click", (e) => {
      e.preventDefault();
      onPick(b.run_id);
    });
    li.appendChild(a);
    list.appendChild(li);
  };

  const activeIndex = activeRunId
    ? briefings.findIndex((b) => b.run_id === activeRunId)
    : -1;
  const exceedsPage = briefings.length > BRIEFING_LIST_PAGE_SIZE;
  // Default expanded if the active briefing falls past the page cutoff.
  const startExpanded = exceedsPage && activeIndex >= BRIEFING_LIST_PAGE_SIZE;

  const initial = exceedsPage && !startExpanded
    ? briefings.slice(0, BRIEFING_LIST_PAGE_SIZE)
    : briefings;
  for (const b of initial) appendRow(b);
  el.appendChild(list);

  if (exceedsPage && !startExpanded) {
    const more = document.createElement("button");
    more.type = "button";
    more.className = "briefing-list-more";
    const remaining = briefings.length - BRIEFING_LIST_PAGE_SIZE;
    more.textContent = `Show ${remaining} older ↓`;
    more.setAttribute(
      "aria-label",
      `Show ${remaining} older briefings`,
    );
    more.addEventListener("click", () => {
      for (const b of briefings.slice(BRIEFING_LIST_PAGE_SIZE)) {
        appendRow(b);
      }
      more.remove();
    });
    el.appendChild(more);
  }
}

export function indexFeedbackByTarget(
  events: SubmittedFeedbackEvent[],
): Map<string, SubmittedFeedbackEvent[]> {
  const map = new Map<string, SubmittedFeedbackEvent[]>();
  for (const ev of events) {
    if (ev.kind === "read_receipt") continue;
    const id = ev.target?.id ?? "";
    if (!id) continue;
    const list = map.get(id) ?? [];
    list.push(ev);
    map.set(id, list);
  }
  return map;
}

export function indexCorrectionsByTarget(
  corrections: Correction[] | undefined,
): Map<string, Correction[]> {
  const map = new Map<string, Correction[]>();
  if (!corrections) return map;
  for (const c of corrections) {
    const id = c.target?.id;
    if (!id) continue;
    const list = map.get(id) ?? [];
    list.push(c);
    map.set(id, list);
  }
  return map;
}

// Slice 8 Item #3 — compactness toggle state. `recommended` defers to
// each work-item's synth-emitted `compact` flag; `compact` forces every
// work-item compact; `expanded` forces every work-item expanded.
// Persisted in localStorage so the operator's preference survives
// refreshes / navigation.
export type CompactnessMode = "recommended" | "compact" | "expanded";
const COMPACTNESS_KEY = "compactness-mode";
const COMPACTNESS_OVERRIDE_PREFIX = "compactness-override:";

export function readCompactnessMode(): CompactnessMode {
  try {
    const raw = localStorage.getItem(COMPACTNESS_KEY);
    if (raw === "compact" || raw === "expanded" || raw === "recommended") {
      return raw;
    }
  } catch {
    // localStorage unavailable (private browsing on some browsers).
  }
  return "recommended";
}

export function writeCompactnessMode(mode: CompactnessMode): void {
  try {
    localStorage.setItem(COMPACTNESS_KEY, mode);
  } catch {
    // ignore
  }
}

export function readCompactnessOverride(
  runId: string,
  targetId: string,
): "compact" | "expanded" | null {
  try {
    const raw = localStorage.getItem(
      `${COMPACTNESS_OVERRIDE_PREFIX}${runId}:${targetId}`,
    );
    if (raw === "compact" || raw === "expanded") return raw;
  } catch {
    // ignore
  }
  return null;
}

export function writeCompactnessOverride(
  runId: string,
  targetId: string,
  override: "compact" | "expanded" | null,
): void {
  try {
    const key = `${COMPACTNESS_OVERRIDE_PREFIX}${runId}:${targetId}`;
    if (override === null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, override);
    }
  } catch {
    // ignore
  }
}

/** Compute the effective compactness for a work-item: per-item
 *  override wins; else global mode; else the synth's `compact` hint
 *  (under "recommended"). */
export function effectiveCompactness(
  globalMode: CompactnessMode,
  override: "compact" | "expanded" | null,
  synthHint: boolean | undefined,
): boolean {
  if (override) return override === "compact";
  if (globalMode === "compact") return true;
  if (globalMode === "expanded") return false;
  return synthHint === true;
}

// Slice 8 Item #5 — participant options for the responsible_party_assign
// form's <datalist>. Three sources, merged + deduped by name:
//   1. Briefing body: names appearing in any work_item's responsible_party,
//      involved, or category entries' owner/assignee/decided_by/raised_by.
//   2. payload.project.team_members: configured project roster from
//      config/projects/<slug>.yaml (injected by the agent harness).
//   3. payload.project.participants: observed-in-traffic roster from
//      state.db's project_participants table (injected by the harness).
// Email metadata: takes the first non-null email seen per name. Sources
// 2 + 3 carry emails directly; source 1 only carries email from
// responsible_party.email. The ParticipantOption type lives in forms.ts
// where it pairs with FormControlSpec.
export function collectParticipantOptions(
  payload: BriefingPayload,
): ParticipantOption[] {
  const byName = new Map<string, ParticipantOption>();
  const ingest = (name: string | null | undefined, email?: string | null) => {
    if (!name) return;
    const key = name.trim();
    if (!key) return;
    const existing = byName.get(key);
    if (existing) {
      if (!existing.email && email) existing.email = email;
      return;
    }
    byName.set(key, { name: key, email: email ?? null });
  };

  const visitWorkItem = (wi: WorkItemView): void => {
    if (wi.responsible_party?.name) {
      ingest(wi.responsible_party.name, wi.responsible_party.email);
    }
    for (const name of wi.involved ?? []) ingest(name);
    const sections: AnyItem[][] = [
      wi.completed ?? [], wi.milestones ?? [], wi.in_progress ?? [],
      wi.decisions ?? [], wi.blockers ?? [], wi.action_items ?? [],
      wi.open_questions ?? [], wi.leadership_attention ?? [],
    ];
    for (const sec of sections) {
      for (const item of sec) {
        ingest(item.owner);
        ingest(item.assignee);
        ingest(item.decided_by);
        ingest(item.raised_by);
      }
    }
  };

  for (const thread of payload.threads ?? []) {
    for (const wi of thread.work_items ?? []) visitWorkItem(wi);
  }
  for (const wi of payload.unthreaded?.work_items ?? []) visitWorkItem(wi);

  // Roster seeds from the agent harness. team_members is the
  // operator-curated project YAML roster; participants is the
  // observed-in-traffic registry. Both feed the same dropdown so the
  // operator can pick a person even when they haven't appeared in
  // *this* briefing yet.
  for (const tm of payload.project.team_members ?? []) {
    ingest(tm.name, tm.email);
  }
  for (const p of payload.project.participants ?? []) {
    ingest(p.name, p.email);
  }

  return Array.from(byName.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

/** Numeric-aware compare of display numbers ("2.9" < "2.10" < "10"); null/
 * absent sort last. Orders the Category + Item pickers by their number
 * rather than alphabetically (#63 follow-up, 2026-06-09). */
/** #66 D1-D3 (2026-07-23): resolve a merge_candidate's counterpart(s) to
 * mergeable OPEN Items rendered this week, for the ✎ menu's one-click merge
 * banner. Pure — unit-tested directly.
 *
 * Structured path (`target_locators`, blobs from the first post-promote cron
 * on): an Action target's mergeable Item is its PARENT (`loc.item`); an Item
 * target is itself. A locator whose item slug isn't in `itemOptions` is
 * DROPPED — the consult stamps before demote/informational-relocate, so a
 * target can leave the rendered tree (correctness requirement, not polish).
 *
 * Labels-only degrade (every blob until that cron): one-click only on an
 * EXACT, UNIQUE, untruncated name match — a "…"-truncated label or an
 * ambiguous name never resolves (accept must not guess; the inline note +
 * menu nudge remain). Self-targets are always excluded. */
export function resolveMergeCandidateTargets(
  mc: MergeCandidateView | null | undefined,
  selfSlug: string,
  itemOptions: ItemOption[],
): Array<{ slug: string; label: string }> {
  if (!mc) return [];
  const out: Array<{ slug: string; label: string }> = [];
  const seen = new Set<string>();
  const push = (opt: ItemOption) => {
    if (opt.slug === selfSlug || seen.has(opt.slug)) return;
    seen.add(opt.slug);
    out.push({
      slug: opt.slug,
      label: opt.display_number ? `${opt.display_number} ${opt.name}` : opt.name,
    });
  };
  const locators = mc.target_locators ?? [];
  if (locators.length > 0) {
    for (const loc of locators) {
      if (!loc?.item) continue;
      const opt = itemOptions.find((o) => o.slug === loc.item);
      if (opt) push(opt);
    }
    return out;
  }
  for (const label of mc.targets ?? []) {
    if (!label || label.endsWith("…")) continue;
    const norm = label.trim().toLowerCase();
    const matches = itemOptions.filter((o) => o.name.trim().toLowerCase() === norm);
    const only = matches[0];
    if (matches.length === 1 && only) push(only);
  }
  return out;
}

/** #66 D1-D3: localStorage dismiss key for an Item's merge-proposal banner +
 * inline notes. Matter-only proposals keep the bare target id (back-compat —
 * Ray's existing dismissals stay honored); an overlap-bearing proposal gets a
 * distinct `:overlap` suffix so a previously dismissed matter hint does NOT
 * silently hide a NEW cross-source overlap signal (it resurfaces once). */
export function mergeBannerDismissKey(
  targetId: string,
  hasOverlap: boolean,
): string {
  return hasOverlap ? `${targetId}:overlap` : targetId;
}

// P4c: display_number parser that understands the P4b sub-project form
// ("A5" = sub-project letter + template index; Items/Actions inherit by
// string-concat: "A5.1", "A5.1.1") alongside the flat numeric form ("5",
// "5.1"). `group` orders lettered (0) before flat (1) before missing /
// unparseable (2) — the SAME rule as agent/synthesize_sort.py::
// thread_display_number_key, so the SPA and the markdown agree on Category
// order. A flat project has no letters, so its order is exactly the pre-P4c
// numeric order (the old comparator did `Number(segment) || 0` per segment;
// the flat branch below reproduces that byte-for-byte).
export function parseDisplayNumber(
  s?: string | null,
): { group: number; letter: number; parts: number[] } {
  if (!s) return { group: 2, letter: 0, parts: [] };
  const segs = String(s).trim().split(".");
  const head = segs[0] ?? "";
  const lettered = /^([A-Z])(\d+)$/.exec(head);
  if (lettered) {
    return {
      group: 0,
      letter: lettered[1]!.charCodeAt(0) - 65,
      parts: [Number(lettered[2]), ...segs.slice(1).map((x) => Number(x) || 0)],
    };
  }
  if (!/^\d+$/.test(head)) return { group: 2, letter: 0, parts: [] };
  return { group: 1, letter: 0, parts: segs.map((x) => Number(x) || 0) };
}

export function compareDisplayNumber(
  a?: string | null, b?: string | null,
): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const pa = parseDisplayNumber(a);
  const pb = parseDisplayNumber(b);
  if (pa.group !== pb.group) return pa.group - pb.group;
  if (pa.letter !== pb.letter) return pa.letter - pb.letter;
  for (let i = 0; i < Math.max(pa.parts.length, pb.parts.length); i++) {
    const x = pa.parts[i] ?? 0;
    const y = pb.parts[i] ?? 0;
    if (x !== y) return x - y;
  }
  return 0;
}

export interface KilLensRow { t: ThreadView; wi: WorkItemView; action: AnyItem }
export interface KilLensBand { label: string; actions: KilLensRow[] }

/** F-FLAT (Ray 2026-11-03): group the reports-lens Key Issue Log into Association
 *  band → flat action rows. Each action bands by its OWN `association_tags` (a
 *  both-tower action appears in each band). Honors the active Association chip filter
 *  (ultra #520): `shown` keeps only actions tagged with a SELECTED association, only
 *  the selected towers' bands render, and the "(No association)" band is dropped when
 *  filtering. `shown` drives the card count + its priority edge. A flat/no-axis
 *  project (`associationOptions` empty) returns one headerless band. */
export function groupKilActionsForLens(
  kilActs: KilLensRow[],
  associationOptions: Array<{ value: string; label: string }>,
  selectedAssoc: Set<string>,
): { bands: KilLensBand[]; shown: KilLensRow[] } {
  const assocActive = selectedAssoc.size > 0;
  const shown = assocActive
    ? kilActs.filter((r) => (r.action.association_tags ?? []).some((s) => selectedAssoc.has(s)))
    : kilActs;
  const byDn = (a: KilLensRow, b: KilLensRow) =>
    compareDisplayNumber(a.action.display_number, b.action.display_number);
  const bands: KilLensBand[] = [];
  const push = (label: string, acts: KilLensRow[]): void => {
    if (acts.length) bands.push({ label, actions: [...acts].sort(byDn) });
  };
  if (associationOptions.length) {
    const bandKnown = new Set(associationOptions.map((b) => b.value));
    const bandsToShow = assocActive
      ? associationOptions.filter((b) => selectedAssoc.has(b.value))
      : associationOptions;
    for (const band of bandsToShow) {
      push(band.label, shown.filter((r) => (r.action.association_tags ?? []).includes(band.value)));
    }
    if (!assocActive) {
      push("(No association)",
        shown.filter((r) => !(r.action.association_tags ?? []).some((s) => bandKnown.has(s))));
    }
  } else {
    push("", shown);
  }
  return { bands, shown };
}

// P4c: registry `seq` → the sub-project LETTER (1 → "A"); null outside 1..26
// (mirrors agent/state.py::_sub_project_letter).
export function subProjectLetter(seq?: number | null): string | null {
  if (seq == null || seq < 1 || seq > 26) return null;
  return String.fromCharCode(64 + seq);
}

// P4c: render order of the sub-project bands = registry seq ASC (the letter
// order); a seq-less row sorts last.
export function sortedSubProjects(list?: SubProjectView[] | null): SubProjectView[] {
  return [...(list ?? [])].sort((a, b) => (a.seq ?? 1e9) - (b.seq ?? 1e9));
}

// P4c: the trailing band for Categories with no sub_project (recipient-added
// inside a sub-projected project — the P4b known limit).
export const UNASSIGNED_SUB_PROJECT: SubProjectView = {
  slug: "__unassigned__", name: "Not in a sub-project", seq: null,
};

export function subProjectAnchorId(slug: string): string {
  return `sub-project-${slug}`;
}

// P4 trace ORD-1 (2026-09-03): a Category is "unassigned" when it has NO
// sub_project_slug OR one the registry does not know (config drift seeded
// register-once). Either way it renders under "Not in a sub-project" — it
// must never vanish from the default lens / TOC / md.
export function isUnassignedThread(
  t: { sub_project_slug?: string | null }, subProjects: Array<{ slug: string }>,
): boolean {
  return !t.sub_project_slug || !subProjects.some((sp) => sp.slug === t.sub_project_slug);
}

// A Category with nothing under it (no open OR closed Items). Hide-empty (Ray
// 2026-09-22) hides these from the render on BOTH the flat and sub-projected
// paths — up to 5 sub-projects × 13 standard Categories is 65 mostly-empty
// scaffold slots — unless the whole project is empty (the blank-guard shows the
// full structure). Predicate only; the render decides via `planPhaseBands`.
export function isEmptyCategory(
  t: { status?: string; work_items?: unknown[]; subcategories?: unknown[] },
): boolean {
  // P4 trace ORD-4: a recipient-created sub-category group is structure the
  // recipient placed — never fold it away even when it holds no Item yet.
  // F2 (2026-09-29 review): a judge-flagged suspected duplicate (isJudgeDup) is
  // relocated to Needs-Your-Input and drops from the category body, so a Category
  // whose ONLY Item is a judge-dup renders empty and must be hidden — exclude
  // judge-dups from the count (mirrors summarizeCategory's exclusion). Closed
  // Items are handled upstream (strip_closed_render_nodes), so the count is
  // otherwise unchanged.
  const bodyItems = (t.work_items ?? []).filter(
    (wi) => !isJudgeDup(wi as { status?: string; merge_candidate?: { source?: string | null } | null }),
  );
  return t.status !== "closed" && bodyItems.length === 0 && !(t.subcategories?.length);
}

// P4c-2 Bubble-Up safe-collapse (R §P4, "Lensed Briefing"): a collapsed
// Category header inherits its WORST open Action's priority (left edge) plus
// a priority dot-histogram and a new/updated dot, so collapse-by-default is
// safe — nothing urgent hides behind a closed header. Pure summary over the
// OPEN Items' OPEN Actions (closed rows never bubble); exported for tests.
export interface CategoryBubble {
  worst: PriorityStatus | null;          // null = no open Action
  counts: Partial<Record<PriorityStatus, number>>;
  openItems: number;
  changed: boolean;                      // any open Item/Action is_new || is_updated
  // Restyle round-2 (Ray 2026-09-08): the COUNT of new/updated open Items — the
  // new/updated indicator was a bare dot with no number ("18 active / 9 new … the
  // number's missing"). An open Item counts once if it OR any of its open Actions
  // is new/updated (pairs with the "N items" count).
  changedCount: number;
}

export function summarizeCategory(thread: {
  work_items?: Array<{
    status?: string; is_new?: boolean; is_updated?: boolean;
    merge_candidate?: { source?: string | null } | null;
    action_items?: Array<{
      status?: string; priority_status?: string; is_new?: boolean; is_updated?: boolean;
    }>;
  }>;
}): CategoryBubble {
  const counts: Partial<Record<PriorityStatus, number>> = {};
  let worstIdx = Number.POSITIVE_INFINITY;
  let openItems = 0;
  let changedCount = 0;
  for (const wi of thread.work_items ?? []) {
    // A judge-flagged suspected duplicate is relocated to Needs-Your-Input
    // (isJudgeDup) and drops from the category body, so exclude it from the
    // header "N items"/TOC count too — otherwise the count exceeds the visible
    // cards (code review 2026-09-29). The close-gate openItemCount in
    // renderCategoryV4 deliberately does NOT exclude it (Worker parity).
    if ((wi.status ?? "open") === "closed" || isJudgeDup(wi)) continue;
    openItems += 1;
    let wiChanged = !!(wi.is_new || wi.is_updated);
    for (const a of wi.action_items ?? []) {
      if ((a.status ?? "open") === "closed") continue;
      if (a.is_new || a.is_updated) wiChanged = true;
      const p = (PRIORITY_ORDER as readonly string[]).includes(a.priority_status ?? "")
        ? (a.priority_status as PriorityStatus) : "Active";
      counts[p] = (counts[p] ?? 0) + 1;
      const idx = PRIORITY_ORDER.indexOf(p);
      if (idx < worstIdx) worstIdx = idx;
    }
    if (wiChanged) changedCount += 1;
  }
  return {
    worst: Number.isFinite(worstIdx) ? PRIORITY_ORDER[worstIdx]! : null,
    counts, openItems, changed: changedCount > 0, changedCount,
  };
}

// P4c-2 collapse memory — per viewer, per project+Category (localStorage,
// same fail-soft pattern as spa.filterChangedOnly). Default = COLLAPSED: the
// safe-collapse chrome makes that the intended resting state.
export function collapseStorageKey(
  projectSlug: string, threadSlug: string, lens: string = "sub_project",
): string {
  // P4e: per-LENS memory — a Category collapsed in the Category lens stays
  // open in the Sub-project lens (design decision "per-lens collapse memory").
  return `spa.collapse.${lens}.${projectSlug}:${threadSlug}`;
}
export function readCollapsed(key: string, fallback = true): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === "1";
  } catch { return fallback; }
}
export function writeCollapsed(key: string, collapsed: boolean): void {
  try { localStorage.setItem(key, collapsed ? "1" : "0"); }
  catch { /* private mode — the class still applies for this view */ }
}

// Apply a collapse state to a rendered bubble-up Category section (class +
// aria + memory). Shared by the header toggle, Expand/Collapse all, and the
// TOC jump (which must expand its target).
export function setCategoryCollapsed(section: HTMLElement, collapsed: boolean): void {
  section.classList.toggle("collapsed", collapsed);
  section.querySelector(".category-collapse-toggle")
    ?.setAttribute("aria-expanded", collapsed ? "false" : "true");
  const key = section.dataset.collapseKey;
  if (key) writeCollapsed(key, collapsed);
}

// ---------------------------------------------------------------------------
// P4e-1 — the "Lensed Briefing" (R §P4): ONE row set, several client-side
// group-by reprojections. Rows (Category sections / Item cards) render through
// the same functions in every lens; only the grouping above them changes. All
// of it rides P4c's `payload.sub_projects[]` gate — a flat project never sees
// a lens nav and its DOM is byte-identical to pre-P4c.
//   sub_project (default) — Sub-project group → phase bands → Categories
//   category              — Category NAME (the standard set) → sub-project
//                           pathway → the Category section
//   priority              — Critical → Delay → Active → On Hold → sub-project
//                           → Category pathway → Item cards filtered to that
//                           priority's open Actions (+ a "No open Actions" tail)
//   search                — free text + Sub-project / Category / Priority
//                           facets → flat Item cards with a pathway line
// P4d: Association + Reports join the P4e-1 set (Sub-project/Category/
// Association/Priority/Reports + Search — design §"Lens set = 5"). Both are
// reprojections of EXISTING payload fields (`association_tags` from P3,
// `report_tags` from P1r): the Association lens groups by the axis value, the
// Reports lens by report inclusion. They appear in the nav only when the
// project actually HAS that axis (`availableLenses`) — a sub-projected project
// with no associations placed shows neither, so the nav never offers a lens
// that would put every row in one "Unplaced/None" bucket.
export type LensId = "sub_project" | "category" | "association" | "priority" | "reports" | "search";
// "search" is intentionally ABSENT here (restyle 2026-09-07): it is no longer a
// lens button but a transient focus mode opened from the ⌕ Advanced-search toolbar
// popover (see ActiveSearch). It stays in the LensId union + LENS_LABELS for
// back-compat with any stored lens value (readLens filters against availableLenses,
// so a stored "search" harmlessly falls back to sub_project).
export const LENS_ORDER: readonly LensId[] =
  ["sub_project", "category", "association", "priority", "reports"];
export const LENS_LABELS: Record<LensId, string> = {
  sub_project: "Sub-project", category: "Category", association: "Association",
  priority: "Priority", reports: "Reports", search: "Search",
};
// The lenses offered for THIS payload. `association`/`reports` are dropped when
// their axis is empty (no association placed anywhere / no report_tags anywhere)
// so the nav never lists a lens whose only group would be the catch-all bucket.
export function availableLenses(payload: BriefingPayload): readonly LensId[] {
  const hasAssoc = projectAssociationSlugs(payload).length > 0;
  const hasReports = payloadHasReportMembership(payload);
  return LENS_ORDER.filter(
    (id) => (id !== "association" || hasAssoc) && (id !== "reports" || hasReports),
  );
}

// --- Action-tier report membership (Catch B re-key, 2026-09-10) --------------
// The Key Issue Log (558) was re-keyed Item→Action on 2026-09-09 (Slice 1): a
// report's members are ACTIONS carrying `report_tags ∋ report_id`, and PSU
// membership is an ACTION's `kil_marked`. The SPA report surfaces (Reports lens,
// availableLenses, /reports/558) had stayed Item-`report_tags`-keyed and so
// under-showed the KIL. These helpers read the ACTION tier so an Item surfaces
// in a report exactly when it holds an OPEN action with that membership.
/** An Item's OPEN action carries `report_tags ∋ reportId`. */
export function itemHasReportAction(wi: WorkItemView, reportId: string): boolean {
  return (wi.action_items ?? []).some(
    (a) => (a.status ?? "open") !== "closed" && (a.report_tags ?? []).includes(reportId),
  );
}
/** B (2026-09-16, PSU→item-only): an Item is in the Project Status Update iff its
 *  OWN `kil_marked` flag is set (item-keyed). Was `itemHasPsuAction` (an open action
 *  marked); the PSU no longer keys on actions. */
export function itemHasPsuMark(wi: WorkItemView): boolean {
  return !!wi.kil_marked && (wi.status ?? "open") !== "closed";
}
/** REPORT-TAG-ONE-CLICK (2026-10-18): the one-click pill spec for the ✎ menu.
 *  Item → PSU (item-keyed `wi.kil_marked`, B); Action → KIL/558 (action-keyed
 *  `report_tags ∋ 558`). `undefined` (no pill) on a closed entity. Extracted from
 *  the render-spec builders so the open/closed gate + on-state are route-testable
 *  (ultra nit 2026-10-18) — the showPicker DOM wiring stays fixture-covered. */
export function itemReportToggle(
  wi: WorkItemView,
): { report: "psu"; on: boolean } | undefined {
  return wi.status === "closed"
    ? undefined
    : { report: "psu", on: Boolean(wi.kil_marked) };
}
export function actionReportToggle(
  a: { status?: string | null; report_tags?: string[] | null },
): { report: "kil"; on: boolean } | undefined {
  return a.status === "closed"
    ? undefined
    : { report: "kil", on: (a.report_tags ?? []).includes("558") };
}
/** Any Item anywhere is placed in ANY report — the reports-lens availability gate.
 *  Key Issue Log (558) is action-keyed (an OPEN action's report_tags); the PSU is
 *  item-keyed (the Item's own `kil_marked`, B). */
export function payloadHasReportMembership(payload: BriefingPayload): boolean {
  // Exclude judge-flagged suspected duplicates (review finding, 2026-09-30): the
  // Reports-lens BODY builds its rows from openItemsInLens, which drops isJudgeDup
  // items — so if the ONLY 558/PSU-bearing Item is a judge-dup, offering the lens
  // here leads to an empty tab. These three gates must agree with the body filter.
  return (payload.threads ?? []).some(
    (t) => (t.work_items ?? []).some(
      (wi) => !isJudgeDup(wi) && (itemHasPsuMark(wi)
        || (wi.action_items ?? []).some(
          (a) => (a.status ?? "open") !== "closed"
            && (a.report_tags ?? []).length > 0,
        )),
    ),
  );
}
export function lensStorageKey(projectSlug: string): string {
  return `spa.lens.${projectSlug}`;
}
export function readLens(projectSlug: string, payload?: BriefingPayload): LensId {
  const allowed: readonly string[] = payload ? availableLenses(payload) : LENS_ORDER;
  try {
    const v = localStorage.getItem(lensStorageKey(projectSlug));
    return allowed.includes(v ?? "") ? (v as LensId) : "sub_project";
  } catch { return "sub_project"; }
}
export function writeLens(projectSlug: string, lens: LensId): void {
  try { localStorage.setItem(lensStorageKey(projectSlug), lens); }
  catch { /* private mode — the lens still applies for this render */ }
}

// P4d: the multi-select Association filter selection, persisted per project.
// Stored as a JSON slug array; read back as a Set (stale slugs no longer in the
// axis are dropped by the caller against `projectAssociationSlugs`).
export function assocFilterKey(projectSlug: string): string {
  return `spa.assocfilter.${projectSlug}`;
}
export function readAssocFilter(projectSlug: string): Set<string> {
  try {
    const raw = localStorage.getItem(assocFilterKey(projectSlug));
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter((s): s is string => typeof s === "string") : []);
  } catch { return new Set(); }
}
export function writeAssocFilter(projectSlug: string, selected: Set<string>): void {
  try { localStorage.setItem(assocFilterKey(projectSlug), JSON.stringify([...selected])); }
  catch { /* private mode — the filter still applies for this render */ }
}

// Aggregate bubble across several Categories (a sub-project group's header).
export function aggregateBubbles(
  threads: Array<Parameters<typeof summarizeCategory>[0]>,
): CategoryBubble & { categories: number } {
  const counts: Partial<Record<PriorityStatus, number>> = {};
  let worstIdx = Number.POSITIVE_INFINITY;
  let openItems = 0;
  let changedCount = 0;
  for (const t of threads) {
    const b = summarizeCategory(t);
    openItems += b.openItems;
    changedCount += b.changedCount;
    for (const p of PRIORITY_ORDER) {
      const n = b.counts[p];
      if (!n) continue;
      counts[p] = (counts[p] ?? 0) + n;
      const idx = PRIORITY_ORDER.indexOf(p);
      if (idx < worstIdx) worstIdx = idx;
    }
  }
  return {
    worst: Number.isFinite(worstIdx) ? PRIORITY_ORDER[worstIdx]! : null,
    counts, openItems, changed: changedCount > 0, changedCount, categories: threads.length,
  };
}

// Category lens grouping: by NAME (case-insensitive, trimmed) — on a real
// sub-projected project the standard-set Categories share their template name
// across sub-projects, so this collapses "Permits" ×5 into one header. Groups
// order by the smallest template index among their members (the P4b
// `<letter><index>` number), then name; members keep display_number order.
export function groupThreadsByCategoryName<T extends { name: string; display_number?: string | null }>(
  threads: T[],
): Array<{ key: string; name: string; index: number; members: T[] }> {
  const groups = new Map<string, { key: string; name: string; index: number; members: T[] }>();
  for (const t of threads) {
    const key = t.name.trim().toLowerCase();
    const parsed = parseDisplayNumber(t.display_number);
    const index = parsed.group === 0 ? (parsed.parts[0] ?? 1e9) : 1e9;
    const g = groups.get(key);
    if (g) {
      g.members.push(t);
      if (index < g.index) g.index = index;
    } else {
      groups.set(key, { key, name: t.name.trim(), index, members: [t] });
    }
  }
  const out = [...groups.values()];
  for (const g of out) {
    g.members.sort((a, b) => compareDisplayNumber(a.display_number, b.display_number));
  }
  out.sort((a, b) => a.index - b.index || a.name.localeCompare(b.name));
  return out;
}

// P4d (R association axis): render-time helpers over the P3 `association_tags`.
// The axis is derived PURELY from the payload (config declaration order via
// `sub_projects[].associations`, plus any row-only slug appended first-seen);
// display is the humanized slug. Proper display names + config order + the
// agent-inferred (ghost) tags arrive with the P4d-2 registry stamp + proposer.
export function humanizeSlug(slug: string): string {
  return slug.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Restyle round-2 (Ray 2026-09-08): the SHORT association label — NT/ST/MA/TW.
// Ray wants the inline association tags abbreviated + de-coloured (the full
// names read as busy on every row). Derived CLIENT-SIDE from the config slug
// (the slug is already on the blob), so it renders on the EXISTING delivered
// blobs with no cron re-run. The known-slug map covers the two live axis
// projects (estates: master/north-tower/south-tower; portofino: master/tower);
// any other slug falls back to a deterministic heuristic — initials of a
// multi-word name, else the first two letters — so an unmapped project still
// abbreviates sensibly. ⚙️ The durable version is a config-supplied
// name→abbreviation (a follow-up that lands on a cron); this is the interim.
const ASSOC_ABBR: Record<string, string> = {
  master: "MA",
  "north-tower": "NT",
  "south-tower": "ST",
  tower: "TW",
};
export function abbreviateAssoc(slug: string): string {
  const known = ASSOC_ABBR[slug];
  if (known) return known;
  const words = slug.replace(/[-_]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  const w = words[0] ?? slug;
  return w.slice(0, 2).toUpperCase();
}

// The action-origin rollup: an Item's EFFECTIVE association set is the union of
// its OPEN Actions' association_tags — associations originate on Actions and the
// Item is a pure rollup (Ray 2026-09-15: "the item reports the accumulation of
// active actions; if it has no action tied to it any longer it drops off the
// association"). The Item's OWN `association_tags` column is NO LONGER a display
// input — the item pencil now CASCADES to the Actions (collectItemAssociationCascade)
// rather than writing an independent item tag. Closed Actions are excluded (a
// closed Action is "closed for the association"); the rendered payload already
// strips closed nodes, so `action_items` here is normally open-only, but the
// status guard keeps the rollup correct if an unstripped node is passed.
export function itemAssociationUnion(
  wi: { association_tags?: string[]; action_items?: Array<{ association_tags?: string[]; status?: string }> },
): string[] {
  const set = new Set<string>();
  for (const a of wi.action_items ?? []) {
    if (a.status === "closed") continue;
    for (const s of a.association_tags ?? []) set.add(s);
  }
  return [...set];
}

// P4d restyle (2026-09-07 "Lensed Briefing"): inline association tag chips (.atag)
// rendered ON item/action rows so a placed association reads at a glance by tower
// colour + dot. `ghost` = agent-inferred (dashed + a trailing "?" via CSS ::after,
// click-to-confirm). `data-a` carries the raw slug — the per-slug colour rules key
// on the real config slugs (master / north-tower / south-tower / tower); an
// unrecognised slug falls back to the neutral base chip.
export function renderAssocTagChip(slug: string, opts?: { ghost?: boolean }): HTMLElement {
  const chip = document.createElement("span");
  chip.className = opts?.ghost ? "atag ghost" : "atag";
  chip.setAttribute("data-a", slug);
  const dot = document.createElement("span");
  dot.className = "dot";
  chip.appendChild(dot);
  // Restyle round-2: abbreviated label (NT/ST/MA) — the full name stays on the
  // toolbar Association filter (the labelled control), which reads as the legend.
  const t = abbreviateAssoc(slug);
  const txt = document.createElement("span");
  txt.className = "atag-label";
  txt.textContent = t;
  txt.title = humanizeSlug(slug);
  chip.appendChild(txt);
  return chip;
}

// The unplaced-on-axis marker (⚑). On an Action it invites a place via the ✎ menu;
// on an Item it is a non-interactive roll-up flag (.wi-title .needs-chip drops the
// pointer). Rendered only on axis projects — see the associationOptions gate.
export function renderNeedsAssocChip(text: string): HTMLElement {
  const chip = document.createElement("span");
  chip.className = "needs-chip";
  chip.textContent = text;
  return chip;
}

// P4d restyle: the short report-chip label for a report slug. The 558 report is
// the "Key Issue Log" client-facing (2026-09-09 rename); its gutter chip is "KIL"
// (matching the 3-letter chip convention alongside PSU). The internal slug stays
// "558". `summary` is de-listed but its "SUM" chip stays for any legacy tag.
export function reportChipLabel(slug: string): string {
  const s = slug.toLowerCase();
  if (s === "558") return "KIL";
  if (s === "summary" || s === "scale-summary") return "SUM";
  if (s === "psu") return "PSU";
  return humanizeSlug(slug).replace(/\s+/g, "").slice(0, 4).toUpperCase();
}

// P4d restyle: the fixed right-gutter of report chips on a row. `.rchip` per
// label (thin purple outline), or an em-dash placeholder when the row is in no
// report — so the chips stay column-aligned down the card. Mounted on axis
// projects only (associationOptions gate), so a flat row keeps its plain shell.
const REPORT_CHIP_RANK: Record<string, number> = { KIL: 0, SUM: 1, PSU: 2 };
export function renderReportGutter(labels: string[]): HTMLElement {
  const g = document.createElement("span");
  g.className = "report-gutter";
  const ordered = [...labels].sort(
    (a, b) => (REPORT_CHIP_RANK[a] ?? 9) - (REPORT_CHIP_RANK[b] ?? 9),
  );
  if (ordered.length) {
    for (const lbl of ordered) {
      const c = document.createElement("span");
      c.className = "rchip";
      c.textContent = lbl;
      g.appendChild(c);
    }
  } else {
    const d = document.createElement("span");
    d.className = "dash";
    d.textContent = "—";
    g.appendChild(d);
  }
  return g;
}

// P4d restyle: Advanced search is a TRANSIENT focus mode, NOT a persisted lens —
// "search" was removed from LENS_ORDER, and the ⌕ Advanced-search toolbar button
// opens a facet popover. While a search is active, renderBriefingV4 renders flat
// cross-cutting results in place of the current lens, with a "← back to lens" that
// clears it. Module-scoped so it survives the in-place re-render; project-scoped
// so switching project/run drops it (activeSearchFor returns null for others).
interface ActiveSearch {
  projectSlug: string; text: string; assoc: string; cat: string; pri: string; rep: string;
}
let activeSearch: ActiveSearch | null = null;
function clearActiveSearch(): void { activeSearch = null; }
function activeSearchFor(projectSlug: string): ActiveSearch | null {
  return activeSearch && activeSearch.projectSlug === projectSlug ? activeSearch : null;
}
function activeSearchHasCriteria(s: ActiveSearch): boolean {
  return !!(s.text.trim() || s.assoc || s.cat || s.pri || s.rep);
}

// A ghost `?` chip's click target: a small fixed popover offering Confirm (→ the
// deployed P3 action_set_associations) / Dismiss (→ action_dismiss_association_
// candidate). Reuses the P4d-2 handlers; one popover at a time; closes on an
// outside click. On success it transforms the chip in place (solid = confirmed;
// removed = dismissed) — the next cron render is the durable source of truth.
function openAssocGhostPopover(
  anchor: HTMLElement,
  opts: {
    runId: string; targetId: string; slug: string;
    sourceLabel: string | null; chip: HTMLElement;
    // C3 (Ray 2026-10-18): the project's association axis, so a "Dismiss" can chain
    // straight into the inline picker ("disagree → assign the right one") instead of
    // dropping the recipient back to hunt the ✎ feedback menu. Empty/undefined → the
    // old behaviour (dismiss, chip removed, no follow-on picker).
    associationOptions?: Array<{ value: string; label: string }>;
  },
): void {
  document.querySelectorAll(".assoc-confpop").forEach((p) => p.remove());
  const pop = document.createElement("div");
  pop.className = "assoc-confpop";
  const t = document.createElement("p");
  t.className = "cp-t";
  t.appendChild(document.createTextNode("Agent inferred "));
  const b = document.createElement("b");
  b.textContent = humanizeSlug(opts.slug);
  t.appendChild(b);
  t.appendChild(document.createTextNode(" for this action."));
  pop.appendChild(t);
  if (opts.sourceLabel) {
    const s = document.createElement("p");
    s.className = "cp-src";
    s.textContent = opts.sourceLabel;
    pop.appendChild(s);
  }
  const btns = document.createElement("div");
  btns.className = "cp-btns";
  const yes = document.createElement("button");
  yes.type = "button"; yes.className = "cp-yes"; yes.textContent = "Confirm";
  const no = document.createElement("button");
  no.type = "button"; no.className = "cp-no"; no.textContent = "Dismiss";
  btns.appendChild(yes);
  btns.appendChild(no);
  pop.appendChild(btns);
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  pop.style.top = `${Math.min(r.bottom + 6, window.innerHeight - 120)}px`;
  pop.style.left = `${Math.min(r.left, window.innerWidth - 224)}px`;
  const close = (): void => {
    pop.remove();
    document.removeEventListener("mousedown", onDoc, true);
  };
  const onDoc = (e: MouseEvent): void => {
    if (!pop.contains(e.target as Node) && e.target !== anchor) close();
  };
  setTimeout(() => document.addEventListener("mousedown", onDoc, true), 0);
  yes.addEventListener("click", async () => {
    yes.disabled = true; no.disabled = true; yes.textContent = "Confirming…";
    try {
      await confirmProposedAssociations(opts.runId, opts.targetId, [opts.slug]);
      opts.chip.classList.remove("ghost");
      opts.chip.title = "";
      close();
    } catch {
      yes.disabled = false; no.disabled = false; yes.textContent = "Confirm";
    }
  });
  no.addEventListener("click", async () => {
    yes.disabled = true; no.disabled = true; no.textContent = "Dismissing…";
    try {
      await dismissProposedAssociation(opts.runId, opts.targetId, opts.slug);
      close();
      // C3 (Ray 2026-10-18): chain "disagree" straight into the inline association
      // picker so the recipient can set the RIGHT one without reopening the ✎ menu
      // (C12.3.1 — "I disagreed and it went away without providing an option of what
      // to assign"). Open the picker anchored to the (still-mounted) chip FIRST — it
      // reads the anchor's rect synchronously — THEN remove the dismissed ghost chip.
      // No options (flat project / none threaded in) → the prior behaviour: just drop.
      if (opts.associationOptions && opts.associationOptions.length) {
        openNeedsAssocPopover(opts.chip, {
          runId: opts.runId, targetId: opts.targetId,
          options: opts.associationOptions,
        });
      }
      opts.chip.remove();
    } catch {
      yes.disabled = false; no.disabled = false; no.textContent = "Dismiss";
    }
  });
}

// W2 ASSOC-ASSIGN-UX (2026-10-07): the inline association picker. The ⚑ "Needs association"
// flag on an unplaced, OPEN Action (axis projects only) is clickable — it opens a small fixed
// popover listing the project's association axis (the 1–5 config slugs) as TOGGLE rows + a
// "Set" button. An Action can belong to MORE THAN ONE association (e.g. a tower-spanning scope
// is Master + North Tower), so this is a multi-select (Ray 2026-10-08): toggle any rows, then
// Set submits the DEPLOYED replace-whole `action_set_associations` with the full selected set
// in ONE event (via confirmProposedAssociations — the same helper the ghost `?` popover uses).
// The picker then drives the optimistic re-render so the ⚑ swaps to the solid .atag chip(s)
// and the feedback is recorded. Replaces the prior 7-step ✎-menu → "Set associations" →
// multiselect → submit. Scope: Estates + any future multi-axis project (associationOptions
// gate). Portofino's 1:1 click-to-confirm is a candidate → the ghost popover path, NOT this
// one (DESCOPED). Mirrors openAssocGhostPopover's shape (fixed, one-at-a-time, outside-click
// closes WITHOUT applying — an unconfirmed selection is discarded).
function openNeedsAssocPopover(
  anchor: HTMLElement,
  opts: {
    runId: string; targetId: string;
    options: Array<{ value: string; label: string }>;
  },
): void {
  document.querySelectorAll(".assoc-confpop").forEach((p) => p.remove());
  const pop = document.createElement("div");
  pop.className = "assoc-confpop assoc-pickpop";
  const t = document.createElement("p");
  t.className = "cp-t";
  t.textContent = "Set association";
  pop.appendChild(t);
  const list = document.createElement("div");
  list.className = "cp-opts";
  pop.appendChild(list);
  // The staged selection — nothing is submitted until "Set" (so a tower-spanning Action can
  // be tagged with several associations in one go). Order follows the axis (config) order.
  const selected = new Set<string>();

  const apply = document.createElement("button");
  apply.type = "button";
  apply.className = "cp-apply";
  const syncApply = (): void => {
    apply.disabled = selected.size === 0;
    apply.textContent = selected.size > 1
      ? `Set ${selected.size} associations`
      : "Set association";
  };

  for (const o of opts.options) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "cp-opt";
    b.setAttribute("aria-pressed", "false");
    const check = document.createElement("span");
    check.className = "cp-check";
    check.textContent = "✓";
    b.appendChild(check);
    b.appendChild(renderAssocTagChip(o.value));
    const lbl = document.createElement("span");
    lbl.className = "cp-opt-label";
    lbl.textContent = o.label;
    b.appendChild(lbl);
    b.addEventListener("click", () => {
      // Toggle membership; no network until Set (replace-whole takes the whole set at once).
      if (selected.has(o.value)) {
        selected.delete(o.value);
        b.classList.remove("selected");
        b.setAttribute("aria-pressed", "false");
      } else {
        selected.add(o.value);
        b.classList.add("selected");
        b.setAttribute("aria-pressed", "true");
      }
      syncApply();
    });
    list.appendChild(b);
  }

  apply.addEventListener("click", async () => {
    if (selected.size === 0) return;
    // Axis order, not click order — stable + matches how the row renders the chips.
    const tags = opts.options.map((o) => o.value).filter((v) => selected.has(v));
    list.querySelectorAll("button").forEach((x) => { (x as HTMLButtonElement).disabled = true; });
    apply.disabled = true;
    try {
      // ONE replace-whole action_set_associations with the full selected set. Safe: this flag
      // only renders when the Action has NO placed tags (renderActionV4 `else` branch), so
      // there is nothing to clobber.
      await confirmProposedAssociations(opts.runId, opts.targetId, tags);
      // confirmProposedAssociations dispatches feedback-submitted (records the event for
      // persistence/replay) but NOT feedback-state-changed, so on its own the ⚑ chip would
      // not update until the next cron (verified in ?fixture=2 — the chip stayed ⚑). Drive the
      // optimistic re-render here, the same feedback-state-changed the ✎-menu set-associations
      // path and acceptProposedAnswer dispatch: applyFeedbackEventToPayload
      // (optimistic_feedback.ts `action_set_associations`) writes the Action node and
      // renderViewer swaps the ⚑ for the solid .atag chip(s) and recomputes the Item /
      // sub-project-band association roll-ups. Scoped to THIS picker — the ghost/candidate
      // confirm path (confirmProposedAssociations' other caller) is left byte-identical
      // (DESCOPED: Portofino 1:1 click-to-confirm untouched).
      document.dispatchEvent(new CustomEvent("feedback-state-changed", {
        detail: {
          kind: "action_set_associations",
          payload: { association_tags: tags },
          targetId: opts.targetId,
          targetType: "action_item",
        },
      }));
      close();
    } catch {
      list.querySelectorAll("button").forEach((x) => { (x as HTMLButtonElement).disabled = false; });
      syncApply();
    }
  });

  syncApply();
  pop.appendChild(apply);
  document.body.appendChild(pop);
  const r = anchor.getBoundingClientRect();
  pop.style.top = `${Math.min(r.bottom + 6, window.innerHeight - 220)}px`;
  pop.style.left = `${Math.min(r.left, window.innerWidth - 224)}px`;
  const close = (): void => {
    pop.remove();
    document.removeEventListener("mousedown", onDoc, true);
  };
  const onDoc = (e: MouseEvent): void => {
    if (!pop.contains(e.target as Node) && e.target !== anchor) close();
  };
  setTimeout(() => document.addEventListener("mousedown", onDoc, true), 0);
}

// The project's association axis as ORDERED slugs: config `sub_projects[]`
// declaration order first, then any slug appearing only on a row (recipient-
// placed outside the declared set) appended in first-seen order. Empty => the
// project has no association axis (→ no Association lens/filter, `availableLenses`).
export function projectAssociationSlugs(payload: BriefingPayload): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const add = (s: string): void => { if (s && !seen.has(s)) { seen.add(s); out.push(s); } };
  for (const sp of payload.sub_projects ?? []) for (const s of sp.associations ?? []) add(s);
  for (const t of payload.threads ?? []) {
    for (const wi of t.work_items ?? []) for (const s of itemAssociationUnion(wi)) add(s);
  }
  return out;
}

// Search lens matching — case-insensitive substring over the Category name,
// the Item name + narrative, and each open Action's text + narrative. An Item
// HIT (name/narrative) shows the whole card; otherwise only the hit Actions.
export interface SearchHit<A> { itemHit: boolean; actionHits: A[] }
export function itemSearchHits<A extends { status?: string; text?: string; what?: string; narrative?: string | null }>(
  categoryName: string,
  wi: { name: string; narrative?: string | null; action_items?: A[] },
  query: string,
): SearchHit<A> {
  const q = query.trim().toLowerCase();
  const has = (s?: string | null) => !!s && s.toLowerCase().includes(q);
  const openActions = (wi.action_items ?? []).filter((a) => a.status !== "closed");
  if (!q) return { itemHit: true, actionHits: openActions };
  const itemHit = has(categoryName) || has(wi.name) || has(wi.narrative);
  const actionHits = openActions.filter((a) => has(a.text) || has(a.what) || has(a.narrative));
  return { itemHit, actionHits };
}

// Build a {slug, name, display_number} list of Categories on this briefing
// for the move/merge target pickers (thread_merge, item_move_to_category,
// place_*). Only CONFIRMED, OPEN Categories are targets:
//  - closed/retired are skipped (you can't move/merge into a retired one);
//  - candidate_threads (inferred "Suggested Categories") are EXCLUDED — a
//    proposal isn't a real Category and must not be a move/merge target
//    until it's promoted (client feedback 2026-06-09; this reverses the
//    earlier contract-drift batch-2 inclusion). Promotion happens via the
//    candidate's own Needs-Your-Input "place as Category" affordance.
// Ordered by Category number (#63 follow-up), name as the tiebreaker.
export function collectThreadOptions(
  payload: BriefingPayload,
): ThreadOptionView[] {
  const out: ThreadOptionView[] = [];
  const seen = new Set<string>();
  const push = (
    slug: string | undefined, name: string | undefined,
    display_number?: string | null,
    subcategories?: { slug: string; name: string }[],
    sub_project_slug?: string | null,
  ) => {
    if (!slug || !name) return;
    if (seen.has(slug)) return;
    seen.add(slug);
    out.push({
      slug, name, display_number: display_number ?? null, subcategories,
      sub_project_slug: sub_project_slug ?? null,
    });
  };
  for (const t of payload.threads ?? []) {
    if (t.status === "closed" || t.status === "retired") continue;
    // #75: carry the Category's OPEN sub-categories for the deep-move picker.
    const openSubcats = (t.subcategories ?? [])
      .filter((s) => s.status !== "closed")
      .map((s) => ({ slug: s.slug, name: s.name }));
    // B3: carry the sub-project pointer for the Sub-project → Category pre-filter.
    push(t.slug, t.name, t.display_number, openSubcats, t.sub_project_slug);
  }
  return out.sort((a, b) =>
    compareDisplayNumber(a.display_number, b.display_number)
    || a.name.localeCompare(b.name));
}

// Local re-export of the forms.ts ThreadOption shape; defined here so
// briefings.ts doesn't take a runtime dep on forms.ts (the test suite
// imports collectThreadOptions standalone).
export interface ThreadOptionView {
  slug: string;
  name: string;
  display_number?: string | null;
  // #75: this Category's OPEN sub-categories, so the move/place picker can
  // offer "Category › Sub-category" destinations and file in one step.
  subcategories?: { slug: string; name: string }[];
  // B3 (2026-09-15): the Category's sub-project (ThreadView.sub_project_slug),
  // so the move/place picker can offer a Sub-project → Category pre-filter on
  // multi-axis projects (a flat 47-Category list otherwise). null on a flat
  // project or an unassigned Category — both stay reachable via the
  // "(No sub-project)" option.
  sub_project_slug?: string | null;
}

// Restyle 2026-09-07: on a LENSED (sub-projected) project the grouped toolbar is
// lifted OUT of the viewer to a full-width host above the layout, and the left-rail
// TOC becomes a per-lens jump list. app.ts owns those two hosts and passes them in;
// renderBriefingV4 clears + rebuilds them each render. Absent (flat projects, tests)
// → the toolbar renders inline in the viewer exactly as before (byte-identical).
export interface LensChromeHosts {
  toolbarHost?: HTMLElement;
  lensTocHost?: HTMLElement;
}
export function renderBriefingViewer(
  el: HTMLElement,
  record: BriefingRecord,
  feedbackByTarget?: Map<string, SubmittedFeedbackEvent[]>,
  chrome?: LensChromeHosts,
): void {
  el.innerHTML = "";
  const fb = feedbackByTarget ?? new Map<string, SubmittedFeedbackEvent[]>();
  // Corrections raised against THIS run_id (delivered via the agent's
  // briefing envelope at agent/portal.py::_corrections_for_run). Indexed
  // by target.id so each row can decorate itself; corrections without a
  // target.id ride along but aren't rendered here.
  const corr = indexCorrectionsByTarget(record.payload.corrections);

  // Slice 7 L3 feedback (2026-05-10): the download action moved from
  // a prominent text link here to a compact icon in the shell header
  // next to "Week ending {date}". `buildDownloadIcon` in app.ts owns
  // that render; the viewer leaves docx_b64 to the icon to surface.

  // #52b recoverability: render the Executive summary section whenever there IS a
  // summary OR the briefing is v4 (so the "Edit summary" pencil is always present
  // on v4). Without the v4 clause, clearing the summary made the section falsy →
  // the pencil vanished → no SPA path to re-add (recovery needed operator CLI).
  // v3 (immutable historical) keeps the prior behaviour: render only if present.
  const isV4Summary = record.payload.payload_schema_version === 4;
  const summaryText = record.payload.executive_summary;
  if (summaryText || isV4Summary) {
    const summary = document.createElement("section");
    summary.className = "summary";
    const h = document.createElement("h2");
    h.textContent = "Executive summary";
    // R2-3 fast-follow: a recipient can edit the executive summary directly (v4
    // only — v3 are immutable historical snapshots). The summary is a project-
    // level singleton, so the control targets `project:<slug>` (like the Add
    // Category pencil) and carries a single `set_executive_summary` kind,
    // prefilled with the current text (blank when empty → an "add" form). The
    // edit is reasserted onto the node every run (overrides the synth's
    // regenerated §14 summary). The title + pencil share a grid header row
    // (.summary-header), mirroring .thread-header — the `.controls` needs a
    // positioned ancestor or its popover escapes to the viewport corner (the L3
    // round-3 bug documented in styles.css).
    if (isV4Summary) {
      const header = document.createElement("div");
      header.className = "summary-header";
      header.appendChild(h);
      const summaryTargetId = `project:${record.project_slug}`;
      const editControls = renderFormControls({
        runId: record.run_id,
        targetType: "project",
        targetId: summaryTargetId,
        kinds: ["set_executive_summary"],
        // Filter to THIS control's kind: the Add-Category pencil shares the same
        // `project:<slug>` targetId, so an unfiltered list would cross-show the
        // other control's chips/badge.
        submitted: (fb.get(summaryTargetId) ?? []).filter(
          // R2: the project-level scope (null) — excludes per-sub-project edits,
          // which belong to their band's control.
          (e) => isSummaryEditForScope(e, null),
        ),
        current: { summary: summaryText },
      });
      editControls.classList.add("v4-edit-summary-control");
      header.appendChild(editControls);
      summary.appendChild(header);
    } else {
      summary.appendChild(h);
    }
    const p = document.createElement("p");
    if (summaryText) {
      p.textContent = summaryText;
    } else {
      // Empty (cleared, or none produced) — a muted placeholder; the pencil above
      // lets the recipient add one.
      p.textContent = "No executive summary this period.";
      p.className = "muted";
    }
    summary.appendChild(p);
    el.appendChild(summary);
  }

  const isV2 = record.payload.payload_schema_version === 2;
  const isV3 = record.payload.payload_schema_version === 3;
  const isV4 = record.payload.payload_schema_version === 4;

  // v4 takes a different shape from v2/v3 (Category → Item → Action
  // with explicit display numbers, priority enum, Open Questions
  // sub-section, Unfiled top-level). Dispatch through the parallel
  // renderer and return early; v4 doesn't reuse v3's thread/unthreaded/
  // candidate_threads helpers.
  if (isV4) {
    // Reuse the v3 collectors for participants + threads — they walk
    // the payload's people / thread fields and the SPA's responsible_
    // party_assign / thread_merge / move-to-category forms need the
    // same datalists.
    const v4Participants = collectParticipantOptions(record.payload);
    const v4Threads = collectThreadOptions(record.payload);
    renderBriefingV4(el, record, fb, corr, v4Participants, v4Threads, chrome);
    return;
  }

  // Participant options for responsible_party_assign — built once
  // per render from the payload's people fields. Passed to each v3
  // renderer below so the popover's form can populate a datalist.
  const participantOptions = isV3 ? collectParticipantOptions(record.payload) : [];

  // Thread options for thread_merge (thread-header) and thread_retag
  // (per-item-row). Same build-once-per-render shape as
  // participantOptions. V1/V2 paths fall back to free-text input plus
  // the slugify safety net in forms.ts::reshapePayloadForKind.
  const threadOptions = isV3 ? collectThreadOptions(record.payload) : [];

  // Re-render closure used by every interactive control that mutates
  // a localStorage flag (global compactness toggle, per-work-item
  // override). Invoking it clears the viewer and re-runs this
  // function against the same record.
  const rerender = (): void => {
    renderBriefingViewer(el, record, fb);
  };

  // Slice 8 Item #3 — v3 viewer mounts a global compactness toggle.
  // Clicking re-renders the viewer in place using the new mode.
  if (isV3) {
    el.appendChild(renderCompactnessToggle(rerender));
  }

  for (const thread of record.payload.threads ?? []) {
    if (isV3) {
      el.appendChild(renderThreadV3(record.run_id, thread, fb, corr, participantOptions, threadOptions, rerender));
    } else if (isV2) {
      el.appendChild(renderThreadV2(record.run_id, thread, fb, corr));
    } else {
      el.appendChild(renderThread(record.run_id, thread, fb, corr));
    }
  }

  if (record.payload.unthreaded) {
    if (isV3) {
      el.appendChild(renderUnthreadedV3(record.run_id, record.payload.unthreaded, fb, corr, participantOptions, threadOptions, rerender));
    } else if (isV2) {
      el.appendChild(renderUnthreadedV2(record.run_id, record.payload.unthreaded, fb, corr));
    } else {
      el.appendChild(renderUnthreaded(record.run_id, record.payload.unthreaded, fb, corr));
    }
  } else if (
    !(record.payload.threads ?? []).length &&
    isFlatLegacyPayload(record.payload)
  ) {
    // Pre-threads-refactor briefings (synthesizer output before
    // `feat(threads): organize briefings by sub-project thread`) have
    // section keys at the top level of payload — no `threads`, no
    // `unthreaded`. The SPA otherwise renders nothing for them. Route
    // the flat payload through renderUnthreaded with an "All items"
    // heading (vs the threads-schema "Cross-cutting" label) so the
    // section accurately describes its contents — there's no thread
    // breakdown to be cross-cutting against.
    el.appendChild(
      renderUnthreaded(
        record.run_id,
        record.payload as Partial<Record<Section, AnyItem[]>>,
        fb, corr,
        "All items",
      ),
    );
  }

  // Slice 8 Item #1 Layer B — candidate threads bucket. v3 renders
  // them between threads and needs_review with thread_promote /
  // thread_reject / comment controls; older payloads currently render
  // nothing (the markdown template handles them separately).
  if (isV3 && record.payload.candidate_threads?.length) {
    el.appendChild(
      renderCandidateThreadsV3(record.run_id, record.payload.candidate_threads, fb, corr),
    );
  }

  if (record.payload.needs_review?.length) {
    el.appendChild(renderNeedsReview(record.run_id, record.payload.needs_review, fb, corr));
  }
}

/** Detect briefings synthesized before the threads refactor. They have
 * `action_items` / `blockers` / etc. at the top level instead of inside
 * a `threads` or `unthreaded` block. Exported for unit test. */
export function isFlatLegacyPayload(p: BriefingRecord["payload"]): boolean {
  const flatKeys: Section[] = [
    "action_items", "blockers", "completed", "decisions",
    "in_progress", "leadership_attention", "milestones", "open_questions",
  ];
  const bag = p as unknown as Record<string, unknown>;
  return flatKeys.some((k) => Array.isArray(bag[k]));
}

function renderThread(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = thread.name;
  // Thread-level corrections (rename / merge / retag / approve raised
  // via the form path with target.id = "thread:<slug>") get a badge
  // alongside the title.
  const threadTargetId = threadKey(thread.slug);
  const threadBadge = renderCorrectedBadge(corr.get(threadTargetId));
  if (threadBadge) h2.appendChild(threadBadge);
  header.appendChild(h2);
  if (thread.one_liner) {
    const p = document.createElement("p");
    p.className = "muted";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }
  // Thread-level controls: rename, merge, retag, approve.
  const threadControls = renderFormControls({
    runId,
    targetType: "thread",
    targetId: threadTargetId,
    kinds: [
      "thread_approve",
      "thread_rename",
      "thread_merge",
      "comment",
    ],
    submitted: fb.get(threadTargetId) ?? [],
  });
  header.appendChild(threadControls);
  section.appendChild(header);

  for (const sec of SECTION_ORDER) {
    const items = thread[sec];
    if (!items?.length) continue;
    section.appendChild(
      renderSection(runId, items, sec, (i) =>
        threadItemKey(thread.slug, sec, i),
        fb, corr,
      ),
    );
  }
  return section;
}

function renderUnthreaded(
  runId: string,
  unthreaded: Partial<Record<Section, AnyItem[]>>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  heading: string = "Cross-cutting",
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread unthreaded";
  const h2 = document.createElement("h2");
  h2.textContent = heading;
  section.appendChild(h2);

  let any = false;
  for (const sec of SECTION_ORDER) {
    const items = unthreaded[sec];
    if (!items?.length) continue;
    any = true;
    section.appendChild(
      renderSection(runId, items, sec, (i) => unthreadedItemKey(sec, i), fb, corr),
    );
  }
  if (!any) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "Nothing here this week.";
    section.appendChild(empty);
  }
  return section;
}

// --- Slice 7 v2 render path -------------------------------------------------

function renderThreadV2(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread thread-v2";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = thread.name;
  const threadTargetId = threadKey(thread.slug);
  const threadBadge = renderCorrectedBadge(corr.get(threadTargetId));
  if (threadBadge) h2.appendChild(threadBadge);
  header.appendChild(h2);
  if (thread.one_liner) {
    const p = document.createElement("p");
    p.className = "muted";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }
  // Thread-level controls. The "Close thread" action is gated by the
  // open work-item count — the SPA shows a notice inside the popover
  // (instead of polluting the thread header with always-on text) and
  // the Worker rejects an unsafe POST as defense in depth.
  const openCount = (thread.work_items ?? []).filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  const threadKinds: string[] = [
    "thread_approve", "thread_rename", "thread_merge", "comment",
  ];
  if (thread.status === "closed") {
    threadKinds.push("thread_reopen");
  } else {
    threadKinds.push("thread_close");
  }
  const threadControls = renderFormControls({
    runId,
    targetType: "thread",
    targetId: threadTargetId,
    kinds: threadKinds,
    submitted: fb.get(threadTargetId) ?? [],
    notice: openCount > 0 && thread.status !== "closed"
      ? `Close ${openCount} open work item${openCount === 1 ? "" : "s"} before closing this thread.`
      : undefined,
  });
  header.appendChild(threadControls);
  section.appendChild(header);

  for (const wi of thread.work_items ?? []) {
    section.appendChild(renderWorkItem(runId, thread.slug, wi, fb, corr));
  }

  if ((thread.archived_work_items ?? []).length > 0) {
    section.appendChild(renderArchivedWorkItems(thread));
  }

  return section;
}

function renderWorkItem(
  runId: string,
  threadSlug: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"}`;

  const header = document.createElement("div");
  header.className = "work-item-header";

  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const targetId = workItemKey(threadSlug, wi.slug);
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  header.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }
  if (wi.involved?.length) {
    const meta = document.createElement("p");
    meta.className = "muted";
    meta.textContent = `Involved: ${wi.involved.join(", ")}`;
    header.appendChild(meta);
  }
  if (wi.key_dates?.length) {
    const meta = document.createElement("p");
    meta.className = "muted";
    meta.textContent = `Key dates: ${wi.key_dates.join(" · ")}`;
    header.appendChild(meta);
  }

  const wiKinds: string[] = ["comment"];
  if (wi.status === "closed") {
    wiKinds.unshift("work_item_reopen");
  } else {
    wiKinds.unshift("work_item_close");
  }
  header.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId,
    kinds: wiKinds,
    submitted: fb.get(targetId) ?? [],
  }));
  wrapper.appendChild(header);

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    wrapper.appendChild(
      renderSection(runId, items, sec, (i) =>
        workItemSectionItemKey(threadSlug, wi.slug, sec, i),
        fb, corr,
      ),
    );
  }
  return wrapper;
}

function renderArchivedWorkItems(thread: ThreadView): HTMLElement {
  const details = document.createElement("details");
  details.className = "archived-work-items";
  const summary = document.createElement("summary");
  const count = (thread.archived_work_items ?? []).length;
  summary.textContent = `Archived work items (${count})`;
  details.appendChild(summary);
  const ul = document.createElement("ul");
  for (const ar of thread.archived_work_items ?? []) {
    const li = document.createElement("li");
    const name = document.createElement("strong");
    name.textContent = ar.name;
    li.appendChild(name);
    const meta = document.createElement("span");
    meta.className = "muted";
    const bits: string[] = [];
    if (ar.closed_at) bits.push(`closed ${ar.closed_at}`);
    if (ar.closed_reason) bits.push(ar.closed_reason);
    if (bits.length) meta.textContent = " — " + bits.join(": ");
    li.appendChild(meta);
    ul.appendChild(li);
  }
  details.appendChild(ul);
  return details;
}

function renderUnthreadedV2(
  runId: string,
  unthreaded: NonNullable<BriefingPayload["unthreaded"]>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread unthreaded thread-v2";
  const h2 = document.createElement("h2");
  h2.textContent = "Cross-cutting";
  section.appendChild(h2);

  const workItems = unthreaded.work_items ?? [];
  for (const wi of workItems) {
    section.appendChild(renderUnthreadedWorkItem(runId, wi, fb, corr));
  }
  if (!workItems.length) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "Nothing here this week.";
    section.appendChild(empty);
  }
  return section;
}

function renderUnthreadedWorkItem(
  runId: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"}`;
  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const targetId = unthreadedWorkItemKey(wi.slug);
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  wrapper.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    wrapper.appendChild(p);
  }

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    wrapper.appendChild(
      renderSection(runId, items, sec, (i) =>
        unthreadedWorkItemSectionItemKey(wi.slug, sec, i),
        fb, corr,
      ),
    );
  }
  return wrapper;
}

// --- Slice 8 v3 render path -------------------------------------------------

function renderCompactnessToggle(onChange: () => void): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "compactness-toggle";
  const label = document.createElement("span");
  label.textContent = "View:";
  wrapper.appendChild(label);
  const current = readCompactnessMode();
  for (const mode of ["recommended", "compact", "expanded"] as CompactnessMode[]) {
    const btn = document.createElement("button");
    btn.type = "button";
    // Display label "Auto" for the synth-driven per-item mode; the
    // internal key stays "recommended" so localStorage values written
    // by older builds keep working (read tolerates either).
    btn.textContent =
      mode === "recommended" ? "Auto" :
      mode === "compact" ? "Compact" : "Expanded";
    if (mode === current) btn.classList.add("active");
    btn.addEventListener("click", () => {
      writeCompactnessMode(mode);
      onChange();
    });
    wrapper.appendChild(btn);
  }
  return wrapper;
}

// Inline SVG icons for the per-work-item compactness toggle. Sized
// 14×14 to match the .btn-edit pencil; same stroke weight + corner
// rounding.
const CHEVRON_DOWN_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="6 9 12 15 18 9"/>' +
  "</svg>";
const CHEVRON_UP_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="6 15 12 9 18 15"/>' +
  "</svg>";
// Counter-clockwise rotation arrow (Lucide "rotate-ccw" style).
// Universal "restore default" affordance — tooltip says "Restore
// Auto" to tie back to the global "Auto" mode label.
const RESET_ARROW_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<polyline points="1 4 1 10 7 10"/>' +
  '<path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>' +
  "</svg>";

/** Per-work-item compactness override toggle. Three states:
 *    • no override + currently compact   → chevron-down (action: expand)
 *    • no override + currently expanded  → chevron-up (action: compact)
 *    • override exists (either kind)     → reset-arrow (action: restore "Auto")
 *  Tooltip names the action. Lives in `.work-item-controls-row`
 *  alongside the pencil; pencil stays at the far right because it's
 *  the last flex child. */
function renderWorkItemCompactnessToggle(
  runId: string,
  targetId: string,
  override: "compact" | "expanded" | null,
  isCompact: boolean,
  rerender: () => void,
): HTMLElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "btn-edit work-item-toggle-btn";

  let svg: string;
  let title: string;
  let onClick: () => void;
  if (override !== null) {
    svg = RESET_ARROW_SVG;
    title = "Restore Auto";
    onClick = () => {
      writeCompactnessOverride(runId, targetId, null);
      rerender();
    };
  } else if (isCompact) {
    svg = CHEVRON_DOWN_SVG;
    title = "Expand this work item";
    onClick = () => {
      writeCompactnessOverride(runId, targetId, "expanded");
      rerender();
    };
  } else {
    svg = CHEVRON_UP_SVG;
    title = "Compact this work item";
    onClick = () => {
      writeCompactnessOverride(runId, targetId, "compact");
      rerender();
    };
  }

  btn.title = title;
  btn.setAttribute("aria-label", title);
  btn.innerHTML = svg;
  btn.addEventListener("click", onClick);
  return btn;
}

function renderThreadV3(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread thread-v3";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = thread.name;
  const threadTargetId = threadKey(thread.slug);
  const threadBadge = renderCorrectedBadge(corr.get(threadTargetId));
  if (threadBadge) h2.appendChild(threadBadge);
  header.appendChild(h2);
  if (thread.one_liner) {
    const p = document.createElement("p");
    p.className = "muted";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }
  // v3 thread controls: thread_confirm replaces thread_approve;
  // thread_reject is new. Close/reopen logic mirrors v2.
  const openCount = (thread.work_items ?? []).filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  const threadKinds: string[] = [
    "thread_confirm", "thread_reject", "thread_rename",
    "thread_merge", "comment",
  ];
  if (thread.status === "closed") {
    threadKinds.push("thread_reopen");
  } else {
    threadKinds.push("thread_close");
  }
  const threadControls = renderFormControls({
    runId,
    targetType: "thread",
    targetId: threadTargetId,
    kinds: threadKinds,
    submitted: fb.get(threadTargetId) ?? [],
    threadOptions,
    notice: openCount > 0 && thread.status !== "closed"
      ? `Close ${openCount} open work item${openCount === 1 ? "" : "s"} before closing this thread.`
      : undefined,
  });
  header.appendChild(threadControls);
  section.appendChild(header);

  const globalMode = readCompactnessMode();
  for (const wi of thread.work_items ?? []) {
    section.appendChild(renderWorkItemV3(runId, thread.slug, wi, fb, corr, globalMode, participantOptions, threadOptions, rerender));
  }

  if ((thread.archived_work_items ?? []).length > 0) {
    section.appendChild(renderArchivedWorkItems(thread));
  }

  return section;
}

function renderWorkItemV3(
  runId: string,
  threadSlug: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  globalMode: CompactnessMode,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const targetId = workItemKey(threadSlug, wi.slug);
  const override = readCompactnessOverride(runId, targetId);
  const isCompact = effectiveCompactness(globalMode, override, wi.compact);

  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"} ${isCompact ? "compact" : "expanded"}`;

  const header = document.createElement("div");
  header.className = "work-item-header";

  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  header.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }

  // Slice 8 Item #5 — Responsible Party line. v3 drops the prior
  // Involved/Key dates lines per client direction; payload still
  // carries `involved` and `key_dates` for historical re-renders
  // but v3 doesn't render them. Responsible Party is the only
  // people-line in v3.
  if (wi.responsible_party) {
    header.appendChild(renderResponsibleParty(wi.responsible_party));
  }

  // Right-aligned control cluster: per-work-item compactness toggle
  // followed by the pencil popover. Wrapped in a flex row pinned to
  // grid col 2 / row 1 via the CSS rule on .work-item-controls-row
  // so the pencil stays at the far right and the toggle sits to its
  // left.
  const wiKinds: string[] = ["responsible_party_assign", "comment"];
  if (wi.status === "closed") {
    wiKinds.unshift("work_item_reopen");
  } else {
    wiKinds.unshift("work_item_close");
  }
  const controlsRow = document.createElement("div");
  controlsRow.className = "work-item-controls-row";
  controlsRow.appendChild(
    renderWorkItemCompactnessToggle(runId, targetId, override, isCompact, rerender),
  );
  controlsRow.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId,
    kinds: wiKinds,
    submitted: fb.get(targetId) ?? [],
    participantOptions,
  }));
  header.appendChild(controlsRow);
  wrapper.appendChild(header);

  // Compact-mode pending list. Renders when (a) effectively compact
  // AND (b) the synth populated `pending`. Empty pending in compact
  // mode just suppresses the list — the always-full surfaces below
  // still render if they have content.
  //
  // BACKLOG #29: a "Pending" header sits above the list so the reader
  // sees the section label, matching the visual treatment of every
  // other category h3.
  if (isCompact && wi.pending?.length) {
    const heading = document.createElement("h3");
    heading.className = "work-item-pending-heading";
    heading.textContent = "Pending";
    wrapper.appendChild(heading);
    const ul = document.createElement("ul");
    ul.className = "work-item-pending";
    for (const text of wi.pending) {
      const li = document.createElement("li");
      li.textContent = text;
      ul.appendChild(li);
    }
    wrapper.appendChild(ul);
  }

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    // BACKLOG #29: in compact view, action_items is always rendered
    // (was: only when at least one item was impact-bearing-non-stale)
    // so the reader sees every action item, including routine ones,
    // as a title-only list. Description suppression for action_items
    // in compact mode is handled inside renderSectionV3.
    const isAlwaysFull = sec === "leadership_attention" ||
      sec === "action_items";
    wrapper.appendChild(
      renderSectionV3(runId, items, sec, (i) =>
        workItemSectionItemKey(threadSlug, wi.slug, sec, i),
        fb, corr,
        isCompact,
        isAlwaysFull,
        threadOptions,
      ),
    );
  }

  // BACKLOG #28: closed AIs per work item. Always rendered (regardless
  // of compactness) because the serial trail is the discussability
  // signal — a reader scanning the WI card needs to see what came off.
  if (wi.archived_action_items?.length) {
    wrapper.appendChild(renderArchivedActionItems(wi.archived_action_items));
  }

  return wrapper;
}

function renderArchivedActionItems(
  items: ArchivedActionItemView[],
): HTMLElement {
  // Collapsed by default (mirrors the item-level "Archived work items"
  // <details>) so recently-closed Actions stay available on click without
  // dominating the briefing after a big close-out — e.g. the 6/1 cleanup put
  // 64 closed Actions in the 2-week window (2026-06-07 density fix).
  const section = document.createElement("details");
  section.className = "section section-archived-action-items";
  const summary = document.createElement("summary");
  summary.textContent = `Closed action items (${items.length})`;
  section.appendChild(summary);
  const list = document.createElement("ul");
  for (const ar of items) {
    const li = document.createElement("li");
    li.className = "row";
    const text = document.createElement("div");
    text.className = "row-text";
    const handle = document.createElement("span");
    handle.className = "ai-handle";
    handle.textContent = `#${String(ar.serial).padStart(3, "0")}`;
    text.appendChild(handle);
    text.appendChild(document.createTextNode(ar.text));
    li.appendChild(text);
    if (ar.closed_at || ar.closed_reason) {
      const meta = document.createElement("p");
      meta.className = "muted";
      const parts: string[] = [];
      if (ar.closed_at) parts.push(`closed ${ar.closed_at}`);
      if (ar.closed_reason) parts.push(ar.closed_reason);
      meta.textContent = parts.join(": ");
      li.appendChild(meta);
    }
    list.appendChild(li);
  }
  section.appendChild(list);
  return section;
}

/** Section renderer for v3. When compact AND not always-full, the
 *  whole section is suppressed via CSS (.work-item.compact > .section
 *  rule). Items that are impact-bearing non-stale action_items get
 *  the [Risk] badge prepended. */
function renderSectionV3(
  runId: string,
  items: AnyItem[],
  section: Section,
  buildId: (index: number) => string,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  isCompact: boolean,
  isAlwaysFull: boolean,
  threadOptions: ThreadOptionView[],
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = `section section-${section}${isAlwaysFull ? " always-full" : ""}`;
  const h3 = document.createElement("h3");
  h3.textContent = SECTION_LABEL[section];
  wrapper.appendChild(h3);

  const list = document.createElement("ul");
  // BACKLOG #29: in compact view, action_items renders every item
  // title-only — descriptions are suppressed below. Routine items
  // no longer fold into `pending`; the Action Items section is the
  // single place a reader scans for them. Impact-bearing items still
  // get the [Risk] badge.
  const compactActionList = isCompact && section === "action_items";
  items.forEach((item, idx) => {
    const li = document.createElement("li");
    li.className = "row";
    const targetId = buildId(idx);
    li.dataset.targetId = targetId;

    const text = document.createElement("div");
    text.className = "row-text";
    // BACKLOG #28: per-Work-Item serial handle (e.g. #007). Present
    // only on action_items where the pipeline upsert assigned a serial.
    // Bare #NNN inside the WI card; parent composition is the reader's
    // responsibility for cross-references (BACKLOG decision 2026-05-19).
    if (section === "action_items" && typeof item.serial === "number") {
      const aiHandle = document.createElement("span");
      aiHandle.className = "ai-handle";
      aiHandle.textContent = `#${String(item.serial).padStart(3, "0")}`;
      text.appendChild(aiHandle);
    }
    if (section === "action_items" && isImpactBearingNonStale(item)) {
      const riskBadge = document.createElement("span");
      riskBadge.className = "risk-badge";
      riskBadge.textContent = "Risk";
      text.appendChild(riskBadge);
    }
    text.appendChild(document.createTextNode(describeItem(item)));
    const badge = renderCorrectedBadge(corr.get(targetId));
    if (badge) text.appendChild(badge);
    li.appendChild(text);

    // Description rules:
    // - open_questions: never rendered (Slice 8 Item #7, template
    //   matches).
    // - action_items in compact view: suppressed per BACKLOG #29
    //   so the section reads as a scannable title-only list.
    // - everything else: render when present.
    if (
      item.description
      && section !== "open_questions"
      && !compactActionList
    ) {
      const desc = document.createElement("p");
      desc.className = "row-description";
      desc.textContent = item.description;
      li.appendChild(desc);
    }

    // Meta-line rules (assignee · due · owner · etc.):
    // - action_items in compact view: suppressed per BACKLOG #29 —
    //   "title only" means no meta strip either.
    // - everything else: render when describeMeta() returns content.
    const meta = compactActionList ? null : describeMeta(item);
    if (meta) {
      const m = document.createElement("p");
      m.className = "muted";
      m.textContent = meta;
      li.appendChild(m);
    }

    li.appendChild(
      renderFormControls({
        runId,
        targetType: "item",
        targetId,
        kinds: kindsForSection(section),
        submitted: fb.get(targetId) ?? [],
        threadOptions,
      }),
    );

    list.appendChild(li);
  });
  wrapper.appendChild(list);
  return wrapper;
}

function isImpactBearingNonStale(item: AnyItem): boolean {
  return !!(item.impact && item.impact.length > 0) && item.stale !== true;
}

function renderResponsibleParty(rp: ResponsiblePartyView): HTMLElement {
  const p = document.createElement("p");
  p.className = "work-item-responsible";
  const label = document.createElement("span");
  label.className = "muted";
  label.textContent = "Responsible:";
  p.appendChild(label);
  const name = document.createElement("span");
  name.className = "work-item-responsible-name";
  name.textContent = rp.name;
  p.appendChild(name);
  if (rp.source === "default_pm") {
    const tag = document.createElement("span");
    tag.className = "work-item-responsible-source";
    tag.textContent = "(PM default)";
    p.appendChild(tag);
  } else if (rp.source === "operator_override") {
    const tag = document.createElement("span");
    tag.className = "work-item-responsible-source";
    tag.textContent = "(assigned)";
    p.appendChild(tag);
  }
  return p;
}

function renderUnthreadedV3(
  runId: string,
  unthreaded: NonNullable<BriefingPayload["unthreaded"]>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread unthreaded thread-v3";

  const header = document.createElement("div");
  header.className = "thread-header";
  const h2 = document.createElement("h2");
  h2.textContent = "Cross-cutting";
  header.appendChild(h2);
  section.appendChild(header);

  const globalMode = readCompactnessMode();
  const workItems = unthreaded.work_items ?? [];
  for (const wi of workItems) {
    section.appendChild(
      renderUnthreadedWorkItemV3(runId, wi, fb, corr, globalMode, participantOptions, threadOptions, rerender),
    );
  }
  if (!workItems.length) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.textContent = "Nothing here this week.";
    header.appendChild(empty);
  }
  return section;
}

function renderUnthreadedWorkItemV3(
  runId: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
  globalMode: CompactnessMode,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  rerender: () => void,
): HTMLElement {
  const targetId = unthreadedWorkItemKey(wi.slug);
  const override = readCompactnessOverride(runId, targetId);
  const isCompact = effectiveCompactness(globalMode, override, wi.compact);

  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"} ${isCompact ? "compact" : "expanded"}`;

  const header = document.createElement("div");
  header.className = "work-item-header";
  const h3 = document.createElement("h3");
  h3.textContent = wi.name;
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) h3.appendChild(badge);
  header.appendChild(h3);

  if (wi.summary) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }
  // v3 unthreaded work items: Responsible Party replaces Involved/Key
  // dates per client direction (parallel to renderWorkItemV3 above).
  if (wi.responsible_party) {
    header.appendChild(renderResponsibleParty(wi.responsible_party));
  }
  const wiKinds: string[] = ["responsible_party_assign", "comment"];
  if (wi.status === "closed") {
    wiKinds.unshift("work_item_reopen");
  } else {
    wiKinds.unshift("work_item_close");
  }
  const controlsRow = document.createElement("div");
  controlsRow.className = "work-item-controls-row";
  controlsRow.appendChild(
    renderWorkItemCompactnessToggle(runId, targetId, override, isCompact, rerender),
  );
  controlsRow.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId,
    kinds: wiKinds,
    submitted: fb.get(targetId) ?? [],
    participantOptions,
  }));
  header.appendChild(controlsRow);
  wrapper.appendChild(header);

  if (isCompact && wi.pending?.length) {
    const ul = document.createElement("ul");
    ul.className = "work-item-pending";
    for (const text of wi.pending) {
      const li = document.createElement("li");
      li.textContent = text;
      ul.appendChild(li);
    }
    wrapper.appendChild(ul);
  }

  for (const sec of SECTION_ORDER) {
    const items = wi[sec];
    if (!items?.length) continue;
    const isAlwaysFull = sec === "leadership_attention" ||
      (sec === "action_items" && items.some(isImpactBearingNonStale));
    wrapper.appendChild(
      renderSectionV3(runId, items, sec, (i) =>
        unthreadedWorkItemSectionItemKey(wi.slug, sec, i),
        fb, corr,
        isCompact,
        isAlwaysFull,
        threadOptions,
      ),
    );
  }
  return wrapper;
}

function renderCandidateThreadsV3(
  runId: string,
  candidates: CandidateThreadView[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  // Slice 8 Item #1 Layer B — split into structural (promote/merge
  // decision) and relevance_uncertain (confirm-belonging decision)
  // when the agent supplies the `bucket` field; otherwise render all
  // under one heading. The bucket discriminator is plumbed defensively;
  // until the agent emits it, candidates default to the structural
  // bucket and render with the "promote or reject" verb pair.
  const structural = candidates.filter(
    (c) => (c.bucket ?? "structural") === "structural",
  );
  const relevance = candidates.filter((c) => c.bucket === "relevance_uncertain");

  const wrapper = document.createElement("section");
  wrapper.className = "candidates-section";

  if (structural.length) {
    const heading = document.createElement("h3");
    // Decision 9 (close-out): user-facing label switched from
    // "Candidate Threads" to "Suggested Categories". Internal field
    // name (`candidate_threads`) stays for backward compat.
    heading.textContent = relevance.length
      ? "Suggested Categories — promote or reject"
      : "Suggested Categories";
    wrapper.appendChild(heading);
    for (const c of structural) {
      wrapper.appendChild(renderCandidateRow(runId, c, fb, corr));
    }
  }
  if (relevance.length) {
    const heading = document.createElement("h3");
    heading.textContent = "Relevance-uncertain — confirm belonging";
    wrapper.appendChild(heading);
    for (const c of relevance) {
      wrapper.appendChild(renderCandidateRow(runId, c, fb, corr));
    }
  }

  return wrapper;
}

function renderCandidateRow(
  runId: string,
  candidate: CandidateThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const row = document.createElement("div");
  row.className = "candidate-row";
  const name = document.createElement("div");
  name.className = "candidate-name";
  name.textContent = candidate.name;
  const targetId = threadKey(candidate.slug);
  const badge = renderCorrectedBadge(corr.get(targetId));
  if (badge) name.appendChild(badge);
  row.appendChild(name);
  if (candidate.one_liner) {
    const p = document.createElement("p");
    p.className = "candidate-one-liner";
    p.textContent = candidate.one_liner;
    row.appendChild(p);
  }
  row.appendChild(renderFormControls({
    runId,
    targetType: "thread",
    targetId,
    kinds: ["thread_promote", "thread_reject", "comment"],
    submitted: fb.get(targetId) ?? [],
  }));
  return row;
}

function renderNeedsReview(
  runId: string,
  items: Array<{ item_id: string; summary: string; why_flagged?: string }>,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "needs-review";
  const h2 = document.createElement("h2");
  h2.textContent = "Needs review";
  section.appendChild(h2);
  const ul = document.createElement("ul");
  for (const item of items) {
    const li = document.createElement("li");
    li.className = "row";
    const summary = document.createElement("div");
    summary.className = "row-text";
    summary.textContent = item.summary;
    const targetId = needsReviewItemKey(item.item_id);
    const badge = renderCorrectedBadge(corr.get(targetId));
    if (badge) summary.appendChild(badge);
    li.appendChild(summary);
    if (item.why_flagged) {
      const why = document.createElement("p");
      why.className = "muted";
      why.textContent = item.why_flagged;
      li.appendChild(why);
    }
    li.appendChild(
      renderFormControls({
        runId,
        targetType: "item",
        targetId,
        // Decision 2: review_clarification removed from SPA (operator-only).
        kinds: ["not_relevant", "comment"],
        submitted: fb.get(targetId) ?? [],
      }),
    );
    ul.appendChild(li);
  }
  section.appendChild(ul);
  return section;
}

function renderSection(
  runId: string,
  items: AnyItem[],
  section: Section,
  buildId: (index: number) => string,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  corr: Map<string, Correction[]>,
): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = `section section-${section}`;
  const h3 = document.createElement("h3");
  h3.textContent = SECTION_LABEL[section];
  wrapper.appendChild(h3);

  const list = document.createElement("ul");
  items.forEach((item, idx) => {
    const li = document.createElement("li");
    li.className = "row";
    const targetId = buildId(idx);
    li.dataset.targetId = targetId;

    const text = document.createElement("div");
    text.className = "row-text";
    text.textContent = describeItem(item);
    const badge = renderCorrectedBadge(corr.get(targetId));
    if (badge) text.appendChild(badge);
    li.appendChild(text);

    // Slice 7 L3 feedback: verbose 1-3 sentence description below
    // the bold lead. Only renders when synthesizer populated it.
    if (item.description) {
      const desc = document.createElement("p");
      desc.className = "row-description";
      desc.textContent = item.description;
      li.appendChild(desc);
    }

    const meta = describeMeta(item);
    if (meta) {
      const m = document.createElement("p");
      m.className = "muted";
      m.textContent = meta;
      li.appendChild(m);
    }

    li.appendChild(
      renderFormControls({
        runId,
        targetType: "item",
        targetId,
        kinds: kindsForSection(section),
        submitted: fb.get(targetId) ?? [],
      }),
    );

    list.appendChild(li);
  });
  wrapper.appendChild(list);
  return wrapper;
}

export function correctedBadgeTitle(corrections: Correction[]): string {
  // Pure text-formatting separated from DOM creation so it's unit-testable
  // without a DOM environment. The DOM-side `renderCorrectedBadge` builds
  // the span and uses this for the hover tooltip.
  return corrections.map(describeCorrection).join("\n");
}

export function renderCorrectedBadge(
  corrections: Correction[] | undefined,
): HTMLElement | null {
  if (!corrections || corrections.length === 0) return null;
  const span = document.createElement("span");
  span.className = "corrected-badge";
  span.textContent = " ✓ corrected";
  span.title = correctedBadgeTitle(corrections);
  return span;
}

function describeCorrection(c: Correction): string {
  const when = c.recorded_at ? ` (${c.recorded_at.slice(0, 10)})` : "";
  const who = c.recorded_by ? ` by ${c.recorded_by}` : "";
  switch (c.kind) {
    case "operator_note": {
      const subkind = c.kind_note ?? "note";
      const text = c.text ? `: ${c.text}` : "";
      return `${subkind}${text}${who}${when}`;
    }
    case "date_correction":
      return `date_correction · ${c.field ?? "?"} → ${c.new_value ?? "?"}${who}${when}`;
    case "thread_rename":
      return `thread_rename · ${c.from_slug ?? "?"} → ${c.to_slug ?? "?"}${who}${when}`;
    case "thread_merge":
      return `thread_merge · ${(c.source_slugs ?? []).join(", ")} → ${c.target_slug ?? "?"}${who}${when}`;
    case "thread_tag":
      return `thread_tag · ${c.from_thread ?? "?"} → ${c.to_thread ?? "?"}${who}${when}`;
    case "classification":
      return `classification · ${c.item_id ?? "?"}${who}${when}`;
    default:
      return `${c.kind}${who}${when}`;
  }
}

function describeItem(item: AnyItem): string {
  return item.what ?? item.question ?? "(no description)";
}

function describeMeta(item: AnyItem): string {
  const bits: string[] = [];
  if (item.owner) bits.push(`Owner: ${item.owner}`);
  if (item.assignee) bits.push(`Assignee: ${item.assignee}`);
  if (item.decided_by) bits.push(`Decided by: ${item.decided_by}`);
  if (item.decided_at) bits.push(`Decided: ${item.decided_at}`);
  if (item.due) bits.push(`Due: ${item.due}`);
  if (item.status) bits.push(`Status: ${item.status}`);
  if (item.impact) bits.push(`Impact: ${item.impact}`);
  const nextStep = formatRecommendedNextStep(item.recommended_next_step);
  if (nextStep) bits.push(`Next: ${nextStep}`);
  return bits.join(" · ");
}

export function formatRecommendedNextStep(
  v: RecommendedNextStep | string | null | undefined,
): string {
  if (!v) return "";
  if (typeof v === "string") return v;  // Legacy payload shape.
  // Prefer the human attribution (e.g., "Ray Lastra email, 5/4")
  // resolved by the harness. Fall back to the source_ref hash for
  // briefings rendered before the source-label feature landed.
  const sourceLabel = v.attribution ?? v.source_ref;
  const attrib: string[] = [];
  if (sourceLabel) attrib.push(`per ${sourceLabel}`);
  if (v.observed_at) attrib.push(v.observed_at);
  return attrib.length ? `${v.text} (${attrib.join(", ")})` : v.text;
}

// Open-question attribution line. Only prefix "Raised by" when a name is
// present; an unattributed question (raised_by null — the extractor couldn't
// identify an asker) must not render a dangling "Raised by <date>" to the
// client. Returns null when there's nothing to show.
export function openQuestionMeta(
  raisedBy: string | null | undefined,
  raisedAt: string | null | undefined,
): string | null {
  if (raisedBy) {
    const bits = [raisedBy];
    if (raisedAt) bits.push(raisedAt);
    return `Raised by ${bits.join(", ")}`;
  }
  if (raisedAt) return `Raised ${raisedAt}`;
  return null;
}

// N2/#117 — the Q line pairs provenance inline ("asked by X, date"), so the
// question and who asked it stay together. Reuses openQuestionMeta's logic.
export function askedByMeta(
  raisedBy: string | null | undefined,
  raisedAt: string | null | undefined,
): string | null {
  const m = openQuestionMeta(raisedBy, raisedAt);
  return m
    ? m.replace(/^Raised by /, "asked by ").replace(/^Raised /, "asked ")
    : null;
}

// N2/#117 — format a proposed answer's DETERMINISTIC source provenance for the
// citation line: `From "<subject>" — <author>, <date>`, with graceful fallbacks
// (no subject for a transcript/doc source; any field may be absent).
export function proposedSourceLabel(rc: {
  source_subject?: string | null;
  source_author?: string | null;
  source_timestamp?: string | null;
}): string | null {
  const subj = (rc.source_subject ?? "").trim();
  const who = (rc.source_author ?? "").trim();
  const date = formatSourceDate(rc.source_timestamp);
  const whoDate = [who, date].filter(Boolean).join(", ");
  if (subj && whoDate) return `From “${subj}” — ${whoDate}`;
  if (subj) return `From “${subj}”`;
  if (whoDate) return `From ${whoDate}`;
  return null;
}

// Parse a "Mon D" label from an ISO timestamp deterministically — take the date
// parts straight from the string, so no Date() timezone off-by-one.
function formatSourceDate(ts: string | null | undefined): string {
  if (!ts) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ts);
  const mo = m?.[2];
  const day = m?.[3];
  if (!mo || !day) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const name = months[parseInt(mo, 10) - 1];
  if (!name) return "";
  return `${name} ${parseInt(day, 10)}`;
}

export function kindsForSection(section: Section): string[] {
  // Operator-only kinds removed from SPA (Decision 2 close-out):
  //  - thread_retag: redundant with v4 item_move_to_category
  //  - date_correction: field-enum + LLM-mechanics, operator data hygiene
  //  - review_clarification: verdict-enum, LLM-mechanics
  // All three remain in the absorber + CLI for backward compat.
  void section;
  // D4 (2026-10-16): missing_item + flag_next_week removed from every menu tier
  // (non-additive notes; carryover is automatic).
  return ["not_relevant", "comment"];
}

// --- v4 (Task #9) kind menus -----------------------------------------------
//
// One menu per pencil affordance. Each menu lists the kinds available at
// that level. Mirrors the agent-side contract from Task #8 (see
// agent/corrections.py + agent/feedback_absorb.py). Pencil placement is
// in renderCategoryV4 / renderItemV4 / renderActionV4 / renderUnfiledV4
// below.

export function kindsForCategoryV4(
  closed: boolean,
  // P4d-3: offer "Move to Sub-project" ONLY on a project that HAS sub-projects
  // (a destination to move to). False on every flat project → the kind is absent
  // and the pencil menu is byte-identical to pre-P4d-3. The caller passes
  // subProjectOptions.length > 0.
  hasSubProjects: boolean = false,
  // W3 CATEGORY-CASCADE-CLOSE (2026-10-07): when the Category still has OPEN Items
  // (all targetable + their open Actions all targetable + no judge-dup the cascade
  // wouldn't close — the caller checks, two-layer close-gate parity), the greyed
  // dead-end "Close Category" is REPLACED by the thread_close_cascade pseudo ("Close
  // Category & its Items & Actions"), mirroring the Item-tier work_item_close_cascade.
  // Default false → a Category with no open children keeps the plain thread_close
  // (back-compat; the pencil menu is byte-identical).
  cascadeCloseAvailable: boolean = false,
  // D4 (2026-10-16): a preset (template-seeded, origin='seed') Category is a
  // fixed structural slot — its template order IS the numbering index — so a
  // recipient must not restructure it. On a preset Category the restructure ops
  // (rename, merge, move-to-phase, move-to-subproject) are gated OFF; a
  // recipient-added (origin='operator') Category keeps them. The caller passes
  // `thread.preset`.
  preset: boolean = false,
): string[] {
  const closeKind = cascadeCloseAvailable ? "thread_close_cascade" : "thread_close";
  const lifecycle = closed ? ["thread_reopen"] : [closeKind];
  // #38: open Categories only — you don't group a closed Category.
  const grouping = closed ? [] : ["subcategory_add"];
  // D4 (2026-10-16): rename + merge are restructure ops — recipient-added
  // Categories only. A preset Category's name/identity is template-owned.
  const restructure = preset ? [] : ["thread_rename", "thread_merge"];
  // #37: re-band a Category into a phase — offered on OPEN and CLOSED alike
  // (dogfood 2026-06-18: a closed Category still renders in its band for the
  // 2-week retention window, so the recipient must be able to file it under the
  // right phase). Pure attribute move; no lifecycle effect. D4: gated off a
  // preset Category (moving a template slot off its band).
  const phasing = preset ? [] : ["category_move_to_phase"];
  // P4d-3: file a Category under a different sub-project — OPEN only (unlike
  // phase: the move re-numbers the Category + subtree, so it is restructuring,
  // not a retention-window band tidy). Gated on the project having sub-projects.
  // D4: and off a preset Category (it renumbers the template slot + subtree).
  const subProjecting = !closed && hasSubProjects && !preset ? ["category_move_to_subproject"] : [];
  // Operator order 2026-06-12: lifecycle → restructure (rename, merge, phase) →
  // create (sub-category, item) → notes.
  return [
    ...lifecycle,
    ...restructure,
    ...phasing,
    ...subProjecting,
    ...grouping,
    "item_add",
    // D4 (2026-10-16): missing_item + flag_next_week removed from the category
    // menu for ALL Categories — non-additive triage/attention notes (friction
    // without a real-time payoff; carryover is automatic). `comment` remains.
    "comment",
  ];
}

export function kindsForItemV4(
  closed: boolean,
  // Option Y (2026-06-03): an open Item carrying an Item-level
  // closure_candidate ALSO offers "dismiss proposal" (park it open)
  // alongside the always-present "close". Mirrors the Action-level
  // closure_candidate → action_dismiss_closure_candidate visibility.
  hasClosureCandidate: boolean = false,
  // #38: offer "Move to Sub-category" only when the parent Category has ≥1
  // OPEN group (creation never auto-moves; with zero groups the affordance
  // would be a dead end). The caller threads the parent's group list.
  hasOpenSubcategory: boolean = false,
  // #73: offer the reverse "Merge this Item into…" only when ≥1 OTHER open Item
  // exists project-wide (a valid primary to fold into). With none it would be a
  // dead-end picker. The caller threads itemOptions-minus-self.
  hasMergeTarget: boolean = false,
  // Cascade-close (Ray 2026-07-21): when the Item still has OPEN child
  // Actions (all cascade-targetable — the caller checks ai_slugs), the greyed
  // dead-end "Close Item" is REPLACED by the work_item_close_cascade pseudo
  // ("Close Item & its open Actions") — including in the closure_candidate
  // pair, Ray's main flow (approving an agent close proposal on an Item with
  // open children previously dead-ended on the B9 gate).
  cascadeCloseAvailable: boolean = false,
  // P4d-1b: offer "Set associations" only when the project HAS an association
  // axis (≥1 config association). Default false → a flat project's menu is
  // byte-identical (the kind never appears).
  hasAxis: boolean = false,
  // E-5 (Ray 2026-09-16): offer the Item-tier "Set priority" cascade only when
  // the Item has ≥1 OPEN action (a cascade needs targets — else "all 0 actions"
  // would be a dead end). Offered on ALL projects (priority is universal, NOT
  // axis-gated, unlike associations). Default false → the kind never appears
  // unless the caller threads the open-action count.
  hasOpenActions: boolean = false,
  // KIL-POINT (Ray 2026-10-28): offer "Set KIL Point" only on a 558-relevant Item
  // — one with ≥1 OPEN Action tagged `558` (558 is action-keyed) OR a Point already
  // set (so it can be edited/cleared). The Point renders ONLY on the Key Issue Log
  // report, so offering it off the 558 would be a dead control. NOT gated on the PSU
  // `kil_marked` flag despite the "KIL" label. Default false → the kind never appears.
  is558: boolean = false,
): string[] {
  const closeKind = cascadeCloseAvailable
    ? "work_item_close_cascade"
    : "work_item_close";
  // W6 PM-ITEM-NO-ACTION (2026-10-08): "Mark done (keep Item open)" — close the
  // Item's open Actions but PARK the Item open (work_item_dismiss_closure_candidate)
  // for reporting visibility. Offered only when the Item has open, cascade-targetable
  // Actions to close (same gate as the cascade); an Item that is ALREADY action-less
  // (hasClosureCandidate → 0 open Actions) uses the "Keep open" dismiss instead.
  const markCompleted = !closed && cascadeCloseAvailable
    ? ["work_item_mark_completed"]
    : [];
  const lifecycle = closed
    ? ["work_item_reopen"]
    : hasClosureCandidate
      ? [closeKind, "work_item_dismiss_closure_candidate"]
      : [closeKind, ...markCompleted];
  // B2 (2026-06-26): "Acknowledge (not tracked)" (seen-but-untracked → Closed
  // Archive's "Acknowledged / Not Tracked" section, never recorded as completed)
  // + "Not relevant" (reject a mis-placed Item → dropped for good, records a
  // `rejected` signal). Open-only. Operator order 2026-06-27: moved out of the
  // lifecycle cluster to sit AFTER Comment, BEFORE Flag next week (see return).
  const disposition = closed
    ? []
    : ["work_item_acknowledge", "work_item_not_relevant"];
  // #58 (PSU = Actions only): Items no longer offer an independent PSU
  // mark. An Item enters the Project Status Update solely by having a
  // marked Action under it (auto-pulled into the snapshot per Decision
  // 5.3). The item_kil_mark/item_kil_unmark wire kinds stay valid for
  // back-compat (historical corrections must still load) but are never
  // surfaced on the Item pencil.
  // Task 3: rename + merge are structural edits on an open Item, grouped
  // with the move affordance. Hidden on closed Items (nothing to restructure).
  // #38 feedback 2.1: when BOTH move destinations exist (open Item + ≥1 open
  // group), ONE combined "Move to…" button (the item_move pseudo-kind, whose
  // tier select resolves to the real wire kind at submit). With a single
  // destination — closed Item, or no open groups — the plain "Move to
  // Category" renders as before (a one-option submenu is just an extra click).
  const move = !closed && hasOpenSubcategory
    ? ["item_move"]
    : ["item_move_to_category"];
  // #73: reverse merge sits right after the forward "Merge Items" — same
  // operation, opposite direction (fold THIS up into a picked primary).
  const structural = closed
    ? []
    : hasMergeTarget
      ? ["work_item_rename", "work_item_merge", "item_merge_into"]
      : ["work_item_rename", "work_item_merge"];
  // Operator 2026-06-28: "Edit narrative" promoted OUT of the rename group to
  // the FIRST menu position (the most-used edit). Open-only (a closed tombstone
  // isn't edited).
  const editNarrative = closed ? [] : ["work_item_set_narrative"];
  // Slice 2 (2026-09-09): the Item pencil's "Report inclusion" control. The KIL
  // (558) is now ACTION-keyed (Slice 1) and Scale Summary is de-listed, so an
  // Item's own report_tags place nothing — instead this consolidated control's
  // "all actions → Key Issue Log / PSU" checkboxes CASCADE over the Item's open
  // Actions at submit (respects #58 — no independent item mark; the single source
  // of truth is the per-Action tag). Open-only. `item_set_report_tags` stays a
  // valid wire kind (historical corrections still load) but is no longer offered.
  const reportInclusion = closed ? [] : ["item_report_inclusion"];
  // P4d-1b: association placement — axis projects only, open-only.
  const associations = closed || !hasAxis ? [] : ["item_set_associations"];
  // E-5 (Ray 2026-09-16): "Set priority — all N actions" cascade. Open Items with
  // ≥1 open action; ALL projects (priority is universal, not axis-gated).
  const priority = closed || !hasOpenActions ? [] : ["item_set_priority"];
  // KIL-POINT (Ray 2026-10-28): "Set KIL Point" — open 558-relevant Items only
  // (single plain-string UPDATE, not a cascade). The autocomplete + prefill are
  // wired by renderFormControls (spec.pointOptions / spec.current.point).
  const point = closed || !is558 ? [] : ["item_set_point"];
  // Operator order 2026-06-12: lifecycle → restructure (rename, merge, move)
  // → assign owner → create (add action) → notes. Move sits with the other
  // restructure ops; it's the #38 sub-category move + the move-to-Category path.
  return [
    ...editNarrative,
    ...reportInclusion,
    ...associations,
    ...priority,
    ...point,
    ...lifecycle,
    ...structural,
    ...move,
    "responsible_party_assign",
    "action_add",
    // D4 (2026-10-16): missing_item + flag_next_week removed from every menu
    // tier (non-additive notes; carryover is automatic). `comment` remains.
    "comment",
    // Operator order 2026-06-27: disposition verbs (acknowledge / not relevant)
    // sit after Comment.
    ...disposition,
  ];
}

// #38: the affordances on a sub-category header's pencil. Open groups get
// rename + close + delete + notes; a closed group (rendered as a tombstone
// within its retention window) gets only reopen + comment. Worker-gated:
// subcategory_close 409s when the group still has open member Items (the SPA
// also grays it). #74b: subcategory_delete is NOT gated — it ungroups members
// (no close, no tombstone) — so it's always available on an open group.
// E-7a (Ray 2026-09-16): `hasMoveTarget` offers "Move to another Category" only
// when ≥1 OTHER open Category exists to move to (else the picker is a dead end).
// The move is a pure SPA pseudo-kind (subcategory_move) that fans out existing
// wire kinds at submit — see forms.ts collectSubcategoryMove.
export function kindsForSubcategoryV4(
  closed: boolean, hasMoveTarget: boolean = false,
): string[] {
  return closed
    ? ["subcategory_reopen", "comment"]
    : [
        "subcategory_rename",
        ...(hasMoveTarget ? ["subcategory_move"] : []),
        "subcategory_close", "subcategory_delete",
        // D4 (2026-10-16): flag_next_week removed from every menu tier.
        "comment",
      ];
}

export function kindsForActionV4(
  hasOpenQuestion: boolean,
  isInvoicesAggregate: boolean,
  kilMarked: boolean,
  priority?: PriorityStatus | null,
  // Decision 12.8: status drives close vs reopen visibility;
  // closure_candidate presence drives dismiss-candidate visibility.
  status?: "open" | "closed" | null,
  hasClosureCandidate: boolean = false,
  // B1 / #73: offer the reverse "Merge this Action into…" only when ≥1 OTHER
  // open Action exists project-wide (a valid primary to fold into). With none it
  // would be a dead-end picker. The caller threads actionOptions-minus-self.
  hasMergeTarget: boolean = false,
  // P4d-1b: offer "Set associations" only on an axis project (open Actions).
  // Default false → a flat project's Action menu is byte-identical.
  hasAxis: boolean = false,
): string[] {
  // Same conditional pattern as kindsForItemV4 — show only the
  // relevant toggle direction.
  // B5 (2026-06-23, Ray): "Add open question" removed from the Action menu —
  // a question is just an Action (client: "the question is an action"); the
  // affordance added clutter with no use. Kind kept in the enum for back-compat
  // (historical corrections still load); only the offer is withdrawn.
  //
  // Client 2026-08-12: "Resolve open question" is gone from here too, and its
  // "which question" picker with it. Every question disposition now lives on a
  // per-question ✎ rendered beside the question itself (see the Open Questions
  // block below). The picker was real friction, not a rounding error — 14 of 45
  // live Actions carry more than one question and one carries five, so answering
  // meant opening this menu and re-identifying, in a dropdown, a question that
  // was already on screen.
  const questions: string[] = [];
  const invoiceOps = isInvoicesAggregate
    ? ["invoice_add", "invoice_status_change", "invoice_blocking_note_set"]
    : [];
  // Slice 2 (2026-09-09): ONE consolidated "Report inclusion" control replaces the
  // standalone PSU toggle. It is an SPA-only pseudo-kind whose form carries two
  // checkboxes — Project Status Update (→ action_kil_mark/unmark) + Key Issue Log
  // (558) (→ action_set_report_tags) — decomposed into those wire kinds at submit
  // (forms.ts collectReportInclusionEdits). Open Actions only (a closed tombstone
  // isn't re-placed). Now LIVE: the action-keyed KIL renderer (Slice 1) consumes
  // Action report_tags ∋ 558. `kilMarked` is prefilled via spec.current instead.
  void kilMarked;
  // REPORT-TAG-ONE-CLICK (2026-10-18): the Action's Key Issue Log (558) membership
  // is now the one-click KIL pill at the top of the menu (spec.reportToggle), so the
  // former `action_report_inclusion` sub-form is withdrawn from the Action menu. The
  // kind stays valid (historical corrections still load); only the offer is dropped.
  // (PSU was already Action-tier-absent since B/2026-09-16.)
  const reportInclusion: string[] = [];

  // Decision 3A (close-out 2026-05-25): priority-aware metadata
  // edits. The per-priority required-fields map mirrors the
  // synthesizer prompt rules (see synthesizer.md priority guard) +
  // Pydantic constraints on Action. Active actions have no reason /
  // impact / off_hold_condition; On Hold has no due. Hiding the
  // irrelevant kinds keeps the popover scoped to what the client
  // can usefully edit at the current priority.
  const metaByPriority: Record<string, readonly string[]> = {
    "Active": ["action_set_due"],
    "Critical": [
      "action_set_reason", "action_set_impact", "action_set_due",
    ],
    "Delay Potential": [
      "action_set_reason", "action_set_impact", "action_set_due",
    ],
    "On Hold": ["action_set_reason", "action_set_off_hold_condition"],
  };
  // Default to "all four" when priority is unknown/missing — the
  // pre-Decision-3A behavior. Defensive against missing payload data.
  const metaFields = priority
    ? metaByPriority[priority] ?? [
        "action_set_reason", "action_set_impact",
        "action_set_off_hold_condition", "action_set_due",
      ]
    : [
        "action_set_reason", "action_set_impact",
        "action_set_off_hold_condition", "action_set_due",
      ];
  // Round-4 #2 (2026-06-12): ≥2 per-field edit buttons flatten into ONE
  // "Edit details" pseudo-kind (decomposed back into the per-field wire
  // kinds at submit — forms.ts collectActionDetailEdits). A single edit
  // field (Active → due) keeps its direct button: no one-option nesting,
  // the same rule as the combined Move.
  const metaKinds = metaFields.length > 1 ? ["action_edit_details"] : metaFields;

  // Decision 12.8: lifecycle kinds.
  // - Open Action: always show action_close. If a closure_candidate
  //   exists, ALSO show action_dismiss_closure_candidate.
  // - Closed Action: show action_reopen.
  const lifecycle: string[] = [];
  if (status === "closed") {
    lifecycle.push("action_reopen");
  } else {
    lifecycle.push("action_close");
    if (hasClosureCandidate) {
      lifecycle.push("action_dismiss_closure_candidate");
    }
  }
  // B2 (2026-06-26): "Acknowledge (not tracked)" (seen-but-untracked → Closed
  // Archive, never completed) + "Not relevant" (reject a mis-placed Action →
  // dropped for good, records a `rejected` signal). Open-only. Operator order
  // 2026-06-27: moved out of the lifecycle cluster to sit AFTER Comment, BEFORE
  // Flag next week (see return).
  const disposition = status === "closed"
    ? []
    : ["action_acknowledge", "action_not_relevant"];
  // Open improvement #3 / B6 / B8: rename + merge + move-to-Item are open-only
  // (a closed Action is a retention tombstone). Operator order 2026-06-12 wants
  // rename+merge grouped near the top (with the Category/Item restructure
  // cluster) and move-to-Item lower, after the metadata edits — so they're
  // split into two groups rather than one `structural`.
  // B1 / #73: the reverse merge sits right after the forward "Merge Actions" —
  // same operation, opposite direction (fold THIS up into a picked primary).
  // Mirrors the Item tier's rename → merge → merge_into order.
  const renameMerge = status === "closed"
    ? []
    : hasMergeTarget
      ? ["action_rename", "action_merge", "action_merge_into"]
      : ["action_rename", "action_merge"];
  // Operator 2026-06-28: "Edit narrative" promoted OUT of the rename group to
  // the FIRST menu position (it's the most-used edit). Open-only — a closed
  // tombstone isn't edited.
  const editNarrative = status === "closed" ? [] : ["action_set_narrative"];
  // P4d-1b: association placement — axis projects only, open Actions only.
  const associations = status === "closed" || !hasAxis ? [] : ["action_set_associations"];
  // #76: promote-to-Item sits with move-to-Item (both relocate the Action).
  // Open-only. Offered on every open Action; Ray expects to use it rarely (on
  // agent-assumed rows) — gating to an is_new/assumed signal is a future
  // refinement (this fn doesn't receive one).
  const moveToItem = status === "closed"
    ? []
    : ["action_move_to_item", "action_to_item"];

  // Operator order 2026-06-12: lifecycle → rename, merge → Mark PSU → change
  // priority → edit details → move to Item → questions → (invoices, on the
  // aggregate Action) → notes.
  return [
    ...editNarrative,
    ...associations,
    ...lifecycle,
    ...renameMerge,
    ...reportInclusion,
    "action_priority_change",
    ...metaKinds,
    ...moveToItem,
    ...questions,
    ...invoiceOps,
    "comment",
    // Operator order 2026-06-27: disposition verbs (acknowledge / not relevant)
    // sit after Comment. (D4 2026-10-16: flag_next_week removed from all menus.)
    ...disposition,
  ];
}

export function kindsForNeedsInputV4(): string[] {
  // #60 PR4-v2 (2026-06-11 review): the row's placement surface is TWO verbs —
  // "Place as New" (a tier select; an "already complete" checkbox makes it
  // born-closed) and "Merge Into Existing" (fold into an existing entity) —
  // plus the dispositions. Supersedes the four-button A³/A² set (3 Place +
  // File). Place-as-New is a pseudo-kind resolved by tier at submit.
  return [
    "place_new", "merge_into_existing",
    // D4 (2026-10-16): flag_next_week removed from every menu tier.
    "informational_acknowledge", "not_relevant", "comment",
  ];
}

// ===========================================================================
// v4 render path — Category → Item → Action redesign.
//
// Entry point is `renderBriefingV4`, dispatched from `renderBriefingViewer`
// when payload_schema_version === 4. Parallel to the v3 path; doesn't
// reuse v3 helpers because v4's shape (Unfiled top-level, explicit
// path numbers, priority enum, Open Questions sub-section) is too
// different for additive overlays. Plan doc:
// ~/.claude/plans/category-item-action-redesign-2026-05-22.md.
//
// Pencil affordances are placeholder click targets in this PR — the
// actual new correction kinds (action_priority_change,
// action_open_question_add, item_kil_mark, place_from_unfiled, etc.)
// land in Tasks #8 (agent) + #9 (SPA forms). v3 form scaffolding
// (thread_retag, missing_item, comment, flag_next_week) IS wired
// where the encoding is unchanged.
// ===========================================================================

// Exported for unit testing — kept const, mutation by callers
// would corrupt the priority sort order so freeze defensively.
export const PRIORITY_ORDER: readonly PriorityStatus[] = Object.freeze([
  "Critical", "Delay Potential", "Active", "On Hold", "Informational",
]);

/** Map a priority_status enum value to its CSS modifier class. Falls
 * back to "active" for missing/invalid values so the renderer never
 * emits a class-less row (which would leave the left-edge color
 * unset). Exported for unit test coverage. */
export function priorityClass(p: PriorityStatus | undefined | null): string {
  switch (p) {
    case "Critical": return "critical";
    case "Delay Potential": return "delay";
    case "On Hold": return "hold";
    case "Informational": return "informational";
    default: return "active";
  }
}

/** The worst (highest-severity) priority class among a set of OPEN actions, or null
 *  when there are none — the lens-group card's left-edge colour. Shared by
 *  `worstItemPriority` and the F-FLAT KIL card so the priority rules live in one
 *  tested place (ultra #520 r3). */
export function worstActionPriority(actions: AnyItem[]): string | null {
  let bestIdx = Infinity;
  for (const a of actions) {
    if ((a.status ?? "open") === "closed") continue;
    const idx = (PRIORITY_ORDER as readonly string[]).indexOf(a.priority_status ?? "Active");
    if (idx >= 0 && idx < bestIdx) bestIdx = idx;
  }
  return bestIdx === Infinity ? null : priorityClass(PRIORITY_ORDER[bestIdx]!);
}

// #59 (client feedback 2026-06-09): the "Due …" meta bit for an Action.
// Returns `Due <date>` when a due is set, `Due date: TBD` when an OPEN Action
// has none (a missing date stays visible and prompts a look — a blank field
// gets ignored), or null when no Due bit should show. On Hold + Informational
// carry no due by contract; a closed Action shows `Closed …` elsewhere, so no
// TBD nudge on a tombstone. Shared by the briefing tree (renderAction) and the
// PSU (kil_marks.renderActionRow) so the two never drift.
export function dueMetaBit(
  priority: string | null | undefined,
  due: string | null | undefined,
  status: string | null | undefined,
): string | null {
  if (priority === "On Hold" || priority === "Informational") return null;
  if (due) return `Due ${due}`;
  if (status === "closed") return null;
  return "Due date: TBD";
}

// A² (2026-06-03): NEW-this-period pill. `is_new` is set deterministically
// by the agent (mark_new). Globally suppressible via localStorage
// `spa.showNewBadge=false` — the one-flag "easy disable" if the briefing
// reads badge-heavy (no code change / redeploy needed).
function newBadgeEnabled(): boolean {
  try {
    return localStorage.getItem("spa.showNewBadge") !== "false";
  } catch {
    return true;
  }
}

function appendNewPill(parent: HTMLElement, isNew?: boolean | null): void {
  if (!isNew || !newBadgeEnabled()) return;
  parent.appendChild(document.createTextNode(" "));
  const pill = document.createElement("span");
  pill.className = "new-pill";
  pill.textContent = "NEW";
  pill.setAttribute("title", "New this period");
  parent.appendChild(pill);
}

// A1 (2026-07-22): "UPDATED" pill — a carryover Item/Action the narrative-
// merger judged a MERGE this period (real new info), distinct from NEW (brand
// new). Independently suppressible via localStorage `spa.showUpdatedBadge=false`
// — mirrors the NEW badge's one-flag easy-disable (no redeploy).
function updatedBadgeEnabled(): boolean {
  try {
    return localStorage.getItem("spa.showUpdatedBadge") !== "false";
  } catch {
    return true;
  }
}

function appendUpdatedPill(
  parent: HTMLElement, isUpdated?: boolean | null,
): void {
  if (!isUpdated || !updatedBadgeEnabled()) return;
  parent.appendChild(document.createTextNode(" "));
  const pill = document.createElement("span");
  pill.className = "updated-pill";
  pill.textContent = "UPDATED";
  pill.setAttribute("title", "Updated this period");
  parent.appendChild(pill);
}

// A1 (merge optimistic UI): a merge closes its sources, but their context
// folds forward into the target — distinct from a plain close. closed_reason
// "merged into <target>" routes them to a "Recently Merged → into X"
// treatment so the recipient sees the merge took AND where it went.
function isMergedClosure(reason?: string | null): boolean {
  return !!reason && /merged\s+into/i.test(reason);
}
function mergeTargetFromReason(reason?: string | null): string | null {
  const m = (reason ?? "").match(/merged\s+into\s+(.+)$/i);
  return m?.[1]?.trim() ?? null;
}

// #37: a phase band banner. Deliberately a <div>, NOT a heading element —
// mirrors the j2's "bold standalone line, no new heading level" rule (#38
// precedent) so the document outline, TOC, and docx export stay anchored on
// `## <N>. Category`. data-phase drives the CSS accent; the anchor id lets
// the TOC scroll to a band.
function renderPhaseBandHeader(phase: PhaseValue, scope?: string): HTMLElement {
  const band = document.createElement("div");
  band.className = "phase-band-header";
  band.dataset.phase = phase;
  band.id = phaseAnchorId(phase, scope);
  band.textContent = PHASE_LABELS[phase];
  return band;
}

// P4c: the sub-project band banner — the layer ABOVE the phase bands. Same
// rules as the phase banner: a <div>, not a heading (outline / TOC / docx stay
// anchored on the Category h2s); sticky (the 3rd layer: shell → View row →
// THIS → phase band → Category header); anchor id for the TOC.
function renderSubProjectBandHeader(sp: SubProjectView): HTMLElement {
  const band = document.createElement("div");
  band.className = "sub-project-band-header";
  band.dataset.subProject = sp.slug;
  band.id = subProjectAnchorId(sp.slug);
  // Restyle round-2 (Ray 2026-09-08): just the LETTER (A/B/…) + the mixed-case
  // name — the literal word "Sub-project" is dropped (Ray: "just show the letter").
  // The letter shows only for a real lettered sub-project (the trailing "Not in a
  // sub-project" band gets just its name).
  const letter = subProjectLetter(sp.seq);
  if (letter) {
    const l = document.createElement("span");
    l.className = "sub-project-letter";
    l.textContent = letter;
    band.appendChild(l);
  }
  const name = document.createElement("span");
  name.className = "sp-name";
  name.textContent = sp.name;
  band.appendChild(name);
  if (sp.status === "closed") {
    const pill = document.createElement("span");
    pill.className = "status-pill closed";
    pill.textContent = "Closed";
    band.appendChild(document.createTextNode(" "));
    band.appendChild(pill);
  }
  return band;
}

// P4e bubble-summary chrome (the priority dot-histogram, the "N new" dot, AND
// the "N items" / "N categories · N items" reference counts) REMOVED 2026-09-29
// — Ray asked to drop all reference counts and dots ("no added value... I don't
// look at them"). Category + sub-project headers now carry just the name and the
// worst-priority left edge (still fed by summarizeCategory/aggregateBubbles, which
// remain). Restore a header chip from git history if the category-numbering
// redesign wants one.

function renderCollapseToggle(section: HTMLElement, label: string): HTMLElement {
  const toggle = document.createElement("button");
  toggle.type = "button";
  toggle.className = "category-collapse-toggle";
  toggle.innerHTML = CHEVRON_DOWN_SVG;
  toggle.title = label;
  toggle.setAttribute("aria-label", label);
  toggle.addEventListener("click", () => {
    setCategoryCollapsed(section, !section.classList.contains("collapsed"));
  });
  return toggle;
}

// P4e: the sub-project TIER as a collapsible group — the band header (sticky)
// plus its 4 phase bands + Categories inside one <section>, so a whole
// sub-project folds to one line carrying the aggregate bubble. Default
// EXPANDED (the Category tier is what rests collapsed).
function renderSubProjectGroup(
  sp: SubProjectView,
  threads: ThreadView[],
  renderThread: (t: ThreadView) => HTMLElement,
  ctx: { projectSlug: string; lens: LensId; hasAxis?: boolean; hideEmpty: boolean;
         fb?: Map<string, SubmittedFeedbackEvent[]>; runId?: string },
): HTMLElement {
  const group = document.createElement("section");
  group.className = "sub-project-group";
  group.dataset.subProject = sp.slug;
  group.dataset.collapseKey = collapseStorageKey(ctx.projectSlug, `sp:${sp.slug}`, ctx.lens);
  const bubble = aggregateBubbles(threads);
  // Hide-empty: compute the visible phase bands once for the band render below
  // (no double planPhaseBands). The header no longer shows a Category/item count.
  const bands = planPhaseBands(threads, ctx.hideEmpty);
  // Restyle (design §1012): the group card carries its WORST open Action's
  // priority on its left edge (the "Bubble-Up" indicator), same as the Category
  // bubble-up + the Action rows. Reuses the existing worst-priority aggregate.
  group.classList.add(bubble.worst ? `w-${priorityClass(bubble.worst)}` : "w-none");
  const header = renderSubProjectBandHeader(sp);
  header.insertBefore(renderCollapseToggle(group, "Collapse / expand this sub-project"), header.firstChild);
  // Restyle (mockup): the sub-project's effective association UNION as inline
  // chips + a ⚑ when it holds an unplaced Action — the group-header roll-up. Axis
  // projects only.
  if (ctx.hasAxis) {
    const unionSlugs: string[] = [];
    const seen = new Set<string>();
    let hasUnplaced = false;
    for (const t of threads) {
      for (const wi of t.work_items ?? []) {
        if ((wi.status ?? "open") === "closed") continue;
        for (const s of itemAssociationUnion(wi)) if (!seen.has(s)) { seen.add(s); unionSlugs.push(s); }
        if ((wi.action_items ?? []).some(
          (a) => (a.status ?? "open") !== "closed" && !(a.association_tags?.length),
        )) hasUnplaced = true;
      }
    }
    if (unionSlugs.length) {
      const chips = document.createElement("span");
      chips.className = "sp-assoc-chips";
      for (const s of unionSlugs) chips.appendChild(renderAssocTagChip(s));
      header.appendChild(chips);
    }
    if (hasUnplaced) {
      const flag = document.createElement("span");
      flag.className = "lens-group-flag";
      flag.textContent = "⚑";
      flag.title = "Contains an unplaced row";
      header.appendChild(flag);
    }
  }
  group.appendChild(header);
  // R2: the per-sub-project executive summary, rendered as prose directly under
  // the (sticky, single-line) band header and above the phase bands — the
  // outward/board headline for this scope. Absent on a flat project or a scope
  // with no summary this week. Inside the collapsible group so it folds with it.
  // R2: a summary block carrying the per-sub-project executive summary prose AND
  // its edit pencil (the matching affordance to the project-level Executive
  // Summary edit, one altitude down). The block is position:relative so the
  // pencil's absolute `.controls` anchors inside it rather than escaping to the
  // viewport corner (the L3 bug the project-level `.summary-header` also avoids).
  const canEditSummary = !!(ctx.fb && ctx.runId && sp.slug
    && sp.slug !== UNASSIGNED_SUB_PROJECT.slug);
  if ((sp.summary && sp.summary.trim()) || canEditSummary) {
    const block = document.createElement("div");
    block.className = "sub-project-summary-block";
    if (sp.summary && sp.summary.trim()) {
      // F1 (Ray 2026-10-18, call-decided): an INLINE "Executive summary." lead-in
      // (NOT a separate header line — redundant/cluttered) + a collapse toggle that
      // clamps the prose to one line with an ellipsis ("roll it up if you don't want
      // it in this sub-project"). State persists per viewer per sub-project (its own
      // key, distinct from the band collapse); default expanded. The toggle sits at
      // the left — the edit pencil keeps its top-right slot (34px right pad), so the
      // ellipsis never collides with it.
      const summary = document.createElement("p");
      summary.className = "sub-project-summary";
      const collapseKey = collapseStorageKey(
        ctx.projectSlug, `sp-summary:${sp.slug}`, ctx.lens,
      );
      const collapsed = readCollapsed(collapseKey, false);
      summary.classList.toggle("collapsed", collapsed);
      const toggle = document.createElement("button");
      toggle.type = "button";
      toggle.className = "sub-project-summary-toggle";
      toggle.innerHTML = CHEVRON_DOWN_SVG;
      const syncToggle = (c: boolean): void => {
        const lbl = c ? "Expand executive summary" : "Collapse executive summary";
        toggle.title = lbl;
        toggle.setAttribute("aria-label", lbl);
        toggle.setAttribute("aria-expanded", c ? "false" : "true");
      };
      syncToggle(collapsed);
      toggle.addEventListener("click", () => {
        const nowCollapsed = !summary.classList.contains("collapsed");
        summary.classList.toggle("collapsed", nowCollapsed);
        syncToggle(nowCollapsed);
        writeCollapsed(collapseKey, nowCollapsed);
      });
      const label = document.createElement("strong");
      label.className = "sub-project-summary-label";
      label.textContent = "Executive summary.";
      summary.appendChild(label);
      summary.appendChild(document.createTextNode(` ${sp.summary.trim()}`));
      block.appendChild(summary);
      // F1 chevron placement (Ray 2026-10-21): the collapse toggle sits on the RIGHT
      // edge, just left of the edit pencil, matching every other right-side collapse
      // chevron (Item compactness, Category). It's a block sibling (not inside the
      // <p>) and absolutely positioned, so the one-line ellipsis clamp never clips it
      // and it never pushes the "Executive summary." lead-in. Rotation keys off the
      // toggle's own aria-expanded (CSS), so it no longer needs to live in the <p>.
      block.appendChild(toggle);
    }
    if (canEditSummary) {
      // Same `set_executive_summary` kind + targetId `project:<slug>`, scoped by
      // a sub_project_slug in the payload (no new wire kind). Shown even when
      // empty (an "add" form). Not on the "Not in a sub-project" catch-all band.
      const spTargetId = `project:${ctx.projectSlug}`;
      const spEdit = renderFormControls({
        runId: ctx.runId!,
        targetType: "project",
        targetId: spTargetId,
        kinds: ["set_executive_summary"],
        // Only THIS scope's edits (filter the shared project:<slug> target by the
        // payload's sub_project_slug), so bands don't cross-show each other's chips.
        submitted: (ctx.fb!.get(spTargetId) ?? []).filter(
          (e) => isSummaryEditForScope(e, sp.slug),
        ),
        current: { summary: sp.summary ?? "", sub_project_slug: sp.slug },
      });
      spEdit.classList.add("v4-edit-summary-control", "sub-project-summary-edit");
      block.appendChild(spEdit);
    }
    group.appendChild(block);
  }
  const collapsed = readCollapsed(group.dataset.collapseKey, false);
  group.classList.toggle("collapsed", collapsed);
  header.querySelector(".category-collapse-toggle")
    ?.setAttribute("aria-expanded", collapsed ? "false" : "true");
  appendPhaseBands(group, bands, renderThread, sp.slug);
  return group;
}

// P4e: pathway subheads nested under a lens group (structural pathway nesting,
// NOT breadcrumbs — design decision 2026-08-28).
// Restyle: a stable anchor id for a lens group header, so the per-lens left-rail
// TOC can jump to it (sub-project groups already carry subProjectAnchorId; these
// are the Category/Association/Priority/Reports lens groups).
function lensGroupAnchorId(kind: string, key: string): string {
  return `lens-group-${kind}-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
}
function renderSubProjectPathway(sp: SubProjectView): HTMLElement {
  const d = document.createElement("div");
  d.className = "lens-pathway sp";
  const letter = subProjectLetter(sp.seq);
  if (letter) {
    const l = document.createElement("span");
    l.className = "sub-project-letter";
    l.textContent = letter;
    d.appendChild(l);
    d.appendChild(document.createTextNode(" · "));
  }
  d.appendChild(document.createTextNode(sp.name));
  return d;
}
function renderCategoryPathway(t: ThreadView): HTMLElement {
  const d = document.createElement("div");
  d.className = "lens-pathway cat";
  d.textContent = t.display_number ? `${t.display_number}. ${t.name}` : t.name;
  return d;
}

// Restyle 2026-09-07: the left-rail TOC becomes a PER-LENS jump list — it names
// the current lens's top-level groups (Sub-projects / Categories / Associations /
// Priority / Reports) and each entry scroll-jumps to that group's (sticky) header.
// Rebuilt on every render (a lens switch replaces it). Lensed projects only.
const LENS_TOC_TITLES: Record<string, string> = {
  sub_project: "Sub-projects", category: "Categories", association: "Associations",
  priority: "Priority", reports: "Reports", search: "Search",
};
function renderLensTocV4(
  host: HTMLElement,
  ctx: {
    lens: LensId; scrollRoot: HTMLElement;
    subProjects: SubProjectView[];
    categoryEntries: Array<{ name: string; number: string | null }>;
    assocSlugs: string[]; reportSlugs: string[]; reportLabels: Record<string, string>;
  },
): void {
  host.innerHTML = "";
  type Row = { id: string; label: string; lz?: string };
  let rows: Row[] = [];
  if (ctx.lens === "sub_project") {
    rows = ctx.subProjects.map((sp) => ({
      id: subProjectAnchorId(sp.slug), label: sp.name, lz: subProjectLetter(sp.seq) ?? undefined,
    }));
  } else if (ctx.lens === "category") {
    // Restyle: the category TOC shows the standard category NUMBER (in the lz
    // column) like the mockup's "3. Design / Drawings".
    rows = ctx.categoryEntries.map((e) => ({
      id: lensGroupAnchorId("cat", e.name), label: e.name,
      lz: e.number ? `${e.number}.` : undefined,
    }));
  } else if (ctx.lens === "association") {
    rows = ctx.assocSlugs.map((s) => ({ id: lensGroupAnchorId("assoc", s), label: humanizeSlug(s) }));
    rows.push({ id: lensGroupAnchorId("assoc", "needs"), label: "⚑ Needs association" });
  } else if (ctx.lens === "priority") {
    rows = (PRIORITY_ORDER as readonly string[]).map((p) => ({ id: lensGroupAnchorId("pri", p), label: p }));
  } else if (ctx.lens === "reports") {
    rows = ctx.reportSlugs.map((r) => ({ id: lensGroupAnchorId("rep", r), label: ctx.reportLabels[r] ?? humanizeSlug(r) }));
  }
  const title = document.createElement("h4");
  title.className = "lens-toc-title";
  title.textContent = LENS_TOC_TITLES[ctx.lens] ?? "Contents";
  host.appendChild(title);
  const ul = document.createElement("ul");
  ul.className = "lens-toc-list";
  for (const r of rows) {
    const li = document.createElement("li");
    const a = document.createElement("a");
    a.className = "lens-toc-link";
    if (r.lz) {
      const lz = document.createElement("span");
      lz.className = "lens-toc-lz";
      lz.textContent = r.lz;
      a.appendChild(lz);
    }
    a.appendChild(document.createTextNode(r.label));
    a.addEventListener("click", () => {
      const target = document.getElementById(r.id);
      if (!target) return;
      const grp = target.closest<HTMLElement>(".sub-project-group.collapsed");
      if (grp) setCategoryCollapsed(grp, false);
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    li.appendChild(a);
    ul.appendChild(li);
  }
  host.appendChild(ul);
}


// Hide-empty render plan (Ray 2026-09-22 SPA call, "hide categories that don't
// apply"): for one render scope (a flat project or a single sub-project), return
// the phase bands to render in `PHASE_ORDER`, each with its visible Categories.
// When `hideEmpty`, an empty Category (no open Items — `isEmptyCategory`) is
// dropped and a band left with no visible Category is omitted. The Categories are
// NEVER removed from `payload.threads` (only from THIS render), so every
// structural picker still lists them (`collectThreadOptions`) and one
// auto-surfaces the moment it holds an open Item. `hideEmpty` is the caller's
// project-wide blank-guard: false ⇒ show every Category, so a fresh/all-seeded
// project (every Category empty) renders its full structure, not a blank page.
export function planPhaseBands(
  threads: ThreadView[], hideEmpty: boolean,
): Array<{ phase: PhaseValue; threads: ThreadView[] }> {
  const bands: Array<{ phase: PhaseValue; threads: ThreadView[] }> = [];
  for (const phase of PHASE_ORDER) {
    const inPhase = threads.filter((t) => phaseBand(t.phase) === phase);
    const visible = hideEmpty ? inPhase.filter((t) => !isEmptyCategory(t)) : inPhase;
    if (visible.length) bands.push({ phase, threads: visible });
  }
  return bands;
}

// #37 band loop, factored so P4c can run it once per sub-project. Takes the
// precomputed bands (`planPhaseBands`) so a caller that also needs the visible-
// Category count doesn't run the plan twice.
function appendPhaseBands(
  el: HTMLElement,
  bands: ReturnType<typeof planPhaseBands>,
  renderThread: (t: ThreadView) => HTMLElement,
  scope?: string,
): void {
  for (const band of bands) {
    el.appendChild(renderPhaseBandHeader(band.phase, scope));
    for (const thread of band.threads) el.appendChild(renderThread(thread));
  }
}

function renderBriefingV4(
  el: HTMLElement,
  record: BriefingRecord,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  _corr: Map<string, Correction[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  // Restyle: on a lensed project these host the full-width toolbar (above the
  // layout) + the per-lens left-rail TOC. Forwarded to every in-place re-render
  // so a lens switch rebuilds them in place. Absent → the toolbar renders inline.
  chrome?: LensChromeHosts,
): void {
  // Compactness now flows per-work-item via `effectiveCompactness(
  //   globalMode, perItemOverride, synth.compact)`. No briefing-wide
  // class — each work-item renders with `.work-item-compact` only
  // when its effective state says so. This way a per-item override
  // to expanded wins over a global Compact mode, matching v3.

  // Compactness toggle + Add Category pencil share one row: View
  // buttons on the left, pencil on the right, with padding above and
  // below the row. Earlier we split them into two rows for v3 parity
  // on the toggle line; operator reverted that 2026-05-24 because
  // the orphan pencil row looked disconnected.
  const toggleRow = document.createElement("div");
  toggleRow.className = "v4-toggle-row";
  // Design §1022 ("Lensed Briefing"): the "View: Auto/Compact/Expanded" density
  // control is RETIRED — the client doesn't use it; group density now flows only
  // through Expand all / Collapse all (mounted below). The global compactness
  // mode still defaults from localStorage (Auto), and per-item overrides remain,
  // so nothing renders differently by default — only the toolbar control is gone.

  // A² (2026-06-03): "New only" filter — composable with the density
  // toggle (NOT a 4th density mode): density = how much detail, filter =
  // which Items. Shows only Items new this period (or carrying a new
  // Action). Persisted across re-renders; applied as a container class
  // consumed by CSS (instant, no re-render on toggle).
  // Client 2026-08-12 item E — "Show New only" REMOVED (operator's call; Ray
  // asked for it directly after first saying he'd leave it for others). The
  // "new/updated" chip below is the strictly broader filter and stays.
  //
  // The stored key is actively DELETED rather than merely ignored. The class was
  // applied from localStorage independently of the checkbox, so dropping only the
  // chip would leave anyone who had ticked it filtered forever with no control to
  // clear it — the filter would look like missing data. Ray had this surface in
  // front of him on 08-12, so that is a live possibility, not a hypothetical.
  try { localStorage.removeItem("spa.filterNewOnly"); }
  catch { /* private mode — nothing was persisted to begin with */ }

  // Client 2026-08-12 item B — drop any stored popover-section preferences, for
  // the same reason as the filter key above: a value that outlives the control
  // that set it is a surprise waiting to happen. See purgeMenuSectionPrefs.
  purgeMenuSectionPrefs();

  // A1 (2026-07-22): combined "new/updated" filter — shows Items new this
  // period OR carrying real new info this week (a narrative-merger MERGE), so a
  // recipient sees everything that changed in one view. Deliberately a SEPARATE
  // chip from "New only" (which is a stricter subset), NOT a second independent
  // "Updated only": two mutually-exclusive "only" filters would AND together via
  // the container-class CSS and hide everything. When both chips are checked the
  // new-only rule wins (hides non-new even if updated) — no empty set, since the
  // changed predicate is a superset of new. Same instant class/CSS mechanism.
  const filterChangedOnly = (() => {
    try { return localStorage.getItem("spa.filterChangedOnly") === "true"; }
    catch { return false; }
  })();
  if (filterChangedOnly) el.classList.add("filter-changed-only");
  const changedChip = document.createElement("label");
  changedChip.className = "new-filter-chip";
  const changedCb = document.createElement("input");
  changedCb.type = "checkbox";
  changedCb.checked = filterChangedOnly;
  changedCb.addEventListener("change", () => {
    try {
      localStorage.setItem("spa.filterChangedOnly", String(changedCb.checked));
    } catch { /* private mode — filter still applies for this view */ }
    el.classList.toggle("filter-changed-only", changedCb.checked);
  });
  changedChip.appendChild(changedCb);
  changedChip.appendChild(document.createTextNode(" Show new/updated"));
  toggleRow.appendChild(changedChip);

  const categoryAddTargetId = `project:${record.project_slug}`;
  // N4b: the Add-Category sub-project <select> options. Computed here (the
  // toolbar renders above the main `subProjectOptions` const at ~4357) directly
  // from the registry, same {value,label} shape (letter-led) — empty on a flat
  // project, so the form stays name+description there.
  const addCategorySubProjectOptions = sortedSubProjects(
    record.payload.sub_projects,
  ).map((sp) => {
    const letter = subProjectLetter(sp.seq);
    return {
      value: sp.slug,
      label: letter ? `${letter} · ${sp.name}` : sp.name,
    };
  });
  const addCategoryControls = renderFormControls({
    runId: record.run_id,
    targetType: "project",
    targetId: categoryAddTargetId,
    kinds: ["category_add"],
    // Filter to THIS control's kind: the Edit-Summary pencil (R2-3 fast-follow)
    // shares the same `project:<slug>` targetId, so an unfiltered list would
    // cross-show set_executive_summary chips here.
    submitted: (fb.get(categoryAddTargetId) ?? []).filter(
      (e) => e.kind === "category_add",
    ),
    participantOptions,
    threadOptions,
    // N4b (Ray 2026-09-22): the Add-Category form's sub-project <select> reuses
    // the project's sub-project registry (empty on a flat project → the select
    // is not rendered, so the form stays name+description there).
    subProjectOptions: addCategorySubProjectOptions,
  });
  addCategoryControls.classList.add("v4-add-category-control");
  toggleRow.appendChild(addCategoryControls);
  // Restyle: a lensed project mounts the toolbar in the full-width host ABOVE the
  // layout (cleared each render so a lens switch replaces it in place); a flat
  // project keeps it inline in the viewer (byte-identical). The toolbar controls
  // are assembled into `toggleRow` further below via insertBefore either way.
  if (chrome?.toolbarHost) {
    chrome.toolbarHost.innerHTML = "";
    chrome.toolbarHost.appendChild(toggleRow);
  } else {
    el.appendChild(toggleRow);
  }

  // Decision 5 (close-out): Unfiled and Suggested Categories both
  // render BELOW the Category tree, grouped under a single "Needs
  // Your Input" wrapper. Was: Unfiled top with ⚠ alarm, Candidate
  // Threads bottom. Spec is silent on placement; main project status
  // reads first; LLM-uncertainty surfaces grouped together at end.
  // Wrapper appended after the Categories loop further below.

  // Sort Categories by display_number ASC so the render order matches
  // the path-notation hierarchy (1, 2, 3, ...). Synth doesn't guarantee
  // payload order — it tends to emit threads in first-seen / carryover
  // order, which produced visual orderings like [..., 5, 14, 6, ...]
  // when display_number was assigned at upsert time in a different
  // sequence than the current emit. Numeric-aware comparator; entries
  // with null / non-numeric display_number sort to the end.
  // P4c: `compareDisplayNumber` — lettered-aware (A3 < A5 < A10 < B5, then
  // flat numerics, then missing), identical to the old parseInt order for a
  // flat project's all-numeric Categories.
  const sortedThreads = [...(record.payload.threads ?? [])].sort(
    (a, b) => compareDisplayNumber(a.display_number, b.display_number),
  );
  // Hide-empty blank-guard: hide empty Categories only when the project has at
  // least one non-empty Category. A fresh/all-seeded project (every Category
  // empty) would otherwise render blank — there, show the full structure.
  const hideEmptyCategories = sortedThreads.some((t) => !isEmptyCategory(t));
  // Task 3: project-wide open-Item options for the work_item_merge
  // multi-select (Ray picks which sibling Items to fold in). Collected
  // once from the full payload, like the Unfiled place picker below.
  const allItemOptions: ItemOption[] = [];
  // B8 cross-Item action_merge: project-wide open Actions for the merge
  // picker — each carries its full target id + parent Item label so the
  // submit handler can re-parent a cross-Item source before merging.
  const allActionOptions: ActionOption[] = [];
  // #66 D4: Items the dedup judge flagged as suspected duplicates, gathered once
  // here (like allItemOptions) so they can be pulled from the body lenses and
  // rendered under Needs-Your-Input instead.
  const judgeDupRows: Array<{ t: ThreadView; wi: WorkItemView }> = [];
  for (const t of record.payload.threads ?? []) {
    for (const wi of t.work_items ?? []) {
      if (wi.status === "closed") continue;
      if (isJudgeDup(wi)) judgeDupRows.push({ t, wi });
      allItemOptions.push({
        slug: wi.slug,
        name: wi.name,
        display_number: wi.display_number ?? null,
        thread_name: t.name,
        // B3-merge: the parent Category's sub-project, for the Item-merge
        // Sub-project pre-filter.
        sub_project_slug: t.sub_project_slug ?? null,
        // F2: the parent Category slug, for the Sub-project → Category cascade
        // that narrows the Item picker to one Category.
        thread_slug: t.slug,
      });
      const itemLabel =
        `${wi.display_number ? wi.display_number + " " : ""}${wi.name}`;
      for (const a of wi.action_items ?? []) {
        if ((a.status ?? "open") !== "open" || !a.ai_slug) continue;
        allActionOptions.push({
          ai_slug: a.ai_slug,
          text: a.what ?? a.text ?? "",
          display_number: a.display_number ?? null,
          target_id: actionItemKey(t.slug, wi.slug, a.ai_slug),
          work_item_slug: wi.slug,
          item_label: itemLabel,
          // B3-merge: parent Category's sub-project, for the Action-merge
          // Sub-project → Item two-level pre-filter.
          sub_project_slug: t.sub_project_slug ?? null,
        });
      }
    }
  }
  // #63 follow-up: order the move-target Item picker by Item number (was
  // payload order, which looked unsorted — e.g. 2.9 before 2.1).
  allItemOptions.sort((a, b) => compareDisplayNumber(a.display_number, b.display_number));
  // #37: partition the display_number-sorted Categories into the fixed 4
  // phase bands, with an always-present banner above each (delta #1,
  // 2026-06-18: render all 4 bands even when empty — the client wants the
  // phase structure visible at all times; pruning is an explicit SPA close,
  // never an auto-hide. Overrides the v5 §9 activation rule). The inner
  // filter preserves the numeric Category sort within each band; null/legacy
  // phase falls into General. The Needs-Your-Input + Needs-Review tail below
  // renders after the (last) General band, matching today's global-tail spot.
  // P4c: the sub-project tier. Activates ONLY when the payload carries the
  // registry (`sub_projects[]`, stamped by the harness for a sub-projected
  // project); a flat project takes the else-branch = the pre-P4c DOM.
  // R2: merge each sub-project's executive summary (top-level map, keyed by
  // slug) onto its view, so the band renderer can show it. A flat project has no
  // map → summary stays undefined → no change.
  const subProjects = sortedSubProjects(record.payload.sub_projects).map((sp) => ({
    ...sp,
    summary: record.payload.sub_project_summaries?.[sp.slug] ?? sp.summary,
  }));
  // P4c-2: Bubble-Up safe-collapse rides the same gate (sub-projected only).
  // P4e: so does the lens (persisted per project; default = By Sub-project).
  const lens: LensId = subProjects.length ? readLens(record.project_slug, record.payload) : "sub_project";
  const bubbleUp = subProjects.length ? { projectSlug: record.project_slug, lens } : undefined;
  // P4d-1b: the association axis {value,label}, forwarded to every Item/Action
  // pencil's set_associations editor. Empty on a flat project (no axis) → the
  // kind is gated off and the pencil menu stays byte-identical.
  const associationOptions = projectAssociationSlugs(record.payload)
    .map((slug) => ({ value: slug, label: humanizeSlug(slug) }));
  // KIL-POINT (Ray 2026-10-28): the distinct 558 "Points" already set across this
  // project's Items (threaded + unthreaded), fed to every Item's "Set KIL Point"
  // <datalist> so a recipient reuses the exact string ("Scale / KAMM") instead of
  // a typo variant ("Scle") that would fragment the Key Issue Log. Free-text still
  // submits (open vocab); this is autocomplete only. Empty → no datalist.
  const pointOptions = [...new Set(
    [
      ...(record.payload.threads ?? []).flatMap((t) => t.work_items ?? []),
      ...(record.payload.unthreaded?.work_items ?? []),
    ]
      .map((wi) => (wi.point ?? "").trim())
      .filter(Boolean),
  )].sort((a, b) => a.localeCompare(b));
  // P4d-3: the sub-project registry {value,label}, forwarded to each Category's
  // "Move to Sub-project" pencil. Empty on a flat project (no sub_projects) → the
  // kind is gated off in kindsForCategoryV4 and the pencil menu stays byte-identical.
  // B3 (2026-09-15): lead each label with the sub-project letter (matching the
  // band headers) so the move/place/merge/promote filters read "A · Electrical
  // Rooms Relocation", not a bare name. Value stays the slug.
  const subProjectOptions = subProjects.map((sp) => {
    const letter = subProjectLetter(sp.seq);
    return {
      value: sp.slug,
      label: letter ? `${letter} · ${sp.name}` : sp.name,
    };
  });
  // B3: known sub-project slugs, to resolve an entity's raw sub_project_slug to a
  // filter bucket (real slug, or "(No sub-project)" for null/unknown).
  const spKnown = new Set(subProjectOptions.map((s) => s.value));
  const renderThread = (thread: ThreadView): HTMLElement => renderCategoryV4(
    record.run_id, thread, fb, participantOptions, threadOptions,
    allItemOptions, allActionOptions, bubbleUp, associationOptions,
    subProjectOptions, pointOptions,
  );
  // P4e: the left-rail TOC is a By-Sub-project artifact (its anchors are the
  // Category sections + sub-project bands); hide it under the other lenses.
  // Body-level so app.ts needs no wiring; cleared on every render (flat too).
  document.body.classList.toggle("lens-alt", subProjects.length > 0 && lens !== "sub_project");
  if (subProjects.length) {
    // Container class → the CSS sticky stack grows a layer (the var is 0px
    // otherwise, so a flat project's offsets are untouched).
    el.classList.add("has-sub-projects");
    // ── Restyle (2026-09-07 "Lensed Briefing") grouped toolbar. The controls are
    // assembled into labelled groups (Association · Lens) split by hairline
    // dividers, then Expand/Collapse, the New/updated chip (already in the row),
    // and a right-aligned ⌕ Advanced-search trigger. Each fragment is inserted
    // before `changedChip` (the row's left-most flat-baseline control) so the
    // order reads left→right; all of it is inside the sub-projects gate, so a flat
    // project keeps its plain [changed-chip · pencil] row untouched.
    const searchFocus = activeSearchFor(record.project_slug);
    const assocSlugs = projectAssociationSlugs(record.payload);
    const selectedAssoc = new Set(
      [...readAssocFilter(record.project_slug)].filter((s) => assocSlugs.includes(s)),
    );
    // Association group — colour tower chips (dot + name + ✓ when on), left-most.
    if (assocSlugs.length) {
      const grp = document.createElement("span");
      grp.className = "tb-grp";
      const lbl = document.createElement("span");
      lbl.className = "tb-lbl";
      lbl.textContent = "Association";
      grp.appendChild(lbl);
      const towers = document.createElement("span");
      towers.className = "towers";
      for (const slug of assocSlugs) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "tchip";
        b.setAttribute("data-a", slug);
        const dot = document.createElement("span");
        dot.className = "dot";
        b.appendChild(dot);
        b.appendChild(document.createTextNode(humanizeSlug(slug)));
        const ck = document.createElement("span");
        ck.className = "ck";
        ck.textContent = "✓";
        b.appendChild(ck);
        const on = selectedAssoc.has(slug);
        b.classList.toggle("active", on);
        b.setAttribute("aria-pressed", String(on));
        b.addEventListener("click", () => {
          const next = new Set(selectedAssoc);
          if (next.has(slug)) next.delete(slug); else next.add(slug);
          writeAssocFilter(record.project_slug, next);
          renderBriefingViewer(el, record, fb, chrome);
        });
        towers.appendChild(b);
      }
      grp.appendChild(towers);
      toggleRow.insertBefore(grp, changedChip);
      const div = document.createElement("span");
      div.className = "tb-div";
      toggleRow.insertBefore(div, changedChip);
    }
    // Lens group — a single segmented control across the available lenses. Search
    // is NOT here (it is the ⌕ trigger below). Clicking a segment leaves any
    // active search focus and switches/returns to that lens.
    const lensGrp = document.createElement("span");
    lensGrp.className = "tb-grp";
    const lensLbl = document.createElement("span");
    lensLbl.className = "tb-lbl";
    lensLbl.textContent = "Lens";
    lensGrp.appendChild(lensLbl);
    // Restyle round-2 (Ray 2026-09-08): the lens picker is a DROPDOWN, not a
    // segmented button row — Sub-project is the default/base view, so the other
    // lenses shouldn't hold permanent top-row real estate. The Association filter
    // chips (above) stay. The <select> keeps the current lens selected underneath
    // a search focus (the results are an overlay, not a 6th lens).
    const sel = document.createElement("select");
    sel.className = "lens-select";
    sel.setAttribute("aria-label", "Lens");
    for (const id of availableLenses(record.payload)) {
      const o = document.createElement("option");
      o.value = id;
      o.textContent = LENS_LABELS[id];
      if (id === lens) o.selected = true;
      sel.appendChild(o);
    }
    sel.addEventListener("change", () => {
      const id = sel.value as LensId;
      const wasSearching = !!activeSearchFor(record.project_slug);
      clearActiveSearch();
      if (id === lens) {
        if (wasSearching) renderBriefingViewer(el, record, fb, chrome);
        return;
      }
      writeLens(record.project_slug, id);
      renderBriefingViewer(el, record, fb, chrome);
    });
    lensGrp.appendChild(sel);
    toggleRow.insertBefore(lensGrp, changedChip);
    const lensDiv = document.createElement("span");
    lensDiv.className = "tb-div";
    toggleRow.insertBefore(lensDiv, changedChip);

    const assocActive = selectedAssoc.size > 0;
    const itemMatchesAssoc = (wi: WorkItemView): boolean =>
      !assocActive || itemAssociationUnion(wi).some((s) => selectedAssoc.has(s));
    const threadMatchesAssoc = (t: ThreadView): boolean =>
      !assocActive || (t.work_items ?? []).some(itemMatchesAssoc);
    // Expand / Collapse (⊞ / ⊟) — every lens now renders collapsible group cards
    // (sub-project groups, bubble-up Categories, and the lens-group cards), so the
    // control shows on all of them; hidden only under a search focus (flat results).
    if (!searchFocus) {
      const xc = document.createElement("span");
      xc.className = "xc";
      for (const [label, icon, collapsed] of
        [["Expand all", "⊞", false], ["Collapse all", "⊟", true]] as const) {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = `${icon} ${label}`;
        b.addEventListener("click", () => {
          el.querySelectorAll<HTMLElement>(
            ".thread.thread-v4.bubble-up, .sub-project-group, .lens-group-card",
          ).forEach((s) => setCategoryCollapsed(s, collapsed));
        });
        xc.appendChild(b);
      }
      toggleRow.insertBefore(xc, changedChip);
    }
    // ⌕ Advanced search — right-aligned (margin-left:auto). Opens a facet popover
    // (free text + Association / Category / Priority / Report). Its results are a
    // transient focus mode (activeSearch), rendered flat below in place of the lens.
    const catFacetNames = groupThreadsByCategoryName(sortedThreads).map((g) => g.name);
    // Scale Summary de-listed (2026-09-09) → only the Key Issue Log (slug "558")
    // is an offered report facet.
    const reportFacetSlugs = ["558"].filter((r) =>
      sortedThreads.some((t) => (t.work_items ?? []).some(
        (wi) => !isJudgeDup(wi) && itemHasReportAction(wi, r))));
    const REPORT_FACET_LABELS: Record<string, string> = { "558": "Key Issue Log" };
    // The reports-LENS TOC rows: mirror the lens's rendered groups (Key Issue Log
    // + Project Status Update + a trailing "Not in a report"), ACTION-keyed. This
    // is DISTINCT from `reportFacetSlugs` above — the advanced-search Report facet
    // stays report_tags-only (no PSU). Only consumed when the reports lens is active.
    const REPORT_LENS_LABELS: Record<string, string> = {
      "558": "Key Issue Log", psu: "Project Status Update", none: "Not in a report",
    };
    // Exclude judge-dups so the TOC rows agree with the lens body (openItemsInLens)
    // — a judge-dup-only facet would render a TOC link to an empty group / dead anchor.
    const reportLensTocSlugs: string[] = [];
    // The 558 TOC entry mirrors the KIL card's render condition (ultra #520 r3): an
    // Item that passes the association filter with an OPEN 558 action that itself
    // matches the active chip — else a selected chip can hide the card while the rail
    // still links to its (now-absent) anchor.
    const kilHasShownAction = sortedThreads.some((t) => (t.work_items ?? []).some((wi) =>
      (wi.status ?? "open") !== "closed" && !isJudgeDup(wi) && itemMatchesAssoc(wi)
      && (wi.action_items ?? []).some((aa) =>
        (aa.status ?? "open") !== "closed" && (aa.report_tags ?? []).includes("558")
        && (!assocActive || (aa.association_tags ?? []).some((s) => selectedAssoc.has(s))))));
    if (kilHasShownAction) reportLensTocSlugs.push("558");
    if (sortedThreads.some((t) => (t.work_items ?? []).some((wi) => !isJudgeDup(wi) && itemHasPsuMark(wi)))) reportLensTocSlugs.push("psu");
    if (sortedThreads.some((t) => (t.work_items ?? []).some(
      (wi) => (wi.status ?? "open") !== "closed" && !isJudgeDup(wi) && !itemHasReportAction(wi, "558") && !itemHasPsuMark(wi),
    ))) reportLensTocSlugs.push("none");
    const advBtn = document.createElement("button");
    advBtn.type = "button";
    advBtn.className = "advtrigger";
    advBtn.textContent = "⌕ Advanced search";
    if (searchFocus) advBtn.classList.add("open");
    advBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (document.querySelector(".adv-pop.open")) {
        document.querySelectorAll(".adv-pop").forEach((p) => p.remove());
        advBtn.classList.toggle("open", !!searchFocus);
        return;
      }
      const pop = buildAdvSearchPopover();
      document.body.appendChild(pop);
      const r = advBtn.getBoundingClientRect();
      pop.style.top = `${r.bottom + 5}px`;
      pop.style.left = `${Math.min(r.left, window.innerWidth - 302)}px`;
      pop.classList.add("open");
      advBtn.classList.add("open");
      const onDoc = (ev: MouseEvent): void => {
        if (!pop.contains(ev.target as Node) && ev.target !== advBtn) {
          pop.remove();
          advBtn.classList.toggle("open", !!activeSearchFor(record.project_slug));
          document.removeEventListener("mousedown", onDoc, true);
        }
      };
      setTimeout(() => document.addEventListener("mousedown", onDoc, true), 0);
    });
    // Right-aligned (its own margin-left:auto), after the New/updated chip and
    // before the Add-category pencil — matches the mockup's right cluster.
    toggleRow.insertBefore(advBtn, toggleRow.querySelector(".v4-add-category-control"));
    // The popover DOM — pre-filled from the current search focus. "Search" sets
    // activeSearch + re-renders (flat results overlay); "Clear" drops the focus.
    const buildAdvSearchPopover = (): HTMLElement => {
      const cur = activeSearchFor(record.project_slug);
      const pop = document.createElement("div");
      pop.className = "adv-pop";
      const h = document.createElement("h5");
      h.textContent = "Search across all sub-projects";
      pop.appendChild(h);
      const txt = document.createElement("input");
      txt.type = "search";
      txt.className = "adv-txt";
      txt.placeholder = "Search item / action text…";
      txt.value = cur?.text ?? "";
      pop.appendChild(txt);
      const mkRow = (label: string, opts: Array<{ value: string; label: string }>, val: string): HTMLSelectElement => {
        const row = document.createElement("div");
        row.className = "adv-row";
        const l = document.createElement("label");
        l.textContent = label;
        row.appendChild(l);
        const sel = document.createElement("select");
        const any = document.createElement("option");
        any.value = ""; any.textContent = "Any";
        sel.appendChild(any);
        for (const o of opts) {
          const opt = document.createElement("option");
          opt.value = o.value; opt.textContent = o.label;
          if (o.value === val) opt.selected = true;
          sel.appendChild(opt);
        }
        row.appendChild(sel);
        pop.appendChild(row);
        return sel;
      };
      const assocSel = mkRow("Association",
        assocSlugs.map((s) => ({ value: s, label: humanizeSlug(s) })), cur?.assoc ?? "");
      const catSel = mkRow("Category",
        catFacetNames.map((n) => ({ value: n, label: n })), cur?.cat ?? "");
      const priSel = mkRow("Priority",
        (PRIORITY_ORDER as readonly string[]).map((p) => ({ value: p, label: p })), cur?.pri ?? "");
      const repSel = reportFacetSlugs.length
        ? mkRow("Report",
            reportFacetSlugs.map((r) => ({ value: r, label: REPORT_FACET_LABELS[r] ?? humanizeSlug(r) })),
            cur?.rep ?? "")
        : null;
      const btns = document.createElement("div");
      btns.className = "adv-btns";
      const go = document.createElement("button");
      go.type = "button"; go.className = "adv-go"; go.textContent = "Search";
      const clr = document.createElement("button");
      clr.type = "button"; clr.className = "adv-clr"; clr.textContent = "Clear";
      btns.appendChild(go);
      btns.appendChild(clr);
      pop.appendChild(btns);
      const submit = (): void => {
        activeSearch = {
          projectSlug: record.project_slug,
          text: txt.value, assoc: assocSel.value, cat: catSel.value,
          pri: priSel.value, rep: repSel?.value ?? "",
        };
        pop.remove();
        renderBriefingViewer(el, record, fb, chrome);
      };
      go.addEventListener("click", submit);
      txt.addEventListener("keydown", (ev) => { if (ev.key === "Enter") submit(); });
      clr.addEventListener("click", () => {
        clearActiveSearch();
        pop.remove();
        renderBriefingViewer(el, record, fb, chrome);
      });
      return pop;
    };
    // (The mockup's association legend row was a design EXPLAINER, not a control —
    // operator 2026-09-07: don't build it. The chip states are self-evident on the
    // rows + the ghost chip's own popover; a second "Association:" row read as a
    // duplicate menu.)
    // Restyle: the per-lens left-rail TOC (names the current lens's groups + jumps
    // to them). Rebuilt every render so a lens switch re-lists the rail.
    if (chrome?.lensTocHost) {
      renderLensTocV4(chrome.lensTocHost, {
        lens, scrollRoot: el, subProjects,
        categoryEntries: groupThreadsByCategoryName(
          hideEmptyCategories ? sortedThreads.filter((t) => !isEmptyCategory(t)) : sortedThreads,
        ).map((g) => {
          const m = (g.members.find((t) => t.display_number)?.display_number ?? "")
            .match(/^[A-Za-z]*(\d+)/);
          return { name: g.name, number: m ? m[1]! : null };
        }),
        assocSlugs, reportSlugs: reportLensTocSlugs, reportLabels: REPORT_LENS_LABELS,
      });
    }
    const lensCtx = { projectSlug: record.project_slug, lens, hasAxis: associationOptions.length > 0, hideEmpty: hideEmptyCategories, fb, runId: record.run_id };
    const threadsOf = (sp: SubProjectView): ThreadView[] => sp === UNASSIGNED_SUB_PROJECT
      ? sortedThreads.filter((t) => isUnassignedThread(t, subProjects))
      : sortedThreads.filter((t) => (t.sub_project_slug ?? null) === sp.slug);
    const spWithUnassigned = [...subProjects, UNASSIGNED_SUB_PROJECT];
    const spOf = (t: ThreadView): SubProjectView =>
      subProjects.find((sp) => sp.slug === t.sub_project_slug) ?? UNASSIGNED_SUB_PROJECT;
    const renderItemInLens = (
      t: ThreadView, wi: WorkItemView, lensOpts: Parameters<typeof renderItemV4>[9],
    ): HTMLElement => renderItemV4(
      record.run_id, t.slug, wi, fb, participantOptions, threadOptions,
      allItemOptions, allActionOptions, t.subcategories ?? [], lensOpts, associationOptions,
      // B3: sub-project registry + this Item's current sub-project (its parent
      // Category's), resolved to a bucket so an UNASSIGNED Item defaults the move
      // filter to "(No sub-project)" (not the first sub-project). Works in every lens.
      subProjectOptions,
      resolveSubProjectBucket(t.sub_project_slug, spKnown),
      pointOptions, // KIL-POINT: "Set KIL Point" datalist source
    );
    // P4d: open Items in a Category, honoring the Association filter (used by the
    // card lenses). The full-card lenses (sub_project/category) filter at the
    // THREAD tier via threadMatchesAssoc instead, since they render whole
    // Categories — a category shows when any of its Items match.
    const openItemsInLens = (t: ThreadView): WorkItemView[] =>
      (t.work_items ?? []).filter(
        // #66 D4: judge-flagged dups relocate to Needs-Your-Input (all lenses).
        (wi) => (wi.status ?? "open") !== "closed" && !isJudgeDup(wi)
          && itemMatchesAssoc(wi),
      );
    // P4d: pathway-nest a flat list of {sub-project, Category, Item} rows,
    // emitting each pathway header once on change (mirrors the Priority lens).
    const appendPathwayItemRows = (
      rows: Array<{ sp: SubProjectView; t: ThreadView; wi: WorkItemView }>,
      target: HTMLElement = el,
    ): void => {
      let lastSp: SubProjectView | null = null;
      let lastThread: ThreadView | null = null;
      for (const row of rows) {
        if (row.sp !== lastSp) { target.appendChild(renderSubProjectPathway(row.sp)); lastSp = row.sp; lastThread = null; }
        if (row.t !== lastThread) { target.appendChild(renderCategoryPathway(row.t)); lastThread = row.t; }
        target.appendChild(renderItemInLens(row.t, row.wi, { hideClosedActions: true }));
      }
    };
    // Restyle (design §1012): each lens's TOP-LEVEL group renders as a collapsible
    // card carrying its worst open Action's priority on its left edge + a ⚑ when it
    // holds an unplaced row — the same "Bubble-Up" treatment as the sub-project
    // groups. Returns {section, body}; callers render rows into `body`. `worst` is a
    // priorityClass string (or null → neutral edge).
    const openLensGroupCard = (
      name: string, count: number, anchorId: string,
      worst: string | null, hasUnplaced: boolean, collapseSlug: string,
    ): { section: HTMLElement; body: HTMLElement } => {
      const section = document.createElement("section");
      section.className = `lens-group-card w-${worst ?? "none"}`;
      const collapseKey = collapseStorageKey(record.project_slug, `lg:${collapseSlug}`, lens);
      section.dataset.collapseKey = collapseKey;
      const header = document.createElement("div");
      header.className = "lens-group-header";
      header.id = anchorId;
      header.appendChild(renderCollapseToggle(section, "Collapse / expand this group"));
      const nameEl = document.createElement("span");
      nameEl.className = "lens-group-name";
      nameEl.textContent = name;
      header.appendChild(nameEl);
      if (hasUnplaced) {
        const flag = document.createElement("span");
        flag.className = "lens-group-flag";
        flag.textContent = "⚑";
        flag.title = "Contains an unplaced row";
        header.appendChild(flag);
      }
      const c = document.createElement("span");
      c.className = "lens-group-count";
      c.textContent = String(count);
      header.appendChild(c);
      section.appendChild(header);
      const body = document.createElement("div");
      body.className = "lens-group-body";
      section.appendChild(body);
      const collapsed = readCollapsed(collapseKey, false);
      section.classList.toggle("collapsed", collapsed);
      header.querySelector(".category-collapse-toggle")
        ?.setAttribute("aria-expanded", collapsed ? "false" : "true");
      return { section, body };
    };
    // Worst open-Action priority across a set of Items (for the lens-group edge).
    const worstItemPriority = (items: WorkItemView[]): string | null =>
      worstActionPriority(items.flatMap((wi) => wi.action_items ?? []));
    const itemsUnplaced = (items: WorkItemView[]): boolean =>
      associationOptions.length > 0 && items.some(
        (wi) => (wi.action_items ?? []).some(
          (a) => (a.status ?? "open") !== "closed" && !(a.association_tags?.length),
        ),
      );

    if (searchFocus) {
      // Advanced-search FOCUS (transient) — flat cross-cutting results in place of
      // the current lens, driven by the activeSearch criteria. A "← back to lens"
      // clears the focus. The toolbar Association filter still applies on top. The
      // matching mirrors the former Search lens (itemSearchHits + facet filters).
      const note = document.createElement("div");
      note.className = "adv-active-note";
      const parts: string[] = [];
      if (searchFocus.text.trim()) parts.push(`“${searchFocus.text.trim()}”`);
      if (searchFocus.assoc) parts.push(humanizeSlug(searchFocus.assoc));
      if (searchFocus.cat) parts.push(searchFocus.cat);
      if (searchFocus.pri) parts.push(searchFocus.pri);
      if (searchFocus.rep) parts.push(searchFocus.rep === "558" ? "Key Issue Log" : "Scale Summary");
      const lead = document.createElement("span");
      lead.textContent = parts.length
        ? `Search — ${parts.join(" · ")}, flat across all sub-projects.`
        : "Search — flat across all sub-projects.";
      note.appendChild(lead);
      const back = document.createElement("span");
      back.className = "adv-back";
      back.textContent = "← back to lens";
      back.addEventListener("click", () => {
        clearActiveSearch();
        renderBriefingViewer(el, record, fb, chrome);
      });
      note.appendChild(back);
      el.appendChild(note);

      const results = document.createElement("div");
      results.className = "search-results";
      el.appendChild(results);
      const anyCriteria = activeSearchHasCriteria(searchFocus);
      const catName = searchFocus.cat.trim().toLowerCase();
      let n = 0;
      if (anyCriteria) {
        for (const sp of spWithUnassigned) {
          for (const t of threadsOf(sp)) {
            if (catName && t.name.trim().toLowerCase() !== catName) continue;
            for (const wi of (t.work_items ?? [])) {
              if ((wi.status ?? "open") === "closed") continue;
              // Judge-flagged dup: relocated to Needs-Your-Input — mirror the
              // other lens bodies and exclude it here (code review 2026-09-29).
              if (isJudgeDup(wi)) continue;
              if (!itemMatchesAssoc(wi)) continue;
              if (searchFocus.assoc && !itemAssociationUnion(wi).includes(searchFocus.assoc)) continue;
              if (searchFocus.rep && !itemHasReportAction(wi, searchFocus.rep)) continue;
              const hit = itemSearchHits(t.name, wi, searchFocus.text);
              let actions = hit.itemHit
                ? (wi.action_items ?? []).filter((a) => a.status !== "closed")
                : hit.actionHits;
              if (searchFocus.pri) {
                actions = actions.filter((a) => (a.priority_status ?? "Active") === searchFocus.pri);
              }
              if (!hit.itemHit && !actions.length) continue;
              if (searchFocus.pri && !actions.length) continue;
              n += 1;
              const path = document.createElement("div");
              path.className = "search-hit-path";
              const letter = subProjectLetter(sp.seq);
              path.textContent = `${letter ? `${letter} · ` : ""}${sp.name} › ${t.display_number ? `${t.display_number}. ` : ""}${t.name}`;
              results.appendChild(path);
              const shown = new Set(actions);
              results.appendChild(renderItemInLens(t, wi, {
                actionFilter: (a) => shown.has(a), hideClosedActions: true,
              }));
            }
          }
        }
      }
      const status = document.createElement("div");
      status.className = "search-status muted";
      status.textContent = !anyCriteria
        ? "No search criteria — open ⌕ Advanced search to refine."
        : (n === 1 ? "1 Item" : `${n} Items`);
      results.insertBefore(status, results.firstChild);
    } else if (lens === "sub_project") {
      for (const sp of subProjects) {
        const threads = threadsOf(sp).filter(threadMatchesAssoc);
        if (assocActive && !threads.length) continue;
        el.appendChild(renderSubProjectGroup(sp, threads, renderThread, lensCtx));
      }
      const unassigned = threadsOf(UNASSIGNED_SUB_PROJECT).filter(threadMatchesAssoc);
      if (unassigned.length) {
        el.appendChild(renderSubProjectGroup(UNASSIGNED_SUB_PROJECT, unassigned, renderThread, lensCtx));
      }
    } else if (lens === "category") {
      // Category NAME → sub-project pathway → the full Category section, in a
      // collapsible card with the worst-priority edge + a leading category number.
      const catIndexOf = (members: ThreadView[]): string | null => {
        for (const t of members) {
          const m = (t.display_number ?? "").match(/^[A-Za-z]*(\d+)/);
          if (m) return m[1]!;
        }
        return null;
      };
      // Hide-empty: filter empty Categories before grouping so the Category-lens
      // body matches its rail (renderLensTocV4's categoryEntries) — a group with
      // no non-empty member disappears, and empty members within a surviving
      // group are hidden. Gated by the same project-wide blank-guard.
      for (const g of groupThreadsByCategoryName(sortedThreads.filter(
        (t) => threadMatchesAssoc(t) && (!hideEmptyCategories || !isEmptyCategory(t)),
      ))) {
        const items = g.members.flatMap((t) => openItemsInLens(t));
        const idx = catIndexOf(g.members);
        const { section, body } = openLensGroupCard(
          idx ? `${idx}. ${g.name}` : g.name, g.members.length,
          lensGroupAnchorId("cat", g.name), worstItemPriority(items),
          itemsUnplaced(items), `cat:${g.name}`,
        );
        for (const t of g.members) {
          // Restyle round-2 (Ray 2026-09-08): in the Category lens the sub-project
          // pathway sticks as a secondary subhead (below the sticky Category
          // header) so the current sub-project stays visible while scrolling a tall
          // category group. `.sticky-sp` scopes the sticky to this lens only — the
          // same pathway in the association/priority lenses stays in-flow.
          const spPath = renderSubProjectPathway(spOf(t));
          spPath.classList.add("sticky-sp");
          body.appendChild(spPath);
          body.appendChild(renderThread(t));
        }
        el.appendChild(section);
      }
    } else if (lens === "association") {
      // P4d: Association value → sub-project/Category pathway → Item cards whose
      // effective set (own ∪ its Actions', the cascade) includes that value. An
      // Item is multi-valued, so it appears under EACH of its associations. With
      // the filter active, only the selected values' groups show; otherwise a
      // trailing "⚑ Needs association" bucket collects axis-project Items with no
      // association placed (openItemsInLens already excludes them when filtering).
      const slugsToShow = assocActive ? assocSlugs.filter((s) => selectedAssoc.has(s)) : assocSlugs;
      for (const slug of slugsToShow) {
        const rows: Array<{ sp: SubProjectView; t: ThreadView; wi: WorkItemView }> = [];
        for (const sp of spWithUnassigned) {
          for (const t of threadsOf(sp)) {
            for (const wi of openItemsInLens(t)) {
              if (itemAssociationUnion(wi).includes(slug)) rows.push({ sp, t, wi });
            }
          }
        }
        if (!rows.length) continue;
        const { section, body } = openLensGroupCard(
          humanizeSlug(slug), rows.length, lensGroupAnchorId("assoc", slug),
          worstItemPriority(rows.map((r) => r.wi)), false, `assoc:${slug}`,
        );
        appendPathwayItemRows(rows, body);
        el.appendChild(section);
      }
      if (!assocActive) {
        const rows: Array<{ sp: SubProjectView; t: ThreadView; wi: WorkItemView }> = [];
        for (const sp of spWithUnassigned) {
          for (const t of threadsOf(sp)) {
            for (const wi of openItemsInLens(t)) {
              if (!itemAssociationUnion(wi).length) rows.push({ sp, t, wi });
            }
          }
        }
        if (rows.length) {
          const { section, body } = openLensGroupCard(
            "⚑ Needs association", rows.length, lensGroupAnchorId("assoc", "needs"),
            worstItemPriority(rows.map((r) => r.wi)), true, "assoc:needs",
          );
          appendPathwayItemRows(rows, body);
          el.appendChild(section);
        }
      }
    } else if (lens === "reports") {
      // Report inclusion → Item cards. TWO reports with DIFFERENT keying (B,
      // 2026-09-16): Key Issue Log = ACTION-keyed (`report_tags ∋ 558`) — the Item
      // card is FILTERED to only its 558 actions (unmarked siblings must NOT show);
      // Project Status Update = ITEM-keyed (`wi.kil_marked`) — the Item renders
      // ITEM-ONLY (title + narrative, no action rows), matching the PSU report.
      // Scale Summary de-listed; an Item in both shows in both cards. A trailing
      // "Not in a report" bucket collects the rest. ⚑ omitted (placement is a
      // cross-concern). Honors the Association filter via openItemsInLens.
      const openActionsOf = (wi: WorkItemView): AnyItem[] =>
        (wi.action_items ?? []).filter((a) => (a.status ?? "open") !== "closed");
      const renderReportGroup = (
        key: string, label: string,
        rows: Array<{ sp: SubProjectView; t: ThreadView; wi: WorkItemView; hits: AnyItem[] | null }>,
      ): void => {
        if (!rows.length) return;
        const { section, body } = openLensGroupCard(
          label, rows.length,
          lensGroupAnchorId("rep", key), worstItemPriority(rows.map((r) => r.wi)),
          false, `rep:${key}`, // ⚑ omitted on Reports — placement is a cross-concern here
        );
        let lastSp: SubProjectView | null = null;
        let lastThread: ThreadView | null = null;
        for (const row of rows) {
          if (row.sp !== lastSp) { body.appendChild(renderSubProjectPathway(row.sp)); lastSp = row.sp; lastThread = null; }
          if (row.t !== lastThread) { body.appendChild(renderCategoryPathway(row.t)); lastThread = row.t; }
          // 558: filter to the item's marked actions. PSU (hits === null):
          // item-only, show no action rows.
          const hitSet = row.hits ? new Set(row.hits) : null;
          body.appendChild(renderItemInLens(row.t, row.wi, {
            actionFilter: hitSet ? (a) => hitSet.has(a) : () => false,
            hideClosedActions: true,
          }));
        }
        el.appendChild(section);
      };
      // Key Issue Log (558) — F-FLAT (Ray 2026-11-03 SPA-review call): the KIL lens
      // group renders Association band → FLAT action rows (no sub-project /
      // sub-category / Item tiers), mirroring the 558 report page's flat SHAPE. Band
      // order follows the SPA's association axis (`associationOptions` / the chips),
      // as every other lens does — NOT the config order the .xlsx + report page use
      // (ultra #520 nit; SPA-vs-output order consistency is a separate follow-up).
      // Each OPEN 558 action bands by its OWN `association_tags` (a both-tower action
      // shows under each); the Item header + its PSU pill — the "noise" Ray flagged —
      // are gone. An action with no known association shows under a "(No association)"
      // band so it stays visible + fixable in the working lens (the .xlsx/report page
      // footer-exclude it; the lens surfaces it).
      const kilActs: KilLensRow[] = [];
      for (const sp of spWithUnassigned) {
        for (const t of threadsOf(sp)) {
          for (const wi of openItemsInLens(t)) {
            for (const a of openActionsOf(wi)) {
              if ((a.report_tags ?? []).includes("558")) kilActs.push({ t, wi, action: a });
            }
          }
        }
      }
      const { bands: kilBands, shown: shownKilActs } =
        groupKilActionsForLens(kilActs, associationOptions, selectedAssoc);
      if (shownKilActs.length) {
        // The card's priority edge comes from the SHOWN actions only (ultra #520 r2):
        // worstItemPriority scans every open action on each Item, so it would colour
        // the card for a non-558 / other-tower sibling the flat card never lists.
        const { section, body } = openLensGroupCard(
          "Key Issue Log", shownKilActs.length, lensGroupAnchorId("rep", "558"),
          worstActionPriority(shownKilActs.map((r) => r.action)), false, "rep:558",
        );
        for (const band of kilBands) {
          if (band.label) {
            const h = document.createElement("h3");
            h.className = "report-assoc-band-header";
            h.textContent = band.label;
            body.appendChild(h);
          }
          const list = document.createElement("ul");
          list.className = "actions-list";
          for (const r of band.actions) {
            list.appendChild(renderActionV4(
              record.run_id, r.t.slug, r.wi.slug, r.action, fb,
              participantOptions, threadOptions, allActionOptions, allItemOptions,
              associationOptions, subProjectOptions,
              resolveSubProjectBucket(r.t.sub_project_slug, spKnown),
            ));
          }
          body.appendChild(list);
        }
        el.appendChild(section);
      }
      // Project Status Update — ITEM-keyed (B): the Item's own mark; item-only.
      const psuRows: Array<{ sp: SubProjectView; t: ThreadView; wi: WorkItemView; hits: AnyItem[] | null }> = [];
      for (const sp of spWithUnassigned) {
        for (const t of threadsOf(sp)) {
          for (const wi of openItemsInLens(t)) {
            if (itemHasPsuMark(wi)) psuRows.push({ sp, t, wi, hits: null });
          }
        }
      }
      renderReportGroup("psu", "Project Status Update", psuRows);
      const noneRows: Array<{ sp: SubProjectView; t: ThreadView; wi: WorkItemView }> = [];
      for (const sp of spWithUnassigned) {
        for (const t of threadsOf(sp)) {
          for (const wi of openItemsInLens(t)) {
            if (!itemHasReportAction(wi, "558") && !itemHasPsuMark(wi)) noneRows.push({ sp, t, wi });
          }
        }
      }
      if (noneRows.length) {
        const { section, body } = openLensGroupCard(
          "Not in a report", noneRows.length, lensGroupAnchorId("rep", "none"),
          worstItemPriority(noneRows.map((r) => r.wi)), false, "rep:none",
        );
        appendPathwayItemRows(noneRows, body);
        el.appendChild(section);
      }
    } else if (lens === "priority") {
      // Priority → sub-project → Category pathway → Item cards filtered to
      // that priority's open Actions; Items with no open Action trail last.
      const openItemsOf = (t: ThreadView): WorkItemView[] =>
        (t.work_items ?? []).filter(
          // #66 D4: judge-flagged dups relocate to Needs-Your-Input.
          (wi) => (wi.status ?? "open") !== "closed" && !isJudgeDup(wi));
      const openActionsOf = (wi: WorkItemView): AnyItem[] =>
        (wi.action_items ?? []).filter((a) => a.status !== "closed");
      const prioOf = (a: AnyItem): PriorityStatus =>
        (PRIORITY_ORDER as readonly string[]).includes(a.priority_status ?? "")
          ? (a.priority_status as PriorityStatus) : "Active";
      const buckets: Array<{ label: string; match: (wi: WorkItemView) => AnyItem[] | null }> = [
        ...PRIORITY_ORDER.map((p) => ({
          label: p,
          match: (wi: WorkItemView) => {
            const hits = openActionsOf(wi).filter((a) => prioOf(a) === p);
            return hits.length ? hits : null;
          },
        })),
        { label: "No open Actions", match: (wi) => openActionsOf(wi).length ? null : [] },
      ];
      for (const bucket of buckets) {
        const rows: Array<{ sp: SubProjectView; t: ThreadView; wi: WorkItemView; hits: AnyItem[] }> = [];
        for (const sp of spWithUnassigned) {
          for (const t of threadsOf(sp)) {
            for (const wi of openItemsOf(t)) {
              if (!itemMatchesAssoc(wi)) continue;
              const hits = bucket.match(wi);
              if (hits) rows.push({ sp, t, wi, hits });
            }
          }
        }
        if (!rows.length) continue;
        const worstCls = (PRIORITY_ORDER as readonly string[]).includes(bucket.label)
          ? priorityClass(bucket.label as PriorityStatus) : null;
        const { section, body } = openLensGroupCard(
          bucket.label, rows.length, lensGroupAnchorId("pri", bucket.label),
          worstCls, false, `pri:${bucket.label}`, // ⚑ omitted on Priority (cross-concern)
        );
        let lastSp: SubProjectView | null = null;
        let lastThread: ThreadView | null = null;
        for (const row of rows) {
          if (row.sp !== lastSp) { body.appendChild(renderSubProjectPathway(row.sp)); lastSp = row.sp; lastThread = null; }
          if (row.t !== lastThread) { body.appendChild(renderCategoryPathway(row.t)); lastThread = row.t; }
          const hitSet = new Set(row.hits);
          body.appendChild(renderItemInLens(row.t, row.wi, {
            actionFilter: (a) => hitSet.has(a), hideClosedActions: true,
          }));
        }
        el.appendChild(section);
      }
    }
    // (The former "Search" lens branch is gone — search is now the transient
    // focus mode handled by the `if (searchFocus)` branch at the top of this
    // dispatch, opened from the ⌕ Advanced-search toolbar popover.)
  } else {
    appendPhaseBands(el, planPhaseBands(sortedThreads, hideEmptyCategories), renderThread);
  }

  // #60 (2026-06-09 review): ONE undifferentiated "Needs Your Input" queue.
  // Supersedes the 2026-06-03 three-sub-section layout (Suggested Categories /
  // Unfiled / Informational): Ray asked for "all one area" with no
  // category-vs-informational pre-framing. candidate_threads[] is NO LONGER a
  // recipient surface (option (a)) — the #61 substantiation gate + synth
  // rule 1c route a candidate's content to unfiled[] with missing-info
  // framing, so the bare topic row would be redundant noise; the array stays
  // in the payload as harness telemetry only.
  const hasUnfiled = !!record.payload.unfiled?.length;
  const hasInformational = !!record.payload.informational?.length
    || !!record.payload.informational_acknowledged?.length;
  // #66 D4: judge-flagged duplicates can be the ONLY Needs-Your-Input content,
  // so the section (and its TOC anchor, app.ts) must appear on their account too.
  const hasJudgeDup = judgeDupRows.length > 0;
  if (hasUnfiled || hasInformational || hasJudgeDup) {
    // Decision 7A / A³: OPEN Items across all Categories for the Place-as-New
    // → Action parent picker (open-only — placing under a closed Item would be
    // a contradiction; that's what "Already complete" is for).
    const itemOptions: ItemOption[] = [];
    // #60 PR4-v2 Merge-Into-Existing pickers — OPEN+CLOSED per tier (a row may
    // dedup against either). Separate from the open Place pickers, which keep
    // excluding closed/retired per the 2026-06-09 client decision.
    const mergeThreadOptions: ThreadOption[] = [];
    const mergeItemOptions: ItemOption[] = [];
    const mergeActionOptions: ClosedActionOption[] = [];
    for (const t of record.payload.threads ?? []) {
      mergeThreadOptions.push({
        slug: t.slug, name: t.name, display_number: t.display_number ?? null,
        closed: t.status === "closed",
        sub_project_slug: t.sub_project_slug ?? null, // B3: merge-into-existing filter
      });
      for (const wi of t.work_items ?? []) {
        const wiClosed = wi.status === "closed";
        const itemOpt: ItemOption = {
          slug: wi.slug, name: wi.name,
          display_number: wi.display_number ?? null, thread_name: t.name,
          closed: wiClosed,
          sub_project_slug: t.sub_project_slug ?? null, // B3: merge-into-existing filter
          thread_slug: t.slug, // F2: Sub-project → Category cascade over the Item picker
        };
        mergeItemOptions.push(itemOpt);
        if (!wiClosed) itemOptions.push(itemOpt);
        for (const ai of wi.action_items ?? []) {
          if (!ai.ai_slug) continue;
          // Synth emits `what` (alias); persisted payloads use `text`.
          const aiText = ai.text ?? ai.what ?? "";
          if (!aiText) continue;
          // #2.2: lead with the Action's display number (N.N.N) when present.
          const num = ai.display_number ? `${ai.display_number} ` : "";
          mergeActionOptions.push({
            work_item_slug: wi.slug,
            action_item_slug: ai.ai_slug,
            label: `${num}${aiText} (${wi.name})`,
            closed: ai.status === "closed",
            // B3: parent sub-project + Item label for the Action-tier filter.
            sub_project_slug: t.sub_project_slug ?? null,
            item_label: `${wi.display_number ? wi.display_number + " " : ""}${wi.name}`,
          });
        }
      }
    }
    itemOptions.sort((a, b) => compareDisplayNumber(a.display_number, b.display_number));
    mergeItemOptions.sort((a, b) => compareDisplayNumber(a.display_number, b.display_number));
    // Closed Items beyond the blob's 2-week retention come from the
    // Closed-Items snapshot (all ages — the kimley-horn case). Async: the
    // arrays are captured by reference in each row's form spec, and
    // buildForm reads them lazily at popover-open, so options that arrive
    // before the recipient opens the form (near-always) just appear.
    // Memoized — re-renders (KIL toggles, feedback events) reuse the
    // session's fetch. Categories/Actions stay blob-only in v1.
    // B3: the snapshot's ClosedItem carries no sub_project_slug, so derive it
    // from its Category via the blob's threads — otherwise a cross-window closed
    // Item buckets under "(No sub-project)" in the merge_into_existing filter.
    // A truly-archived Category (not in the blob) stays unassigned (acceptable).
    const threadSubProject = new Map<string, string | null>();
    for (const t of record.payload.threads ?? []) {
      threadSubProject.set(t.slug, t.sub_project_slug ?? null);
    }
    closedItemsSnapshotOnce().then((snap) => {
      const have = new Set(mergeItemOptions.map((i) => i.slug));
      for (const ci of snap.closed_items ?? []) {
        if (have.has(ci.slug)) continue;
        mergeItemOptions.push({
          slug: ci.slug, name: ci.name,
          display_number: ci.display_number ?? null,
          thread_name: ci.thread_name ?? undefined,
          closed: true,
          sub_project_slug: ci.thread_slug
            ? threadSubProject.get(ci.thread_slug) ?? null : null,
          thread_slug: ci.thread_slug ?? null, // F2: cascade over the Item picker
        });
      }
      mergeItemOptions.sort(
        (a, b) => compareDisplayNumber(a.display_number, b.display_number),
      );
    }).catch(() => { /* picker degrades to blob-only closed targets */ });

    // #66 D4: build the relocated suspected-duplicate Item cards via the SAME
    // renderItemV4 the lenses use, so each carries its live merge_candidate
    // banner + one-click "Merge into X" (work_item_merge) for free. Rendered as
    // a "Possible duplicates" group at the top of Needs-Your-Input.
    const judgeDupCards = judgeDupRows.map(({ t, wi }) => renderItemV4(
      record.run_id, t.slug, wi, fb, participantOptions, threadOptions,
      allItemOptions, allActionOptions, t.subcategories ?? [],
      { hideClosedActions: true }, associationOptions, subProjectOptions,
      resolveSubProjectBucket(t.sub_project_slug, spKnown),
      pointOptions, // KIL-POINT: "Set KIL Point" datalist source
    ));
    el.appendChild(renderNeedsInputUnifiedV4(
      record.run_id,
      record.payload.unfiled ?? [],
      record.payload.informational ?? [],
      record.payload.informational_acknowledged ?? [],
      fb, participantOptions, threadOptions, itemOptions,
      { mergeThreadOptions, mergeItemOptions, mergeActionOptions },
      subProjectOptions, // B3: Place → "Under Category" Sub-project pre-filter
      judgeDupCards,
    ));
  }

  // v3 legacy `needs_review` surface. E1 (Ray, 2026-07-14): this block leaked
  // non-project items (the queue is surfaced run-wide, unfiltered by project —
  // agent/state.py iter_review_queue) AND is a read-only dead-end with no
  // recipient affordance. Operator decision: OPERATOR-ONLY for now — recipients
  // only see actionable work. Render ONLY on the operator's /preview surface so
  // it drops off the live recipient briefing entirely. Backlog: scope the queue
  // by project + an actionable "confirm this is a [Project] item" hybrid.
  if (inPreviewMode() && record.payload.needs_review?.length) {
    el.appendChild(
      renderNeedsReviewV4(record.run_id, record.payload.needs_review, fb),
    );
  }
}

// #60 (2026-06-09 review): one undifferentiated Needs-Your-Input queue.
// unfiled[] + informational[] interleave into a single type-neutral list —
// no "Suggested Category" / "FYI" / "Unfiled" sub-headings or chips (Ray:
// "all one area", "let me decide"). Every row gets the SAME full affordance
// set (kindsForNeedsInputV4). Target identity stays per-source
// (unfiled:<id> / informational:<id>) so prior feedback decoration, Worker
// routing, and the absorber are untouched (architecture option A).
type NeedsInputEntryView =
  | { source: "unfiled"; e: UnfiledItemView }
  | { source: "informational"; e: InformationalItemView };

function renderNeedsInputUnifiedV4(
  runId: string,
  unfiled: UnfiledItemView[],
  informational: InformationalItemView[],
  acknowledged: InformationalItemView[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  itemOptions: ItemOption[],
  merge: {
    mergeThreadOptions: ThreadOption[];
    mergeItemOptions: ItemOption[];
    mergeActionOptions: ClosedActionOption[];
  },
  // B3: the sub-project registry, for the Place → "Under Category" Sub-project
  // pre-filter. Empty on a flat project → no pre-filter (byte-identical). No
  // "current" sub-project for a Needs-Input row → the filter defaults to the
  // first sub-project.
  subProjectOptions: Array<{ value: string; label: string }> = [],
  // #66 D4: pre-built Item cards for Items the dedup judge flagged as suspected
  // duplicates (relocated out of the body). Rendered as a "Possible duplicates"
  // group at the top of the section. Empty on every project until the judge is
  // enabled → byte-identical to before.
  judgeDupCards: HTMLElement[] = [],
): HTMLElement {
  const section = document.createElement("section");
  // Class pairing: `.unfiled-section` carries the card chrome (bg/border/
  // row dividers), `.needs-input-section` the anchor offset + h2 type.
  section.className = "unfiled-section needs-input-section";
  // Decision 12.15 (2026-05-28): stable id for the sidebar TOC link.
  section.id = NEEDS_INPUT_ANCHOR_ID;

  const dupCount = judgeDupCards.length;
  const active = unfiled.length + informational.length;
  const h2 = document.createElement("h2");
  h2.textContent = `Needs Your Input (${active + dupCount})`;
  section.appendChild(h2);

  const lead = document.createElement("p");
  lead.className = "lead";
  // Honest copy per state: the call-to-action only when there's something
  // to act on (the section can render with 0 active entries when only the
  // acknowledged collapse remains).
  lead.textContent = active
    ? "Things the agent couldn't place or decide on its own — you decide " +
      "what each is. Place it as a Category, Item, or Action; file it under " +
      "already-closed work; dismiss it (kept on the record); or mark it not " +
      "relevant."
    : dupCount
      ? "Possible duplicates the agent flagged for your review are below."
      : "Nothing needs your input this week.";
  section.appendChild(lead);

  // #66 D4: the "Possible duplicates" group — full Item cards pulled from the
  // body, each carrying its one-click "Merge into…" banner. At the TOP so the
  // declutter is what the recipient sees first.
  if (dupCount) {
    const dupHead = document.createElement("h3");
    dupHead.className = "needs-input-subhead";
    dupHead.textContent = `Possible duplicates (${dupCount})`;
    section.appendChild(dupHead);
    const dupLead = document.createElement("p");
    dupLead.className = "lead";
    dupLead.textContent =
      "The agent created these as new but suspects they duplicate work already " +
      "tracked. On each, use the ✎ menu's “Merge into…” to fold it into the " +
      "existing item, or dismiss the hint to keep it as its own item.";
    section.appendChild(dupLead);
    const dupWrap = document.createElement("div");
    dupWrap.className = "needs-input-dup-cards";
    for (const card of judgeDupCards) dupWrap.appendChild(card);
    section.appendChild(dupWrap);
  }

  // Type-neutral interleave: NEW first, then most recent source activity.
  const entries: NeedsInputEntryView[] = [
    ...unfiled.map((e) => ({ source: "unfiled" as const, e })),
    ...informational.map((e) => ({ source: "informational" as const, e })),
  ];
  entries.sort((a, b) => {
    const newDiff = Number(!!b.e.is_new) - Number(!!a.e.is_new);
    if (newDiff) return newDiff;
    return (b.e.source_timestamp ?? "").localeCompare(a.e.source_timestamp ?? "");
  });

  const fillRow = (
    li: HTMLElement, e: UnfiledItemView | InformationalItemView,
    // The acknowledged collapse is a read-only record — triage PROMPTS
    // (Hint / Missing) would be a call-to-action on a row with no controls.
    opts: { triagePrompts: boolean } = { triagePrompts: true },
  ): void => {
    const text = document.createElement("strong");
    text.textContent = e.text;
    li.appendChild(text);
    appendNewPill(li, e.is_new);
    if (e.attribution) {
      const attr = document.createElement("span");
      attr.className = "muted";
      attr.textContent = ` (${e.attribution})`;
      li.appendChild(attr);
    }
    // Origin breadcrumb (guard-relocated entries) — provenance context,
    // not a type label.
    if ("origin_thread_name" in e && e.origin_thread_name) {
      const origin = document.createElement("div");
      origin.className = "muted";
      origin.style.fontStyle = "italic";
      origin.style.marginTop = "2px";
      origin.textContent = e.origin_work_item_name
        ? `from ${e.origin_thread_name} › ${e.origin_work_item_name}`
        : `from ${e.origin_thread_name}`;
      li.appendChild(origin);
    }
    if (e.narrative) {
      const narrative = document.createElement("div");
      narrative.className = "muted";
      narrative.style.marginTop = "2px";
      narrative.textContent = e.narrative;
      li.appendChild(narrative);
    }
    if (opts.triagePrompts && "hint" in e && e.hint) {
      const hint = document.createElement("div");
      hint.className = "muted";
      hint.style.fontStyle = "italic";
      hint.style.marginTop = "2px";
      hint.textContent = `Hint: ${e.hint}`;
      li.appendChild(hint);
    }
    // #61: "you're missing information" line — what would settle the
    // placement, plus the agent's best tier guess. Replaces the old
    // assume-it's-a-Category framing.
    if (opts.triagePrompts && "missing" in e && e.missing?.length) {
      const missing = document.createElement("div");
      missing.className = "muted needs-input-missing";
      missing.style.marginTop = "2px";
      const tierLead = ("suggested_tier" in e && e.suggested_tier)
        ? (e.suggested_tier === "action" ? "Likely an Action — " : "Likely an Item — ")
        : "";
      missing.textContent = `${tierLead}Missing: ${e.missing.join(" · ")}`;
      li.appendChild(missing);
    }
  };

  const ul = document.createElement("ul");
  for (const entry of entries) {
    const li = document.createElement("li");
    li.className = "unfiled-row";
    fillRow(li, entry.e);
    // S3 accept-assist (2026-10-29): a one-click "Merge into «X»" when the agent
    // resolved the hint to a concrete existing Item (suggested_action==="merge_item")
    // AND that Item is in the rendered tree. Accept-assist, not auto-place — Ray reads
    // the target, taps to accept only when right, ignores it otherwise (normal picker
    // stays). Undo-backed: POST fires IMMEDIATELY (durable optimistic removal), and the
    // Undo toast RETRACTS the pending event within the window (a wrong accept after the
    // window is a low-harm born-closed note under the Item, recoverable via Closed
    // Items). Rows without a resolvable merge target stay plain. Reuses
    // merge_into_existing — no new kind, no Worker.
    if (
      entry.source === "unfiled"
      && entry.e.suggested_action === "merge_item"
      && entry.e.suggested_target_work_item_slug
    ) {
      const wslug = entry.e.suggested_target_work_item_slug;
      const tgt = itemOptions.find((o) => o.slug === wslug);
      if (tgt) {
        const label = tgt.display_number
          ? `${tgt.display_number} ${tgt.name}` : tgt.name;
        const bar = document.createElement("div");
        bar.className = "unfiled-accept-assist";
        const lead = document.createElement("span");
        lead.className = "muted";
        lead.textContent = "Suggested — ";
        bar.appendChild(lead);
        const accept = document.createElement("button");
        accept.type = "button";
        accept.className = "unfiled-accept-btn";
        accept.textContent = `✓ Merge into ${label}`;
        accept.title =
          `Fold this into the existing Item "${label}". You can Undo right after.`;
        const srcRef = entry.e.source_ref;
        const entryId = entry.e.id;
        const entryText = entry.e.text;
        const unfiledTargetId = unfiledKey(entryId);
        accept.addEventListener("click", () => {
          // Double-submit guard until the POST resolves (the optimistic removal then
          // tears the whole row out, so the button is gone after re-render anyway).
          if (accept.disabled) return;
          accept.disabled = true;
          void (async () => {
            let eventId: string;
            try {
              // POST IMMEDIATELY (#510 nit-1/2) — feedback-state-changed drives the
              // durable optimistic removal; the row leaves on re-render and stays gone.
              eventId = await acceptMergeSuggestion(runId, unfiledTargetId, {
                id: entryId, source_ref: srcRef, text: entryText,
                targetWorkItemSlug: wslug,
              });
            } catch {
              accept.disabled = false;  // POST failed — row stays, let them retry
              return;
            }
            // Undo = RETRACT the pending event (removes it from the Worker + re-derives
            // the optimistic layer so the row returns). After the window it commits as
            // a low-harm born-closed note under the Item (recoverable via Closed Items).
            showUndoToast({
              message: `Merged into ${label}`,
              undoLabel: "Undo",
              durationMs: 8000,
              // #510 F1: RETURN the retract promise and DON'T swallow errors. The
              // toast awaits it — a failure (409 already-absorbed, 403, network)
              // surfaces inline + keeps the toast open so the recipient knows the
              // merge is still committed, instead of closing as if undo succeeded.
              onUndo: () => retractFeedbackEvent(
                runId, eventId, "merge_into_existing", unfiledTargetId,
              ),
            });
          })();
        });
        bar.appendChild(accept);
        li.appendChild(bar);
      }
    }
    const targetId = entry.source === "unfiled"
      ? unfiledKey(entry.e.id)
      : informationalKey(entry.e.id);
    const controls = renderFormControls({
      runId,
      targetType: entry.source,
      targetId,
      kinds: kindsForNeedsInputV4(),
      submitted: fb.get(targetId) ?? [],
      participantOptions,
      threadOptions,
      itemOptions,
      subProjectOptions, // B3: Place → "Under Category" Sub-project pre-filter
      // The Place tiers + dispositions need the entry's source_ref (bucket
      // suppression) + id; File-under-Closed also snapshots the text as the
      // record's body. Injected at submit — not visible fields.
      bucketContext: {
        sourceRef: entry.e.source_ref, entryId: entry.e.id,
        entryText: entry.e.text,
        entryNarrative: entry.e.narrative ?? undefined,
        // #1.4: candidate-folded rows carry candidate_slug so a Place/Merge
        // retires the inferred thread.
        candidateSlug: entry.source === "unfiled"
          ? entry.e.candidate_slug ?? undefined : undefined,
      },
      mergeThreadOptions: merge.mergeThreadOptions,
      mergeItemOptions: merge.mergeItemOptions,
      mergeActionOptions: merge.mergeActionOptions,
    });
    controls.classList.add("unfiled-controls");
    li.appendChild(controls);
    ul.appendChild(li);
  }
  section.appendChild(ul);

  // Collapsed "Acknowledged this period (N)" — visible-but-tucked record of
  // dismissed-as-acknowledged entries (read-only; they age out naturally).
  // Holds acknowledged unfiled entries too (#60 widened the kind).
  if (acknowledged.length) {
    const details = document.createElement("details");
    details.className = "informational-acknowledged";
    const summary = document.createElement("summary");
    summary.textContent = `Acknowledged this period (${acknowledged.length})`;
    details.appendChild(summary);
    const ackUl = document.createElement("ul");
    for (const e of acknowledged) {
      const li = document.createElement("li");
      li.className = "unfiled-row acknowledged";
      // CADENCE: an agent-set-aside recurring ritual carries no recipient-safe
      // acknowledged_by (the actor sanitizer suppresses an agent label), so show
      // the reason instead — so the recipient sees WHY it's tucked, not a blank.
      if (e.auto_acknowledged) {
        // #508 ultra nit-2: unlike a recipient ack (which the recipient chose),
        // an AGENT set-aside must stay RECOVERABLE — a false-positive ritual match
        // keeps the full triage controls so the recipient can Place / mark
        // not-relevant / merge it back out of the acknowledged list. So this row
        // is NOT read-only (triagePrompts true) and carries its own controls.
        fillRow(li, e, { triagePrompts: true });
        const by = document.createElement("span");
        by.className = "muted";
        by.textContent = ` — set aside by Scale PA (${e.acknowledged_reason || "recurring weekly ritual"})`;
        li.appendChild(by);
        const targetId = informationalKey(e.id);
        const controls = renderFormControls({
          runId,
          targetType: "informational",
          targetId,
          kinds: kindsForNeedsInputV4(),
          submitted: fb.get(targetId) ?? [],
          participantOptions,
          threadOptions,
          itemOptions,
          subProjectOptions,
          bucketContext: {
            sourceRef: e.source_ref, entryId: e.id, entryText: e.text,
            entryNarrative: e.narrative ?? undefined,
          },
          mergeThreadOptions: merge.mergeThreadOptions,
          mergeItemOptions: merge.mergeItemOptions,
          mergeActionOptions: merge.mergeActionOptions,
        });
        controls.classList.add("unfiled-controls");
        li.appendChild(controls);
      } else {
        fillRow(li, e, { triagePrompts: false });
        if (e.acknowledged_by) {
          const by = document.createElement("span");
          by.className = "muted";
          by.textContent = ` — acknowledged by ${e.acknowledged_by}`;
          li.appendChild(by);
        }
      }
      ackUl.appendChild(li);
    }
    details.appendChild(ackUl);
    section.appendChild(details);
  }
  return section;
}


function renderCategoryV4(
  runId: string,
  thread: ThreadView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  itemOptions: ItemOption[],
  // B8 cross-Item action_merge: project-wide open Actions for the merge
  // picker (so it can fold an Action from another Item, not just siblings).
  allActionOptions: ActionOption[] = [],
  // P4c-2: Bubble-Up safe-collapse chrome — passed ONLY on the sub-projected
  // render path (renderBriefingV4), so a flat project's Category DOM is
  // byte-identical to pre-P4c.
  bubbleUp?: { projectSlug: string; lens?: LensId },
  // P4d-1b: association axis {value,label}, forwarded to each Item's pencil.
  // Empty on a flat project (byte-identical menu).
  associationOptions: Array<{ value: string; label: string }> = [],
  // P4d-3: sub-project registry {value,label} for this Category's "Move to
  // Sub-project" pencil. Empty on a flat project → the kind is gated off
  // (byte-identical menu).
  subProjectOptions: Array<{ value: string; label: string }> = [],
  // KIL-POINT (Ray 2026-10-28): the project's distinct existing 558 "Points",
  // forwarded to each Item's "Set KIL Point" <datalist>. Empty → no datalist.
  pointOptions: string[] = [],
): HTMLElement {
  const section = document.createElement("section");
  // v4-tagged so styles.css's `.thread.thread-v4 > .closed-items-divider`
  // and any future v4-only thread chrome can hook in without crossing
  // the v3 selectors.
  section.className = "thread thread-v4";
  if (thread.status === "closed") section.classList.add("closed");

  const header = document.createElement("div");
  header.className = "thread-header";
  // Decision 12.11 (2026-05-28, client feedback #8): TOC anchor id on
  // each Category header so the sidebar TOC can scroll to it. Format
  // matches `categoryAnchorId(slug)` below so renderTocV4 builds the
  // same href.
  if (thread.slug) {
    header.id = categoryAnchorId(thread.slug);
  }

  if (bubbleUp) {
    const bubble = summarizeCategory(thread);
    section.classList.add("bubble-up");
    section.classList.add(bubble.worst ? `bubble-${priorityClass(bubble.worst)}` : "bubble-none");
    if (bubble.changed) section.classList.add("bubble-changed");
    section.dataset.collapseKey = collapseStorageKey(bubbleUp.projectSlug, thread.slug, bubbleUp.lens);
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "category-collapse-toggle";
    toggle.innerHTML = CHEVRON_DOWN_SVG;
    toggle.title = "Collapse / expand this Category";
    toggle.setAttribute("aria-label", "Collapse / expand this Category");
    toggle.addEventListener("click", () => {
      setCategoryCollapsed(section, !section.classList.contains("collapsed"));
    });
    header.appendChild(toggle);
    // Resting state from memory; default collapsed (safe-collapse). Set
    // directly (not via setCategoryCollapsed): the header is not attached to
    // the section yet, so a querySelector would miss the toggle — and the
    // initial state must not WRITE memory (a first visit stays "unset").
    // S7 (Ray 2026-10-27): a Category with NO stored preference that has
    // new/updated open work (bubble.changed) defaults to EXPANDED, not
    // collapsed — otherwise a never-opened Category hides its new work AND the
    // "NEW" pill behind a collapsed header (Portofino A6.8/C3/C8). A stored
    // preference (viewer opened/closed it before) still wins. Quiet weeks with
    // no changed work keep the safe-collapse default.
    const initialCollapsed = readCollapsed(
      section.dataset.collapseKey, !bubble.changed,
    );
    section.classList.toggle("collapsed", initialCollapsed);
    toggle.setAttribute("aria-expanded", initialCollapsed ? "false" : "true");
  }

  const h2 = document.createElement("h2");
  if (thread.display_number) {
    const num = document.createElement("span");
    num.className = "path-num";
    num.textContent = `${thread.display_number}.`;
    h2.appendChild(num);
  }
  h2.appendChild(document.createTextNode(thread.name));
  if (thread.status === "closed") {
    const pill = document.createElement("span");
    pill.className = "status-pill closed";
    pill.textContent = thread.closed_at
      ? `Closed · ${thread.closed_at}`
      : "Closed";
    h2.appendChild(document.createTextNode(" "));
    h2.appendChild(pill);
  }
  if (thread.inferred) {
    const inf = document.createElement("span");
    inf.className = "muted";
    inf.textContent = " (inferred)";
    h2.appendChild(inf);
  }
  if (SHOW_MOVE_BREADCRUMB && thread.breadcrumb) {
    const bc = renderBreadcrumb(thread.breadcrumb);
    h2.appendChild(bc);
  }
  header.appendChild(h2);

  // Ray 2026-09-29: the Category header no longer shows an item count (all
  // reference counts + dots removed). The worst-priority left edge (set in the
  // bubbleUp block above) preserves the safe-collapse cue on its own.

  // Category-level form controls (Add Item, close/reopen, rename,
  // merge, comment, flag_next_week). Mirrors renderThreadV3's pattern.
  // B9: gate thread_close when the Category still has open Items — gray
  // + warn. The v4 path previously left this to a Worker-only reject, so
  // the button looked clickable ("letting you push through"). Worker
  // stays authoritative; this is the first-line UX.
  const categoryTargetId = threadKey(thread.slug);
  // NB: this counts a judge-flagged duplicate Item too — deliberately, unlike
  // the body/summary which relocate it (isJudgeDup). The Worker's authoritative
  // close gate (effectiveOpenItemsUnderThread) has no knowledge of the judge
  // stamp (no Worker release this promote), so it still blocks the close while
  // the dup Item is open; the recipient resolves it via merge/dismiss in
  // Needs-Your-Input. Excluding it here would desync this SPA hint from the
  // Worker (two-layer close-gate parity). code review 2026-09-29.
  const openItemCount = (thread.work_items ?? []).filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  // W3 CATEGORY-CASCADE-CLOSE: the cascade plan — every rendered-open Item with its
  // open Action targets. Offered ONLY when the cascade would clear the Worker's close
  // gate AND nothing strands the composite:
  //  - the rendered open-Item set equals openItemCount (the Worker-gated count incl.
  //    judge-dups) — if a judge-dup open Item is counted by the Worker gate but hidden
  //    from this render (isJudgeDup), the cascade couldn't close it → thread_close would
  //    409; fall back to the greyed close + notice (two-layer close-gate parity);
  //  - every open Item has a slug AND every open Action under them an ai_slug (an
  //    untargetable node would strand the composite mid-way).
  const openItemsForCascade = (thread.work_items ?? []).filter(
    (w) => (w.status ?? "open") === "open" && !isJudgeDup(w),
  );
  const categoryCascadePlan = openItemsForCascade.map((wi) => {
    const openActs = (wi.action_items ?? []).filter(
      (a) => (a.status ?? "open") === "open",
    );
    const targetableActs = openActs.filter((a) => a.ai_slug);
    return {
      slug: wi.slug,
      target_id: wi.slug ? workItemKey(thread.slug, wi.slug) : null,
      label: `${wi.display_number ? wi.display_number + " " : ""}${wi.name}`,
      display_number: wi.display_number ?? null,
      open_action_targets: targetableActs.map((a) => ({
        target_id: actionItemKey(thread.slug, wi.slug!, a.ai_slug!),
        label: `${a.display_number ? a.display_number + " " : ""}${
          a.text?.trim() || a.what?.trim() || a.ai_slug!}`,
      })),
      allActionsTargetable: targetableActs.length === openActs.length,
    };
  });
  const categoryCascadeAvailable = thread.status !== "closed"
    && openItemCount > 0
    && openItemsForCascade.length === openItemCount
    && categoryCascadePlan.every((ci) => ci.target_id !== null && ci.allActionsTargetable);
  const cascadeOpenItems = categoryCascadeAvailable
    ? categoryCascadePlan.map((ci) => ({
        target_id: ci.target_id!,
        label: ci.label,
        display_number: ci.display_number,
        open_action_targets: ci.open_action_targets,
      }))
    : undefined;
  header.appendChild(renderFormControls({
    runId,
    targetType: "thread",
    targetId: categoryTargetId,
    kinds: kindsForCategoryV4(
      thread.status === "closed", subProjectOptions.length > 0, categoryCascadeAvailable,
      thread.preset === true,
    ),
    // W3: the cascade plan (Items + their open Actions) for the confirm-all list +
    // the submit pre-loop; the Category's display number stamps the Item closes.
    cascadeOpenItems,
    movingFromDisplayNumber: thread.display_number ?? null,
    submitted: fb.get(categoryTargetId) ?? [],
    participantOptions,
    threadOptions,
    // P4d-3: the sub-project options for the Move-to-Sub-project <select>
    // (empty on a flat project → the kind is gated off above).
    subProjectOptions,
    // #38: the add-form mints a collision-free slug against the existing
    // groups (open + closed) + this session's pending adds (the latter ride in
    // via the optimistically-mutated thread.subcategories).
    subcategoryOptions: (thread.subcategories ?? []).map((s) => ({
      slug: s.slug, name: s.name, closed: s.status === "closed",
    })),
    // Pre-fill "Rename Category" with the current name to edit; #37 pre-selects
    // the Category's current phase in the Move-to-Phase select (null => General).
    current: {
      new_name: thread.name, phase: thread.phase ?? "general",
      // P4d-3: pre-select the Category's current sub-project (empty = none yet).
      sub_project_slug: thread.sub_project_slug ?? "",
    },
    // W3: the "close N Items first" notice only shows when the cascade is NOT
    // offered (an untargetable node / judge-dup blocks it) — when it IS offered,
    // thread_close_cascade closes them for the recipient, so the dead-end notice
    // would be wrong.
    notice: openItemCount > 0 && thread.status !== "closed" && !categoryCascadeAvailable
      ? `Close ${openItemCount} open Item${openItemCount === 1 ? "" : "s"} before closing this Category.`
      : undefined,
  }));

  // Category one-liner sits INSIDE the thread-header card so it
  // visually belongs to the Category title rather than floating as
  // a borderless paragraph between Category and first Item. Gated
  // behind CATEGORY_DESCRIPTION_VISIBLE at top of file — the client
  // spec doesn't request Category descriptions.
  if (CATEGORY_DESCRIPTION_VISIBLE && thread.one_liner) {
    const p = document.createElement("p");
    p.className = "thread-one-liner";
    p.textContent = thread.one_liner;
    header.appendChild(p);
  }

  section.appendChild(header);

  // Decision 6B: sort Items within Category by display_number ASC so
  // the path notation reads in order ("3.1 → 3.2 → 3.3"). Matches the
  // Category-level numeric sort in renderBriefingV4. Items with no
  // display_number sort to the end (defensive — pipeline should
  // backfill all numbers).
  const itemCmp = (a: WorkItemView, b: WorkItemView): number => {
    const parseN = (s?: string): number => {
      if (!s) return Number.POSITIVE_INFINITY;
      const parts = s.split(".");
      const last = parts[parts.length - 1];
      const n = parseInt(last ?? "", 10);
      return Number.isNaN(n) ? Number.POSITIVE_INFINITY : n;
    };
    return parseN(a.display_number) - parseN(b.display_number);
  };
  const openItems = (thread.work_items ?? [])
    // #66 D4: a judge-flagged suspected duplicate is relocated to Needs-Your-
    // Input (isJudgeDup), so it drops from the category body here — mirrors the
    // closedItems partition just below (in the tree, rendered elsewhere).
    .filter((wi) => wi.status !== "closed" && !isJudgeDup(wi))
    .sort(itemCmp);
  const closedItems = (thread.work_items ?? [])
    .filter((wi) => wi.status === "closed")
    .sort(itemCmp);

  // #38: sub-category render order — created_at ASC, seq tiebreak (mirrors the
  // agent stamp's sort so optimistic in-session adds slot in at the right spot).
  const scCmp = (a: SubCategoryView, b: SubCategoryView): number => {
    const ca = a.created_at ?? "";
    const cb = b.created_at ?? "";
    if (ca !== cb) return ca < cb ? -1 : 1;
    return (a.seq ?? 0) - (b.seq ?? 0);
  };
  const sortedSubcats = [...(thread.subcategories ?? [])].sort(scCmp);
  // An OPEN Item is "in" a group iff its pointer names an OPEN group under this
  // Category; a null pointer OR a pointer to a closed/missing group (the orphan
  // case) renders in the UNGROUPED prefix — mirroring the stamp's orphan sweep
  // so the SPA never hides an open Item under a closed group's tombstone.
  const openGroupSlugs = new Set(
    sortedSubcats.filter((s) => s.status !== "closed").map((s) => s.slug),
  );
  const groupOfOpen = (wi: WorkItemView): string | null => {
    const p = wi.subcategory_slug;
    return p && openGroupSlugs.has(p) ? p : null;
  };
  // Both open and closed Items carry sortedSubcats through renderItemV4 (the
  // closed-Item "(was in X)" suffix + the open Item's move picker).
  const renderItem = (wi: WorkItemView): HTMLElement => renderItemV4(
    runId, thread.slug, wi, fb, participantOptions, threadOptions,
    itemOptions, allActionOptions, sortedSubcats, undefined, associationOptions,
    // B3: the sub-project registry + THIS Category's sub-project, resolved to a
    // bucket so an UNASSIGNED Item defaults the move filter to "(No sub-project)".
    subProjectOptions,
    resolveSubProjectBucket(
      thread.sub_project_slug, new Set(subProjectOptions.map((s) => s.value))),
    pointOptions, // KIL-POINT: "Set KIL Point" datalist source
  );

  // UNGROUPED FIRST (legacy-prefix rule): the pre-grouping layout is a strict
  // prefix, so nothing visually jumps when a group is later created.
  for (const wi of openItems.filter((wi) => groupOfOpen(wi) === null)) {
    section.appendChild(renderItem(wi));
  }
  // Then the groups in ONE stamp-ordered sequence (created_at ASC, seq
  // tiebreak): an OPEN group renders its member cards; a CLOSED group renders
  // as a header-only tombstone IN PLACE in the same sequence. Closed groups are
  // interleaved by created_at, NOT bucketed after the open ones — this matches
  // the j2 email's single `for sc in thread.subcategories` loop (briefing-v4.md
  // .j2), so the SPA and the cron render the group order identically.
  for (const sc of sortedSubcats) {
    if (sc.status === "closed") {
      section.appendChild(renderSubcategoryTombstone(runId, thread, sc, fb));
    } else {
      section.appendChild(renderSubcategoryGroup(
        runId, thread, sc,
        openItems.filter((wi) => groupOfOpen(wi) === sc.slug),
        fb, renderItem, threadOptions,
      ));
    }
  }

  // A1: split closed Items into merged (context folded forward into a target)
  // vs plain closed (done/resolved) — a merge is not a close. Closed Items
  // ALWAYS aggregate in the single Category-level disclosure regardless of any
  // group pointer (the 2026-06-07 density fix is preserved), each carrying its
  // "(was in X)" suffix from renderItem.
  const mergedItems = closedItems.filter((wi) => isMergedClosure(wi.closed_reason));
  const plainClosedItems = closedItems.filter((wi) => !isMergedClosure(wi.closed_reason));
  const renderClosedGroup = (items: WorkItemView[], label: string): void => {
    if (!items.length) return;
    // Collapsed by default (2026-06-07 density fix): a big close-out can put
    // dozens of closed Items in the 2-week retention window — tuck them behind
    // a disclosure so they don't bury the active work. Expand for the full
    // closed cards (incl. reopen controls).
    const details = document.createElement("details");
    details.className = "closed-items-group";
    const summary = document.createElement("summary");
    summary.className = "closed-items-divider";
    summary.textContent = `${label} (${items.length})`;
    details.appendChild(summary);
    for (const wi of items) details.appendChild(renderItem(wi));
    section.appendChild(details);
  };
  renderClosedGroup(mergedItems, "Recently Merged Items");
  renderClosedGroup(plainClosedItems, "Recently Closed Items");

  return section;
}

// #38: an OPEN sub-category — a card-less `.subcategory-group` section with an
// unnumbered bold title row (NO path-num span — the unnumbered guarantee), its
// own rename/close/notes controls, and its member Item cards. styles.css
// restores the direct-child card chrome the wrapper would otherwise break
// (the #157-class fix).
function renderSubcategoryGroup(
  runId: string,
  thread: ThreadView,
  sc: SubCategoryView,
  items: WorkItemView[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  renderItem: (wi: WorkItemView) => HTMLElement,
  // E-7a: open Categories for the "Move to another Category" picker. Default []
  // keeps every non-lensed/test call site + the flat path unchanged.
  threadOptions: ThreadOptionView[] = [],
): HTMLElement {
  const group = document.createElement("section");
  group.className = "subcategory-group";

  const titleRow = document.createElement("div");
  titleRow.className = "subcategory-header";
  const title = document.createElement("h3");
  title.className = "subcategory-title";
  title.textContent = sc.name;
  titleRow.appendChild(title);

  // B9-style first-line gray of the close button when the group still has open
  // member Items; the Worker's effectiveOpenItemsUnderSubcategory stays
  // authoritative. The rendered open count already reflects optimistic closes
  // (a pending-closed member has status="closed" and isn't in `items`).
  const scTargetId = subcategoryKey(thread.slug, sc.slug);
  const openMemberCount = items.filter(
    (w) => (w.status ?? "open") === "open",
  ).length;
  // E-7a: offer "Move to another Category" only when ≥1 OTHER open Category
  // exists. The move carries ALL member Items (open + closed — a closed member's
  // pointer still names this group), each via item_move_to_category +
  // item_move_to_subcategory, so the whole group + its Items/Actions/tags land
  // under the target. Derived from the full thread here (not the open-only
  // `items`), so a closed member isn't left dangling under the source.
  const moveTargets = threadOptions.filter((t) => t.slug !== thread.slug);
  const moveMembers = (thread.work_items ?? [])
    .filter((w) => w.subcategory_slug === sc.slug)
    .map((w) => ({
      work_item_slug: w.slug,
      former_display_number: w.display_number,
    }));
  titleRow.appendChild(renderFormControls({
    runId,
    targetType: "subcategory",
    targetId: scTargetId,
    kinds: kindsForSubcategoryV4(false, moveTargets.length > 0),
    submitted: fb.get(scTargetId) ?? [],
    // Pre-fill "Rename Sub-category" with the current name to edit; the move
    // reads the same `to_name` for the recreated group's name.
    current: { to_name: sc.name },
    threadOptions: moveTargets,
    subcategoryMoveMembers: moveMembers,
    notice: openMemberCount > 0
      ? `Move or close ${openMemberCount} open Item${openMemberCount === 1 ? "" : "s"} in this sub-category before closing it.`
      : undefined,
  }));
  group.appendChild(titleRow);

  for (const wi of items) group.appendChild(renderItem(wi));
  return group;
}

// #38: a CLOSED sub-category renders as a HEADER-ONLY tombstone row inside the
// group sequence for 14 days (struck name + "Closed · DATE" pill + reopen
// pencil), holding no Item cards — the closed members aggregate in the
// Category-level "Recently Closed" disclosure with their "(was in X)" suffix.
// In-place closed rendering (the #158 lesson — verify in ?fixture=2).
function renderSubcategoryTombstone(
  runId: string,
  thread: ThreadView,
  sc: SubCategoryView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
): HTMLElement {
  const row = document.createElement("section");
  row.className = "subcategory-group subcategory-closed";

  const titleRow = document.createElement("div");
  titleRow.className = "subcategory-header";
  const title = document.createElement("h3");
  title.className = "subcategory-title closed";
  title.textContent = sc.name;
  const pill = document.createElement("span");
  pill.className = "status-pill closed";
  pill.textContent = sc.closed_at ? `Closed · ${sc.closed_at}` : "Closed";
  title.appendChild(document.createTextNode(" "));
  title.appendChild(pill);
  titleRow.appendChild(title);

  const scTargetId = subcategoryKey(thread.slug, sc.slug);
  titleRow.appendChild(renderFormControls({
    runId,
    targetType: "subcategory",
    targetId: scTargetId,
    kinds: kindsForSubcategoryV4(true),
    submitted: fb.get(scTargetId) ?? [],
  }));
  row.appendChild(titleRow);
  return row;
}

function renderItemV4(
  runId: string,
  threadSlug: string,
  wi: WorkItemView,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  itemOptions: ItemOption[],
  allActionOptions: ActionOption[] = [],
  // #38: the parent Category's sub-categories. Feeds (a) the kindsForItemV4
  // ≥1-open-group gate, (b) the move-picker options via renderFormControls,
  // (c) the closed-Item "(was in <group>)" suffix. Default [] keeps every
  // existing call site + the old-blob path byte-identical.
  subcategories: SubCategoryView[] = [],
  // P4e: a lens may show an Item card with only SOME of its open Actions
  // (Priority lens = that priority's; Search lens = the hits) and without the
  // closed-Actions tail. Absent = today's full card.
  lensOpts?: { actionFilter?: (a: AnyItem) => boolean; hideClosedActions?: boolean },
  // P4d-1b: the project's association axis {value,label}, threaded to the Item +
  // Action pencils' set_associations editor. Empty on a flat project → the kind
  // is gated off (hasAxis false), so the pencil menu is byte-identical.
  associationOptions: Array<{ value: string; label: string }> = [],
  // B3: the sub-project registry {value,label} + this Item's current sub-project
  // (its parent Category's sub_project_slug), for the move picker's Sub-project →
  // Category pre-filter. Empty registry on a flat project → no pre-filter
  // (byte-identical). Default [] / undefined keeps every non-lensed call site
  // and the old-blob path unchanged.
  subProjectOptions: Array<{ value: string; label: string }> = [],
  currentSubProjectSlug?: string | null,
  // KIL-POINT (Ray 2026-10-28): the project's distinct existing 558 "Points",
  // forwarded to the Item pencil's "Set KIL Point" <datalist> autocomplete. Empty
  // when no Point is set anywhere → no datalist (byte-identical pencil).
  pointOptions: string[] = [],
): HTMLElement {
  const wrapper = document.createElement("section");
  wrapper.className = `work-item work-item-${wi.status ?? "open"}`;
  if (wi.status === "closed") wrapper.classList.add("closed");
  // A² "New only" filter marker: an Item is "has-new" when it is itself
  // new OR carries a new Action (so a new Action keeps its Item visible
  // for context under the filter).
  const itemHasNew = !!wi.is_new
    || (wi.action_items ?? []).some((a) => !!a.is_new);
  if (itemHasNew) wrapper.classList.add("has-new");
  // A1 "new/updated" filter marker: an Item is "has-updated" when it was
  // itself MERGE'd this period OR carries an updated Action (mirrors has-new,
  // so a changed Action keeps its Item visible for context under the filter).
  const itemHasUpdated = !!wi.is_updated
    || (wi.action_items ?? []).some((a) => !!a.is_updated);
  if (itemHasUpdated) wrapper.classList.add("has-updated");

  const header = document.createElement("div");
  header.className = "work-item-header";

  const h3 = document.createElement("h3");
  if (wi.display_number) {
    const num = document.createElement("span");
    num.className = "path-num";
    // Demo-reference prefix "Item N.N" matches the v4 design walk
    // (2026-05-22): puts the level-marker word in front of the
    // hierarchical number so operators can scan-read what tier they're
    // looking at without parsing path notation.
    num.textContent = `Item ${wi.display_number}`;
    h3.appendChild(num);
  }
  h3.appendChild(document.createTextNode(wi.name));
  appendNewPill(h3, wi.is_new);
  appendUpdatedPill(h3, wi.is_updated);
  // Item-level "⚑ needs placement" roll-up DROPPED (Ray 2026-09-15): it was a
  // pure roll-up of the per-Action "⚑ Needs association" chip (rendered below on
  // each unplaced open Action) + echoed again by the sub-project header ⚑ — a
  // three-tier repeat of one fact that cluttered the backfill first-pass. The
  // specific signal (per-Action chip) + the top summary (sub-project header ⚑)
  // remain. Re-add here (ideally collapsed-only) if the recipient wants more
  // pending-awareness. Axis-gate previously kept flat projects byte-identical.
  // #58 (PSU = Actions only): no Item-level PSU pill — an Item is never
  // independently marked; it enters the Project Status Update only via a
  // marked Action under it. The Action-level "PSU" pill still renders.
  // Option Y (2026-06-03): Item-level closure proposal. When an open Item's
  // Actions are all complete it carries an Item-level closure_candidate;
  // surface "(Agent proposes closing)" on the title, mirroring the
  // Action-level suffix. The reason is the hover tooltip.
  const itemCandidate = (wi as {
    closure_candidate?: { reason?: string };
  }).closure_candidate;
  if (itemCandidate && wi.status !== "closed") {
    const cc = document.createElement("span");
    cc.className = "item-closure-proposed";
    cc.textContent = " (Agent proposes closing)";
    if (itemCandidate.reason) cc.setAttribute("title", itemCandidate.reason);
    h3.appendChild(cc);
  }
  if (wi.status === "closed") {
    const pill = document.createElement("span");
    const mergedInto = mergeTargetFromReason(wi.closed_reason);
    if (mergedInto) {
      pill.className = "status-pill merged";
      pill.textContent = `Merged → ${mergedInto}`;
    } else {
      pill.className = "status-pill closed";
      pill.textContent = wi.closed_at ? `Closed · ${wi.closed_at}` : "Closed";
    }
    h3.appendChild(document.createTextNode(" "));
    h3.appendChild(pill);
  } else if (wi.status === "acknowledged" || wi.status === "not_relevant") {
    // C5 (2026-07-21): the disposition verbs gray in-session (optimistic echo)
    // like closed does; the next cron then drops them to the Closed Archive's
    // "Acknowledged / Not Tracked" section. No inline date needed.
    const pill = document.createElement("span");
    pill.className = `status-pill ${wi.status === "acknowledged" ? "acknowledged" : "not-relevant"}`;
    pill.textContent = wi.status === "acknowledged" ? "Acknowledged · not tracked" : "Not relevant";
    h3.appendChild(document.createTextNode(" "));
    h3.appendChild(pill);
  }
  // #38: a CLOSED Item that still carries a grouping pointer renders
  // "(was in <group>)" — closed Items aggregate in the Category-level
  // "Recently Closed/Merged" disclosure, not under their group's header, so
  // this suffix is the only place the membership shows. Null-guard: render
  // nothing if the slug doesn't resolve (the stamp windows a group to outlast
  // its members, so a dangling pointer is structurally unlikely).
  if (wi.status === "closed" && wi.subcategory_slug) {
    const grp = subcategories.find((s) => s.slug === wi.subcategory_slug);
    if (grp) {
      const wasIn = document.createElement("span");
      wasIn.className = "muted item-was-in-group";
      wasIn.textContent = ` (was in ${grp.name})`;
      h3.appendChild(wasIn);
    }
  }
  if (SHOW_MOVE_BREADCRUMB && wi.breadcrumb) {
    const bc = renderBreadcrumb(wi.breadcrumb);
    h3.appendChild(bc);
  }
  // #66 PR3: the matter-ledger consult's inline annotation. `absorbed` is a
  // muted breadcrumb (a recurrence was folded in this run); `ambiguous` nudges
  // the recipient toward the existing per-Item Merge-Into-Existing affordance
  // (the item menu already offers work_item_merge / item_merge_into). Inline,
  // in-tier — never a separate panel.
  // N1 (2026-07-14): don't surface the merge proposal on a CLOSED/merged Item —
  // once the recipient accepts (or otherwise closes it), the item folds with a
  // "Recently Merged → into X" treatment and the "possibly part of…" nudge is
  // resolved. The optimistic work_item_merge applier sets status="closed" on the
  // source, so this hides the lingering proposal line the moment Accept lands.
  // #66 D1-D3 (2026-07-23): the dedup consult's overlap proposal shares the
  // banner + the Dismiss with the matter note; the dismiss key gains an
  // `:overlap` suffix when an overlap is present so a NEW overlap signal
  // resurfaces past an older matter-only dismissal.
  const overlapPresent = Boolean(wi.merge_candidate) && wi.status !== "closed";
  const proposalDismissKey = mergeBannerDismissKey(
    workItemKey(threadSlug, wi.slug), overlapPresent,
  );
  const resolvedOverlapTargets = overlapPresent
    ? resolveMergeCandidateTargets(wi.merge_candidate, wi.slug, itemOptions)
    : [];
  if (
    wi.matter_note && wi.status !== "closed"
    // operator 2026-07-14: a session Dismiss (in the ✎ menu) clears this inline
    // note too, not just the banner. Item-level key = the item's target id.
    && !mergeProposalDismissed(proposalDismissKey)
  ) {
    // SURFACE-ONLY: the only matter_note kind is `ambiguous` — a dismissable
    // nudge toward the existing per-Item Merge-Into-Existing affordance.
    // Dogfood feedback 1: name the suggestion target(s) by item NUMBER, not
    // just the matter — the recipient sees exactly which Item to merge into.
    // Resolve the candidate member slugs against the project-wide item list;
    // fall back to the matter name if a target isn't rendered this week.
    const mn = document.createElement("span");
    mn.className = "item-matter-note ambiguous";
    const bySlug = new Map(itemOptions.map((o) => [o.slug, o]));
    const targetLabels = (wi.matter_note.targets ?? [])
      .map((s) => bySlug.get(s))
      .filter((o): o is ItemOption => !!o)
      .map((o) => (o.display_number ? `${o.display_number} ${o.name}` : o.name));
    const hasResolvedTarget = targetLabels.length > 0;
    const hint = hasResolvedTarget
      ? `Possibly part of ${targetLabels.join(", ")}`
      : wi.matter_note.text;
    // N1 (2026-07-14): the inline note flags the proposal in the tree; the
    // one-click Accept/Dismiss lives in the ✎ menu's orange proposal banner
    // (see the mergeProposal spec below + showPicker in forms.ts). When no
    // target resolves this week, fall back to the generic "Merge into…" pointer.
    mn.textContent = hasResolvedTarget
      ? ` ◇ ${hint} — open ✎ to accept or dismiss`
      : ` ◇ ${hint} — place via the item menu → “Merge into…”`;
    mn.title = "The agent suggests this Item may belong with a previously-tracked " +
      "matter. Open the ✎ menu to Accept the merge (pre-filled) or Dismiss the " +
      "suggestion and keep it as a distinct facet.";
    h3.appendChild(mn);
  }
  // #66 D1-D3 (2026-07-23): inline overlap note — the synth's own "overlaps
  // with…" cross-reference tell, surfaced as a dismissable consolidation
  // proposal (md-template parity: briefing-v4.md.j2 "◇ … consider
  // consolidating"). When a counterpart Item resolves, the ✎ banner offers
  // one-click merge; otherwise nudge to the existing menu affordances.
  if (
    wi.merge_candidate && wi.status !== "closed"
    && !mergeProposalDismissed(proposalDismissKey)
  ) {
    const on = document.createElement("span");
    on.className = "item-matter-note overlap";
    on.textContent = resolvedOverlapTargets.length
      ? ` ◇ ${wi.merge_candidate.text} — open ✎ to consolidate or dismiss`
      : ` ◇ ${wi.merge_candidate.text} — review via the item menu → “Merge into…”`;
    on.title = "The agent detected this Item may overlap work tracked elsewhere " +
      "(its narrative cross-references it). Open the ✎ menu to merge the two — " +
      "or Dismiss to keep them separate.";
    h3.appendChild(on);
  }
  header.appendChild(h3);

  // Per-item controls strip: chevron compact toggle + pencil. Mirrors
  // the v3 three-state per-item override:
  //   • no override + currently follows Auto/global → chevron points
  //     opposite of current state (action: override to opposite)
  //   • override active → reset-arrow (action: restore Auto/global)
  // Persists override in localStorage like v3 did, scoped by run +
  // work_item slug. effectiveCompactness combines global mode +
  // override + synth's per-item compact hint to compute the
  // rendered state.
  const controlsRow = document.createElement("div");
  controlsRow.className = "work-item-controls-row";

  // P4d restyle: the report gutter (558 / SUM chips from the Item's report_tags,
  // em-dash when in none) sits at the left of the right-hand controls cluster.
  // B (2026-09-16, PSU→item-only): the Item's OWN PSU mark rides here as a "PSU"
  // chip — the item-header badge Ray asked for. It is UNIVERSAL (a flat
  // quayside/peloro Item can be PSU-marked), so the gutter renders when the Item
  // is axis-eligible OR carries a PSU mark; an unmarked flat Item stays
  // byte-identical (no gutter).
  const itemReportChips = [
    ...(wi.report_tags ?? []).map(reportChipLabel),
    ...(wi.kil_marked ? ["PSU"] : []),
  ];
  if (associationOptions.length > 0 || wi.kil_marked) {
    controlsRow.appendChild(renderReportGutter(itemReportChips));
  }

  const globalMode = readCompactnessMode();
  const override = readCompactnessOverride(runId, wi.slug);
  const isCompact = effectiveCompactness(globalMode, override, wi.compact);
  if (isCompact) wrapper.classList.add("work-item-compact");

  const chevronBtn = document.createElement("button");
  chevronBtn.type = "button";
  chevronBtn.className = "btn-edit work-item-toggle-btn";
  if (override !== null) {
    chevronBtn.innerHTML = RESET_ARROW_SVG;
    chevronBtn.title = "Restore Auto";
    chevronBtn.setAttribute("aria-label", "Restore Auto mode");
    chevronBtn.addEventListener("click", () => {
      writeCompactnessOverride(runId, wi.slug, null);
      // Re-render the whole viewer so the chevron state, content
      // visibility, and any related rows resync from current globals.
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
    });
  } else if (isCompact) {
    chevronBtn.innerHTML = CHEVRON_DOWN_SVG;
    chevronBtn.title = "Expand this Item";
    chevronBtn.setAttribute("aria-label", "Expand this Item");
    chevronBtn.addEventListener("click", () => {
      writeCompactnessOverride(runId, wi.slug, "expanded");
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
    });
  } else {
    chevronBtn.innerHTML = CHEVRON_UP_SVG;
    chevronBtn.title = "Compact this Item";
    chevronBtn.setAttribute("aria-label", "Compact this Item");
    chevronBtn.addEventListener("click", () => {
      writeCompactnessOverride(runId, wi.slug, "compact");
      document.dispatchEvent(new CustomEvent("rerender-briefing"));
    });
  }
  controlsRow.appendChild(chevronBtn);

  // Item-level form controls — Add Action, close/reopen, move-to-
  // Category, assign Owner, KIL toggle, comment, flag next week.
  // B9: gate work_item_close when the Item still has open Actions —
  // gray the button + warn, mirroring the Category→Item gate. The
  // Worker is the authoritative enforcer; this is the first-line UX.
  const itemTargetId = workItemKey(threadSlug, wi.slug);
  const openChildActions = (wi.action_items ?? []).filter(
    (a) => (a.status ?? "open") === "open",
  );
  const openActionCount = openChildActions.length;
  // Cascade-close (Ray 2026-07-21): the open children as cascade targets.
  // Offered ONLY when every open child has an ai_slug (an untargetable child
  // would strand the composite mid-way — fall back to the greyed close +
  // notice, the pre-cascade behavior).
  const cascadeOpenActions = openChildActions
    .filter((a) => a.ai_slug)
    .map((a) => ({
      target_id: actionItemKey(threadSlug, wi.slug, a.ai_slug!),
      label: `${a.display_number ? a.display_number + " " : ""}${
        a.text?.trim() || a.what?.trim() || a.ai_slug!}`,
    }));
  const cascadeCloseAvailable = wi.status !== "closed"
    && openActionCount > 0
    && cascadeOpenActions.length === openActionCount;
  // #38: the parent Category's groups for this Item's move picker + the
  // ≥1-open-group gate. `subcategoryOptions` spans open+closed (the add-
  // collision set; the move form filters to open). The picker pre-selects the
  // Item's current group.
  const openGroups = subcategories.filter((s) => s.status !== "closed");
  // N1 (2026-07-14): the agent's "Possibly part of X" matter suggestion, resolved
  // to the open Items rendered this week (a slug not in itemOptions — target not
  // open/rendered, or self — is dropped). Drives the orange Accept/Dismiss proposal
  // banner (showPicker in forms.ts); ≥2 candidates → the banner offers a dropdown.
  const resolvedMatterTargets =
    wi.matter_note?.kind === "ambiguous" && wi.status !== "closed"
      // dedup slugs (the consult can flatten the same open member across two
      // candidate matters) so a lone shared target renders as one option, not a
      // duplicated dropdown row.
      ? [...new Set((wi.matter_note.targets ?? []).filter((slug) => slug && slug !== wi.slug))]
          .map((slug) => {
            const opt = itemOptions.find((o) => o.slug === slug);
            return opt
              ? {
                  slug,
                  label: opt.display_number
                    ? `${opt.display_number} ${opt.name}`
                    : opt.name,
                }
              : null;
          })
          .filter((t): t is { slug: string; label: string } => t !== null)
      : [];
  // #66 D1-D3 (2026-07-23): one merged banner — overlap targets FIRST (the
  // synth's own narrative tell is the stronger, more specific signal than
  // ledger ambiguity), then matter targets, deduped by slug.
  const mergeTargets = [...resolvedOverlapTargets];
  for (const t of resolvedMatterTargets) {
    if (!mergeTargets.some((m) => m.slug === t.slug)) mergeTargets.push(t);
  }
  controlsRow.appendChild(renderFormControls({
    runId,
    targetType: "work_item",
    targetId: itemTargetId,
    kinds: kindsForItemV4(
      wi.status === "closed",
      Boolean((wi as { closure_candidate?: unknown }).closure_candidate),
      openGroups.length > 0,
      // #73: ≥1 OTHER open Item exists to fold this one up into.
      itemOptions.some((o) => o.slug !== wi.slug),
      cascadeCloseAvailable,
      associationOptions.length > 0, // P4d-1b hasAxis
      // E-5: ≥1 open cascade-targetable action → offer "Set priority".
      (wi.action_items ?? []).some((a) => a.status !== "closed" && a.ai_slug),
      // KIL-POINT: 558-relevant → offer "Set KIL Point". Gated on ≥1 OPEN 558-tagged
      // action (558 is action-keyed), or a Point already set so an existing one can
      // be edited/cleared. NOTE (ultra #503 finding 1): an Item on the 558 via a
      // CLOSED-only 558 action can't be reached here — the pipeline strips closed
      // actions from the blob (strip_closed_render_nodes), so the gate can't see it.
      // Fixing that needs an item-level 558-membership flag on the blob → BACKLOG
      // KIL-POINT-CLOSED-ONLY-558.
      (wi.action_items ?? []).some(
        (a) => a.status !== "closed" && (a.report_tags ?? []).includes("558"),
      ) || Boolean(wi.point),
    ),
    submitted: fb.get(itemTargetId) ?? [],
    participantOptions,
    threadOptions,
    itemOptions,
    associationOptions, // P4d-1b: option set for item_set_associations
    // KIL-POINT (Ray 2026-10-28): the project's distinct existing Points, for the
    // "Set KIL Point" <datalist> autocomplete — a recipient reuses the exact
    // string ("Scale / KAMM") instead of a typo variant that fragments the 558.
    pointOptions,
    subProjectOptions, // B3: registry for the move picker's Sub-project pre-filter
    currentSubProjectSlug, // B3: default the pre-filter to this Item's sub-project
    subcategoryOptions: subcategories.map((s) => ({
      slug: s.slug, name: s.name, closed: s.status === "closed",
    })),
    // REPORT-TAG-ONE-CLICK (2026-10-18): the one-click PSU pill (item-keyed —
    // wi.kil_marked, B/2026-09-16). Open-only. The Item's bulk "all actions → KIL"
    // cascade stays on the item_report_inclusion form below (operator: keep it).
    reportToggle: itemReportToggle(wi),
    // Slice 2 (2026-09-09): the Item's open Actions (with report membership +
    // targetable id) for the item_report_inclusion cascade. The form fans out
    // action_set_report_tags / action_kil_mark over these at submit.
    reportCascadeActions: (wi.action_items ?? [])
      .filter((a) => a.status !== "closed" && a.ai_slug)
      .map((a) => ({
        target_id: actionItemKey(threadSlug, wi.slug, a.ai_slug!),
        kil_marked: Boolean(a.kil_marked),
        report_tags: a.report_tags ?? [],
      })),
    // A2 (Ray 2026-09-15): the Item's open Actions for the item_set_associations
    // CASCADE. The item pencil "Set associations" fans out one
    // action_set_associations per open Action at submit (collectItemAssociationCascade),
    // REPLACE-WHOLE, so setting at the Item tier propagates to all its Actions
    // and the Item chip (a pure rollup, itemAssociationUnion) then reflects it.
    associationCascadeActions: (wi.action_items ?? [])
      .filter((a) => a.status !== "closed" && a.ai_slug)
      .map((a) => ({
        target_id: actionItemKey(threadSlug, wi.slug, a.ai_slug!),
        association_tags: a.association_tags ?? [],
      })),
    // E-5 (Ray 2026-09-16): the Item's open Actions for the item_set_priority
    // CASCADE. The item pencil "Set priority" fans out one action_priority_change
    // per open Action whose priority differs (collectItemPriorityCascade).
    priorityCascadeActions: (wi.action_items ?? [])
      .filter((a) => a.status !== "closed" && a.ai_slug)
      .map((a) => ({
        target_id: actionItemKey(threadSlug, wi.slug, a.ai_slug!),
        priority_status: a.priority_status ?? "Active",
      })),
    // Pre-fill "Rename Item" with the current name to edit; pre-select the
    // Item's current group on the Move-to-Sub-category picker.
    // R2-3: prefill "Edit narrative" with the Item's current narrative.
    current: {
      to_name: wi.name,
      to_subcategory_slug: wi.subcategory_slug ?? "",
      narrative: wi.narrative ?? "",
      // KIL-POINT (Ray 2026-10-28): prefill "Set KIL Point" with the Item's current
      // Point (PREFILL_KINDS copies spec.current.point → the field value) so it's an
      // EDIT, not a blank. Replace-whole (like set_narrative); blank submit clears it.
      point: wi.point ?? "",
      // P1r: pre-check "Include in client reports" with the Item's current set
      // (comma-joined — spec.current is string-typed; forms.ts splits it). A
      // REPLACE-WHOLE multiselect, so pre-checking is load-bearing: without it a
      // submit would clear tags the recipient never touched.
      report_tags: (wi.report_tags ?? []).join(","),
      // A2 (Ray 2026-09-15): pre-check "Associations" with the Item's ROLLUP =
      // the union of its OPEN Actions' association_tags (itemAssociationUnion is
      // the render-time equivalent). REPLACE-WHOLE, so pre-checking is
      // load-bearing: submitting cascades this set onto every open Action
      // (collectItemAssociationCascade). The item no longer carries its own tag —
      // it is a pure rollup of its Actions (Ray 2026-09-15: "the item reports the
      // accumulation of active actions").
      association_tags: (() => {
        const seen = new Set<string>();
        for (const a of wi.action_items ?? []) {
          if (a.status === "closed") continue;
          for (const s of a.association_tags ?? []) seen.add(s);
        }
        return [...seen].join(",");
      })(),
      // Slice 2 (2026-09-09) → B (2026-09-16, PSU→item-only): pre-check the
      // item_report_inclusion boxes. "558" (a cascade) is checked when EVERY open
      // Action is already in the Key Issue Log; "psu" (the ITEM's own mark) is
      // checked from `wi.kil_marked` directly — no longer an all-actions rollup.
      report_inclusion: (() => {
        const open = (wi.action_items ?? []).filter((a) => a.status !== "closed" && a.ai_slug);
        const sel: string[] = [];
        if (open.length > 0 && open.every((a) => (a.report_tags ?? []).includes("558"))) sel.push("558");
        if (wi.kil_marked) sel.push("psu");
        return sel.join(",");
      })(),
      // B: the Item's OWN PSU mark, for collectItemReportCascade to diff against
      // (mirrors the action form's spec.current.kil_marked).
      kil_marked: wi.kil_marked ? "1" : "",
      // N1 (2026-07-14): default the Merge-into picker to the FIRST resolved
      // target, so opening the form directly (or the banner Accept with a
      // single candidate) lands on the proposed primary. The banner overrides
      // this with the recipient's dropdown choice when there are ≥2 candidates.
      merge_primary_item: mergeTargets[0]?.slug,
    },
    // N1 (2026-07-14): the agent's merge suggestion(s), surfaced as an orange
    // Accept/Dismiss proposal banner at the TOP of this Item's ✎ menu (showPicker
    // in forms.ts) — mirroring the closure_candidate affordance. Carries EVERY
    // resolved candidate so the banner offers a dropdown (default the first) when
    // more than one resolved. #66 D1-D3: `reason` = the overlap tell rendered as
    // a muted line under the banner lead; `review` gates one-click auto-submit
    // to a visible prefilled form when folding a multi-action Item (whole-Item
    // fold scope guard — the accept merges EVERY action this Item carries).
    mergeProposal: mergeTargets.length
      ? {
          targets: mergeTargets,
          reason: resolvedOverlapTargets.length
            ? wi.merge_candidate?.text
            : undefined,
          review: resolvedOverlapTargets.length > 0 && openActionCount > 1,
        }
      : undefined,
    // #66 D1-D3: overlap-aware dismiss key (shared by the inline notes above).
    mergeProposalDismissKey: proposalDismissKey,
    // A1: label for the optimistic "Recently Merged → into X" treatment.
    mergeTargetLabel: `${wi.display_number ? wi.display_number + " " : ""}${wi.name}`,
    // Decision 3.6: former path captured for the move breadcrumb.
    movingFromDisplayNumber: wi.display_number,
    // B10: a done-but-misfiled Item (0 open Actions) can Move & Close in one
    // step via the Move-to-Category checkbox; gated here.
    itemCloseable: openActionCount === 0,
    // Cascade-close: the open children the composite will close. When the
    // cascade is offered the greyed-close `notice` is dropped (the dead-end
    // it explained no longer exists); the notice remains as the fallback for
    // the rare un-targetable-child case (an open child without an ai_slug).
    cascadeOpenActions: cascadeCloseAvailable ? cascadeOpenActions : undefined,
    notice: !cascadeCloseAvailable && openActionCount > 0 && wi.status !== "closed"
      ? `Close ${openActionCount} open action${openActionCount === 1 ? "" : "s"} before closing this Item.`
      : undefined,
  }));
  header.appendChild(controlsRow);

  // Item summary + responsible + started ride INSIDE the header div
  // (not as siblings after it). This keeps them visually attached to
  // the Item card and lets `.work-item-header > .work-item-summary`
  // CSS govern the spacing rhythm — matches the demo reference where
  // the Item title and its meta sit in the same bordered segment.
  if (wi.narrative) {
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.narrative;
    header.appendChild(p);
  } else if (wi.summary) {
    // v3 back-compat: payload may carry summary instead of narrative.
    const p = document.createElement("p");
    p.className = "work-item-summary";
    p.textContent = wi.summary;
    header.appendChild(p);
  }

  // Item meta line — Responsible + Started joined on one line with
  // `·` separators, matching the Action-meta pattern. Was rendered
  // as separate <p> elements until v0.4.7 — operator caught the
  // regression vs the demo-preview where this is single-line.
  const itemMetaBits: string[] = [];
  if (wi.responsible_party) {
    let rpText = `Responsible: ${wi.responsible_party.name}`;
    if (wi.responsible_party.source === "default_pm") rpText += " (PM default)";
    itemMetaBits.push(rpText);
  }
  const startDate = wi.start_date ?? wi.first_seen;
  if (startDate) {
    itemMetaBits.push(`Started ${startDate}`);
  }
  // KIL-POINT (Ray 2026-10-28): echo the Item's current 558 "Point" (responsible
  // firm/s) as a quiet meta bit when set — the in-session confirmation surface for
  // an item_set_point edit (the optimistic echo writes wi.point). Nothing renders
  // when unset, so a no-Point Item's meta line stays byte-identical.
  if (wi.point && wi.point.trim()) {
    itemMetaBits.push(`Point: ${wi.point.trim()}`);
  }
  if (itemMetaBits.length) {
    const meta = document.createElement("p");
    meta.className = "work-item-meta";
    meta.textContent = itemMetaBits.join(" · ");
    header.appendChild(meta);
  }

  wrapper.appendChild(header);

  const openActions = (wi.action_items ?? [])
    .filter((a) => a.status !== "closed")
    .filter(lensOpts?.actionFilter ?? (() => true));
  const closedActions = lensOpts?.hideClosedActions ? [] : (wi.action_items ?? [])
    .filter((a) => a.status === "closed");

  if (openActions.length) {
    // Demo-reference wrapper: each sub-area under an Item (Actions,
    // Open Questions, Recently Closed Actions) gets its own dashed-
    // top-border section with an uppercase label. Adds the missing
    // "ACTIONS" divider that v4 inherited without — operators couldn't
    // tell where the Item meta ended and the action list began.
    const actionsSection = document.createElement("div");
    actionsSection.className = "actions-section";

    const labelRow = document.createElement("div");
    labelRow.className = "actions-section-header";
    const actionsLabel = document.createElement("h4");
    actionsLabel.className = "section-label";
    actionsLabel.textContent = "Actions";
    labelRow.appendChild(actionsLabel);

    // Slice 2 (2026-09-09): item-level report inclusion is offered as the Item
    // pencil's "Report inclusion" control (kindsForItemV4 → item_report_inclusion),
    // whose "all actions → Key Issue Log / PSU" checkboxes cascade over the Item's
    // Actions at submit — NOT a free-floating header button. See forms.ts.
    actionsSection.appendChild(labelRow);

    const list = document.createElement("ul");
    list.className = "actions-list";
    // B8 cross-Item action_merge: the merge picker now offers project-wide
    // open Actions (passed down from renderBriefingViewer), not just this
    // Item's siblings. The form excludes the target and labels each option
    // with its parent Item so the recipient sees where it comes from; the
    // submit handler re-parents any cross-Item pick before merging.
    const actionOptions = allActionOptions;
    // Sort by priority enum order — Critical first, On Hold last.
    // Within each priority group, sort by display_number ASC so
    // 3.6.1 renders before 3.6.2.
    for (const priority of PRIORITY_ORDER) {
      const group = openActions
        .filter((a) => ((a.priority_status as PriorityStatus | undefined) ?? "Active") === priority)
        .sort((a, b) => {
          const dn = (s: string | undefined) => {
            if (!s) return 1e9;
            const tail = s.split(".").pop();
            return tail ? parseInt(tail, 10) || 1e9 : 1e9;
          };
          return dn(a.display_number) - dn(b.display_number);
        });
      for (const action of group) {
        list.appendChild(renderActionV4(
          runId, threadSlug, wi.slug, action, fb,
          participantOptions, threadOptions, actionOptions, itemOptions,
          associationOptions, subProjectOptions, currentSubProjectSlug,
        ));
      }
    }
    actionsSection.appendChild(list);
    wrapper.appendChild(actionsSection);
  }

  // Open Questions render INLINE on each Action now (renderActionV4), not in a
  // separate grouped sub-section (2026-06-28 operator: in-tier + actionable).

  // Recently Closed Actions sub-section (Decision 2.2). A1: split out
  // merged Actions ("Recently Merged") from plain closes.
  const mergedActions = closedActions.filter((a) => isMergedClosure(a.closed_reason));
  const plainClosedActions = closedActions.filter((a) => !isMergedClosure(a.closed_reason));
  if (mergedActions.length) {
    wrapper.appendChild(renderRecentlyClosedActions(
      runId, threadSlug, wi.slug, mergedActions, fb,
      participantOptions, threadOptions, "Recently Merged Actions",
    ));
  }
  if (plainClosedActions.length) {
    wrapper.appendChild(renderRecentlyClosedActions(
      runId, threadSlug, wi.slug, plainClosedActions, fb,
      participantOptions, threadOptions, "Recently Closed Actions",
    ));
  }

  return wrapper;
}

function renderActionV4(
  runId: string,
  threadSlug: string,
  workItemSlug: string,
  action: AnyItem,
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  actionOptions: ActionOption[] = [],
  itemOptions: ItemOption[] = [],
  // P4d-1b: association axis {value,label} for the action_set_associations
  // editor. Empty on a flat project → the kind is gated off.
  associationOptions: Array<{ value: string; label: string }> = [],
  // B3-merge: sub-project registry + this Action's current sub-project (its
  // parent Category's), for the Action-merge Sub-project → Item pre-filter.
  // Empty registry on a flat project → no pre-filter (byte-identical).
  subProjectOptions: Array<{ value: string; label: string }> = [],
  currentSubProjectSlug?: string | null,
): HTMLElement {
  const priority = (action.priority_status as PriorityStatus | undefined) ?? "Active";
  const li = document.createElement("li");
  li.className = `action-row ${priorityClass(priority)}`;
  if (action.status === "closed") li.classList.add("closed");
  if (action.is_new) li.classList.add("is-new");

  const head = document.createElement("div");
  head.className = "action-row-head";

  const text = document.createElement("div");
  text.className = "action-text";
  if (action.display_number) {
    const num = document.createElement("span");
    num.className = "path-num";
    num.textContent = action.display_number;
    text.appendChild(num);
  }
  // P4d restyle: inline association state on the row — placed tags as solid .atag
  // chips, an unconfirmed proposer suggestion as ghost `?` chips (click-to-confirm),
  // else the ⚑ Needs-association marker. Gated on associationOptions (empty on a
  // flat project → nothing renders, the flat <main> stays byte-identical). Closed
  // Actions don't carry the axis affordance.
  if (associationOptions.length > 0 && action.status !== "closed") {
    const placed = action.association_tags ?? [];
    if (placed.length) {
      for (const slug of placed) text.appendChild(renderAssocTagChip(slug));
    } else {
      const cand = action.association_candidate;
      const acTargetId = action.ai_slug
        ? actionItemKey(threadSlug, workItemSlug, action.ai_slug)
        : null;
      if (cand && (cand.associations?.length ?? 0) > 0 && acTargetId) {
        const srcLabel = proposedSourceLabel(cand);
        for (const slug of cand.associations) {
          const chip = renderAssocTagChip(slug, { ghost: true });
          chip.title = "Agent-inferred — click to confirm or dismiss";
          chip.addEventListener("click", (e) => {
            e.stopPropagation();
            openAssocGhostPopover(chip, {
              runId, targetId: acTargetId, slug, sourceLabel: srcLabel, chip,
              // C3: on "Dismiss", chain into the inline picker (same axis the ⚑
              // Needs-association flag uses just below).
              associationOptions,
            });
          });
          text.appendChild(chip);
        }
      } else {
        // W2 ASSOC-ASSIGN-UX: the ⚑ flag is a one-click picker when the Action is
        // targetable (has an ai_slug) — click → a popover of the project's associations →
        // one-click set (action_set_associations). An untargetable Action (no ai_slug)
        // keeps the plain, non-interactive flag (mirrors the ghost branch's acTargetId gate).
        const chip = renderNeedsAssocChip("⚑ Needs association");
        if (acTargetId) {
          chip.classList.add("pickable");
          chip.title = "Click to set the association";
          chip.addEventListener("click", (e) => {
            e.stopPropagation();
            openNeedsAssocPopover(chip, {
              runId, targetId: acTargetId, options: associationOptions,
            });
          });
        } else {
          chip.classList.add("flat");
        }
        text.appendChild(chip);
      }
    }
  }
  const strong = document.createElement("strong");
  // Defensive against synth emitting an empty `what`/`text`. Falls back
  // to the narrative's first line, then a `(no title)` marker. The
  // agent-side Pydantic `min_length=1` constraint should prevent this
  // from reaching us in normal runs; this is a render-time safety net
  // for pre-constraint state-repo data + future regressions.
  const fallbackTitle = (() => {
    if (action.text && action.text.trim()) return action.text;
    if (action.what && action.what.trim()) return action.what;
    if (action.narrative && action.narrative.trim()) {
      const firstLine = action.narrative.split(/[.\n]/)[0]?.trim();
      if (firstLine) return firstLine;
    }
    return "(no title)";
  })();
  strong.textContent = fallbackTitle;
  text.appendChild(strong);
  appendNewPill(text, action.is_new);
  appendUpdatedPill(text, action.is_updated);

  if ((action.open_questions_v4 ?? []).some((q) => !q.answer)) {
    const q = document.createElement("span");
    q.className = "action-question-badge";
    q.textContent = "❓";
    q.setAttribute("title", "Has an unanswered question (shown below)");
    text.appendChild(q);
  }
  // Slice 2 follow-up (2026-09-09, operator): report membership (PSU + Key Issue
  // Log) always shows as OUTLINED chips in the right-hand report gutter next to the
  // ✎ — on flat projects too, now that report inclusion applies to them. The old
  // flat-only full-color inline PSU badge (appended to the title) is retired.
  // Decision 12.8 (2026-05-28): closure_candidate title suffix.
  // Mirrors the (inferred) pattern on Categories — muted span
  // appended to the title with a leading space; hover tooltip
  // shows the synth's grounded reason. Recipient/operator acts via
  // the popover (action_close / action_dismiss_closure_candidate).
  const candidate = (action as { closure_candidate?: { reason?: string } })
    .closure_candidate;
  if (candidate) {
    const cc = document.createElement("span");
    cc.className = "action-closure-proposed";
    cc.textContent = " (Agent proposes closing)";
    if (candidate.reason) cc.setAttribute("title", candidate.reason);
    text.appendChild(cc);
  }
  // Decision 3.6 breadcrumb on a recently moved Action — mirrors the Item
  // header crumb (briefings.ts ~2999) one tier down. The agent sets it on
  // the payload node for 2 weeks after an action_move_to_item.
  if (SHOW_MOVE_BREADCRUMB && action.breadcrumb) {
    const bc = renderBreadcrumb(action.breadcrumb);
    text.appendChild(bc);
  }
  // #66 D1-D3 (2026-07-23): cross-source overlap note on an Action — inline
  // advisory only in v1 (md-template parity: "◇ … consider consolidating").
  // Consolidation runs through the existing ✎ menu "Merge into…" / "Move to
  // another Item" affordances; an Action↔Item pair isn't directly mergeable,
  // so no one-click here. #66 D4 (code review 2026-09-29): a JUDGE-sourced dup is
  // suppressed here (showsInlineActionOverlap) — see that predicate.
  if (showsInlineActionOverlap(action)) {
    const on = document.createElement("span");
    on.className = "action-overlap-note";
    // non-null: showsInlineActionOverlap already required merge_candidate.
    on.textContent = ` ◇ ${action.merge_candidate!.text} — consolidate via ✎ ` +
      "“Merge into…” / “Move to another Item”";
    on.title = "The agent detected this Action may overlap work tracked " +
      "elsewhere (its text cross-references it).";
    text.appendChild(on);
  }
  head.appendChild(text);

  const controls = document.createElement("div");
  controls.className = "action-controls";
  const pill = document.createElement("span");
  pill.className = `action-status-pill ${priorityClass(priority)}`;
  if (action.status === "closed") {
    const mergedInto = mergeTargetFromReason(action.closed_reason);
    if (mergedInto) {
      pill.className = "action-status-pill merged";
      pill.textContent = `Merged → ${mergedInto}`;
      pill.title = `Merged → ${mergedInto}`;
    } else {
      pill.className = "action-status-pill closed";
      pill.textContent = action.closed_at
        ? `Closed · ${action.closed_at}`
        : "Closed";
    }
  } else if (action.status === "acknowledged" || action.status === "not_relevant") {
    // C5 (2026-07-21): disposition verbs gray in-session (optimistic echo); the
    // Action drops to the Closed Archive "Acknowledged / Not Tracked" next cron.
    pill.className = `action-status-pill ${action.status === "acknowledged" ? "acknowledged" : "not-relevant"}`;
    pill.textContent = action.status === "acknowledged" ? "Acknowledged" : "Not relevant";
  } else if (priority === "Active") {
    // Ray 2026-09-29 declutter ("remove the green Active tag; keep yellow Delay /
    // red Critical + the left indicator"): Active is the default state, so its
    // green pill is pure noise. Drop the colour class + text, keeping an empty
    // pill span so the report gutter stays column-aligned. Delay/Critical/On Hold
    // still render below.
    pill.className = "action-status-pill";
  } else {
    pill.textContent = priority;
  }
  // Slice 2 follow-up (2026-09-09, operator): the priority pill sits in its own
  // fixed column, then the report gutter (outlined PSU + Key Issue Log chips, or an
  // em-dash when in no report) rides immediately left of the ✎ — so the report tag
  // is next to the pencil and the priority never shifts on whether a report tag is
  // present. Rendered on ALL projects (flat + axis) now that report inclusion is
  // universal; the gutter's `min-width` keeps the pill column-aligned either way.
  controls.appendChild(pill);
  // B (2026-09-16, PSU→item-only): actions carry only their Key Issue Log (558)
  // chip — the PSU is item-keyed now and its badge rides the ITEM header, not the
  // action gutter.
  const reportChips = (action.report_tags ?? []).map(reportChipLabel); // 558 → "KIL"
  controls.appendChild(renderReportGutter(reportChips));

  // Real form controls — picker shows priority change, reason/impact/
  // off-hold setters, due edit, question add/resolve, KIL toggle,
  // and invoice ops on aggregate Actions.
  // Decision 3A: priority-aware filter — pass action.priority_status
  // into kindsForActionV4 so Active doesn't show off_hold_condition
  // etc. Decision 3B: populatedFields tells the form which "Add" vs
  // "Edit" label variant to use.
  if (action.ai_slug) {
    const actionTargetId = actionItemKey(threadSlug, workItemSlug, action.ai_slug);
    const populated = new Set<string>();
    if (action.reason && action.reason.trim()) populated.add("reason");
    if (action.impact && action.impact.trim()) populated.add("impact");
    if (action.off_hold_condition && action.off_hold_condition.trim()) {
      populated.add("off_hold_condition");
    }
    if (action.due && action.due.trim()) populated.add("due");
    const invoiceOptions = action.action_type === "invoices_aggregate"
      ? (action.invoices ?? []).map((inv) => ({
          number: inv.number,
          label: [
            inv.number,
            inv.amount != null ? `$${inv.amount.toFixed(2)}` : null,
            inv.status,
          ].filter(Boolean).join(" · "),
        }))
      : undefined;
    controls.appendChild(renderFormControls({
      runId,
      targetType: "action_item",
      targetId: actionTargetId,
      kinds: kindsForActionV4(
        (action.open_questions_v4 ?? []).length > 0,
        action.action_type === "invoices_aggregate",
        Boolean(action.kil_marked),
        action.priority_status ?? null,
        action.status === "closed" ? "closed" : "open",
        Boolean(
          (action as { closure_candidate?: unknown }).closure_candidate,
        ),
        // B1 / #73: ≥1 OTHER open Action exists project-wide to fold this one
        // up into. actionOptions is the project-wide open-Action set (the same
        // list feeding the forward action_merge picker), self excluded by id.
        actionOptions.some(
          (o) => (o.target_id ?? o.ai_slug) !== actionTargetId,
        ),
        associationOptions.length > 0, // P4d-1b hasAxis
      ),
      submitted: fb.get(actionTargetId) ?? [],
      participantOptions,
      threadOptions,
      invoiceOptions,
      actionOptions,
      associationOptions, // P4d-1b: option set for action_set_associations
      subProjectOptions, // B3-merge: registry for the Action-merge pre-filter
      currentSubProjectSlug, // B3-merge: default the pre-filter to this Action's sub-project
      // #52b: the action_open_question_resolve picker resolves a question by its
      // stable QID (survives the synth's run-to-run re-wording); the value is
      // prefixed `qid:<hex>` (or `text:<text>` for a legacy question without a
      // qid yet), split back apart in reshapePayloadForKind. Label stays the
      // question text. Only unanswered questions are offered.
      openQuestionOptions: (action.open_questions_v4 ?? [])
        .filter((q) => !q.answer)
        .map((q) => ({
          value: q.qid ? `qid:${q.qid}` : `text:${q.text}`,
          label: q.text,
        })),
      // B8: project-wide Item list for the action_move_to_item picker.
      itemOptions,
      priorityStatus: action.priority_status,
      populatedFields: populated,
      // A1: label for the optimistic "Recently Merged → into X" treatment.
      mergeTargetLabel: `${action.display_number ? action.display_number + " " : ""}${action.text ?? action.what ?? ""}`,
      // Decision 3.6: former path captured for the move breadcrumb.
      movingFromDisplayNumber: action.display_number,
      // REPORT-TAG-ONE-CLICK (2026-10-18): the one-click KIL (558) pill
      // (action-keyed — report_tags ∋ 558). Open-only. Replaces the former
      // action_report_inclusion sub-form (dropped from kindsForActionV4).
      reportToggle: actionReportToggle(action),
      // Pre-fill the edit forms (reason / impact / off-hold / due, and the
      // priority form's metadata) with the Action's current values so the
      // recipient edits what's there rather than starting blank.
      current: {
        reason: action.reason ?? undefined,
        impact: action.impact ?? undefined,
        off_hold_condition: action.off_hold_condition ?? undefined,
        due: action.due ?? undefined,
        // B6: pre-fill "Rename Action" with the current text to edit.
        to_text: action.text ?? action.what ?? undefined,
        // #76: pre-fill "Promote to Item" with the Action text as the new
        // Item's default name (the recipient edits or keeps it).
        name: action.text ?? action.what ?? undefined,
        // R2-3: pre-fill "Edit narrative" with the Action's current narrative.
        narrative: action.narrative ?? "",
        // P4d-1b: pre-check "Associations" with the Action's current set
        // (REPLACE-WHOLE; forms.ts splits the comma-join).
        association_tags: (action.association_tags ?? []).join(","),
        // Slice 2 (2026-09-09): the consolidated "Report inclusion" control
        // pre-checks PSU (kil_marked) + Key Issue Log (report_tags ∋ 558).
        report_tags: (action.report_tags ?? []).join(","),
        kil_marked: action.kil_marked ? "1" : "",
      },
    }));
  }
  head.appendChild(controls);

  li.appendChild(head);

  // Narrative + priority metadata + meta line, in order matching
  // the markdown template.
  //
  // Decision 12.6 (2026-05-28, client feedback #2): narrative is a
  // base detail kept visible in both compact and expanded views.
  // Reason / impact / off_hold_condition are "extended detail" —
  // visible only in expanded view. The `.action-extended` class
  // tags them so the compact-mode CSS rule can hide them surgically
  // without affecting the narrative.
  if (action.narrative) {
    const n = document.createElement("p");
    n.className = "action-detail action-narrative";
    n.textContent = action.narrative;
    li.appendChild(n);
  }
  const detailPairs: Array<[string, string]> = [];
  if (action.reason) detailPairs.push(["Reason", action.reason]);
  if (action.impact) detailPairs.push(["Impact", action.impact]);
  if (action.off_hold_condition) {
    detailPairs.push(["Off-hold when", action.off_hold_condition]);
  }
  for (const [label, value] of detailPairs) {
    const p = document.createElement("p");
    p.className = "action-detail action-extended";
    const b = document.createElement("b");
    b.textContent = `${label}:`;
    p.appendChild(b);
    p.appendChild(document.createTextNode(` ${value}`));
    li.appendChild(p);
  }

  // Meta line: Due · Responsible · Started (Decision 2.7). #59: due bit via
  // the shared dueMetaBit helper ("Due date: TBD" when an open Action has none).
  const metaBits: string[] = [];
  const dueBit = dueMetaBit(priority, action.due, action.status);
  if (dueBit) metaBits.push(dueBit);
  const responsibleName = action.responsible_party?.name ?? action.assignee;
  if (responsibleName) metaBits.push(`Responsible: ${responsibleName}`);
  if (action.first_seen) metaBits.push(`Started ${action.first_seen}`);
  if (metaBits.length) {
    const meta = document.createElement("div");
    meta.className = "action-meta";
    meta.textContent = metaBits.join(" · ");
    li.appendChild(meta);
  }

  // P4d-2 — the agent-inferred association suggestion. As of the 2026-09-07
  // "Lensed Briefing" restyle this renders INLINE in the row head as a ghost `?`
  // chip (a dashed .atag) that opens a Confirm/Dismiss popover, NOT the separate
  // "Suggested association:" block that used to live here. See the association
  // cluster near the top of this function (renderAssocTagChip { ghost } +
  // openAssocGhostPopover); it reuses the same confirmProposedAssociations /
  // dismissProposedAssociation handlers.

  // Open Questions — rendered INLINE at the BOTTOM of the Action (operator
  // 2026-07-23): the item/action details above (narrative, reason/impact, dates)
  // come FIRST, THEN the question block. A question "is an action" (B5) and the
  // client wants signals in-tier. The resolve control also lives on the ✎ menu
  // (kindsForActionV4 → action_open_question_resolve).
  const questions = action.open_questions_v4 ?? [];
  if (questions.length > 0) {
    // N2/#117 — the Accept button targets this Action; null when it has no
    // ai_slug (then no proposed-answer actions — it can't be resolved).
    const oqActionTargetId = action.ai_slug
      ? actionItemKey(threadSlug, workItemSlug, action.ai_slug)
      : null;
    const qWrap = document.createElement("div");
    qWrap.className = "action-detail action-open-questions";
    for (const q of questions) {
      const row = document.createElement("div");
      row.className = `action-open-question${q.answer ? " answered" : ""}`;

      // Q line — the question with WHO asked it inline (provenance paired to its
      // own line, never orphaned below the answer).
      const qLine = document.createElement("p");
      qLine.className = "oq-line oq-q";
      const qMark = document.createElement("span");
      qMark.className = "oq-mark";
      qMark.textContent = "Q";
      qLine.appendChild(qMark);
      const qBody = document.createElement("span");
      qBody.appendChild(document.createTextNode(q.text));
      const askedBy = askedByMeta(q.raised_by, q.raised_at);
      if (askedBy) {
        const s = document.createElement("span");
        s.className = "oq-inline-meta";
        s.textContent = ` · ${askedBy}`;
        qBody.appendChild(s);
      }
      qLine.appendChild(qBody);

      // Per-question ✎ (client 2026-08-12). Every disposition of THIS question
      // lives here — Answer it, or Dismiss it without answering — rather than on
      // the parent Action's menu behind a "which question" picker. The pencil is
      // the same idiom Categories / Items / Actions already use, one tier down,
      // so nothing new has to be learned.
      //
      // Only for an unanswered question that still has a stable identity: an
      // answered one is already disposed of, and without a qid neither form can
      // name its target (the reconciler mints qids, so this is defensive).
      if (!q.answer && q.qid && oqActionTargetId) {
        const qControls = renderFormControls({
          runId,
          targetType: "action_item",
          targetId: oqActionTargetId,
          kinds: ["action_open_question_resolve", "action_open_question_dismiss"],
          questionContext: { qid: q.qid, text: q.text },
          // Filter to THIS question's events. Every per-question pencil on the
          // Action shares the Action's targetId, so an unfiltered list would
          // cross-show one question's chips and badge count on all of them —
          // the same trap the executive-summary control documents above.
          submitted: (fb.get(oqActionTargetId) ?? []).filter(
            (e) => (e.payload as Record<string, unknown> | undefined)?.qid === q.qid,
          ),
        });
        qControls.classList.add("oq-controls");
        qLine.appendChild(qControls);
      }
      row.appendChild(qLine);

      // Already-answered → the recorded answer (A with a check).
      if (q.answer) {
        const aLine = document.createElement("p");
        aLine.className = "oq-line oq-a";
        const aMark = document.createElement("span");
        aMark.className = "oq-mark oq-check";
        aMark.textContent = "✓";
        aLine.appendChild(aMark);
        const aBody = document.createElement("span");
        aBody.textContent = q.answer;
        aLine.appendChild(aBody);
        row.appendChild(aLine);
      }

      // N2/#117 — a confident, source-grounded PROPOSED answer to an unanswered
      // question. Calm treatment (orange only on the "proposed" tag + actions),
      // with the deterministic source citation, a one-click Accept (submits the
      // existing action_open_question_resolve — kept as a ONE-CLICK shortcut
      // even though the ✎ also offers it, because it is the recipient's most
      // frequent positive action and routing it through a menu would make his
      // commonest interaction slower) and a localStorage-only Hide suggestion.
      const rc = q.resolution_candidate;
      const rcQid = q.qid;
      const dismissKey = rcQid ? `${oqActionTargetId ?? ""}:${rcQid}` : null;
      if (!q.answer && rc && rc.answer && rcQid && oqActionTargetId && dismissKey
          && !proposedAnswerDismissed(dismissKey)) {
        const rcAnswer = rc.answer;
        const rcTarget = oqActionTargetId;
        const dKey = dismissKey;

        const aLine = document.createElement("p");
        aLine.className = "oq-line oq-a";
        const aMark = document.createElement("span");
        aMark.className = "oq-mark";
        aMark.textContent = "A";
        aLine.appendChild(aMark);
        const aBody = document.createElement("span");
        aBody.appendChild(document.createTextNode(`${rcAnswer} `));
        const tag = document.createElement("span");
        tag.className = "oq-proposed-tag";
        tag.textContent = "proposed";
        aBody.appendChild(tag);
        aLine.appendChild(aBody);
        row.appendChild(aLine);

        const srcText = proposedSourceLabel(rc);
        let src: HTMLElement | null = null;
        if (srcText) {
          src = document.createElement("p");
          src.className = "oq-source muted";
          src.textContent = srcText;
          row.appendChild(src);
        }

        const actions = document.createElement("div");
        actions.className = "oq-proposed-actions";
        const accept = document.createElement("button");
        accept.type = "button";
        accept.className = "btn-action proposed-answer-accept";
        accept.textContent = "Accept answer";
        const dismiss = document.createElement("button");
        dismiss.type = "button";
        dismiss.className = "btn-action proposed-answer-dismiss";
        // "Hide suggestion", not "Dismiss": this is a localStorage-only,
        // per-device hide of the PROPOSAL — the question stays open and
        // tracked. Since 2026-08-12 the question row also carries a ✎ whose
        // "Dismiss question" writes a real correction, and two controls a few
        // pixels apart both reading "dismiss" would be genuinely ambiguous.
        dismiss.textContent = "Hide suggestion";
        const help = document.createElement("span");
        help.className = "oq-proposed-help muted";
        help.textContent =
          "Accepting records it as the answer; hiding just removes the "
          + "suggestion on this device — the question stays open.";

        accept.addEventListener("click", async () => {
          accept.disabled = true;
          dismiss.disabled = true;
          accept.textContent = "Accepting…";
          try {
            await acceptProposedAnswer(runId, rcTarget, rcQid, rcAnswer);
            // A1 (Ray 2026-09-13): acceptProposedAnswer now writes the answer onto
            // the payload (OPTIMISTIC_STATE_KINDS) + dispatches
            // feedback-state-changed, so renderViewer re-renders this question as
            // answered DURABLY — it survives the next optimistic action's rebuild.
            // The former DOM-only patch here (add .answered, set ✓, drop the
            // proposed tag/actions) was exactly the workaround that reverted on the
            // next re-render; it is no longer needed.
          } catch {
            accept.disabled = false;
            dismiss.disabled = false;
            accept.textContent = "Accept answer";
            help.textContent = "Couldn't submit — please try again.";
          }
        });
        dismiss.addEventListener("click", () => {
          // localStorage-only (mirrors the N1 merge dismiss): hide the suggestion
          // for this recipient; the candidate stays in state.
          dismissProposedAnswer(dKey);
          aLine.remove();
          src?.remove();
          actions.remove();
        });
        actions.appendChild(accept);
        actions.appendChild(dismiss);
        actions.appendChild(help);
        row.appendChild(actions);
      }

      qWrap.appendChild(row);
    }
    li.appendChild(qWrap);
  }

  // Billing aggregate: nested invoices list (Section 11.4).
  if (action.action_type === "invoices_aggregate" && (action.invoices ?? []).length) {
    const invList = document.createElement("ul");
    invList.className = "action-invoices-list";
    for (const inv of action.invoices ?? []) {
      const row = document.createElement("li");
      const num = document.createElement("b");
      num.textContent = inv.number;
      row.appendChild(num);
      const tail: string[] = [];
      if (inv.amount != null) tail.push(`$${inv.amount.toFixed(2)}`);
      if (inv.status) tail.push(`(${inv.status})`);
      if (inv.due_date) tail.push(`due ${inv.due_date}`);
      if (inv.blocking_note) tail.push(inv.blocking_note);
      if (tail.length) {
        row.appendChild(document.createTextNode(` — ${tail.join(" · ")}`));
      }
      invList.appendChild(row);
    }
    li.appendChild(invList);
  }

  return li;
}

function renderRecentlyClosedActions(
  runId: string,
  threadSlug: string,
  workItemSlug: string,
  closedActions: AnyItem[],
  fb: Map<string, SubmittedFeedbackEvent[]>,
  participantOptions: ParticipantOption[],
  threadOptions: ThreadOptionView[],
  labelText = "Recently Closed Actions",
): HTMLElement {
  // Collapsed by default (2026-06-07 density fix): after a big close-out the
  // 2-week window can hold dozens of closed Actions per Item — tuck them
  // behind a disclosure so they don't dominate the Item card.
  const section = document.createElement("details");
  section.className = "recently-closed-section";

  const label = document.createElement("summary");
  label.className = "section-label";
  label.textContent = `${labelText} (${closedActions.length})`;
  section.appendChild(label);

  const ul = document.createElement("ul");
  ul.className = "actions-list";
  for (const action of closedActions) {
    ul.appendChild(renderActionV4(
      runId, threadSlug, workItemSlug, action, fb,
      participantOptions, threadOptions,
    ));
  }
  section.appendChild(ul);
  return section;
}


function renderNeedsReviewV4(
  _runId: string,
  reviewItems: NonNullable<BriefingPayload["needs_review"]>,
  _fb: Map<string, SubmittedFeedbackEvent[]>,
): HTMLElement {
  const section = document.createElement("section");
  section.className = "thread thread-v4 needs-review";
  const h2 = document.createElement("h2");
  h2.textContent = "Needs Review";
  section.appendChild(h2);
  const ul = document.createElement("ul");
  for (const r of reviewItems) {
    const li = document.createElement("li");
    const strong = document.createElement("strong");
    strong.textContent = r.summary;
    li.appendChild(strong);
    if (r.why_flagged) {
      li.appendChild(document.createTextNode(` — ${r.why_flagged}`));
    }
    const m = document.createElement("span");
    m.className = "muted";
    m.textContent = ` (item ${r.item_id})`;
    li.appendChild(m);
    ul.appendChild(li);
  }
  section.appendChild(ul);
  return section;
}
